import { EMPTY_WORD_DOC_CONTEXT, type WordDocumentContext } from "../types";
import { logOfficeError } from "../../../../src/telemetry";
import { loadFootnoteBodies } from "./footnotes";

const UNTITLED_DOCUMENT = "Untitled Document";

export function countWords(text: string): number {
  if (!text) return 0;
  // Mirrors Word's own counter, whose rules were probed empirically through
  // COM automation (AIVA-7004 — the greeting must match the status bar):
  //  - en and em dashes (U+2013 / U+2014) are word SEPARATORS exactly like
  //    whitespace: a spaced "—" is not a word, and "8–9" is two;
  //  - every other visible token counts, including standalone "+", "/", "·"
  //    and "≈" ("a + b" is three words to Word);
  //  - a token with no visible character does not count: body.text's control
  //    anchors (footnote marks U+0002, inline images U+0001) and Symbol-font
  //    bullet glyphs (private use, e.g. U+F0B7) are all \p{C}.
  return text.split(/[\s–—]+/u).filter((t) => /[^\p{C}]/u.test(t)).length;
}

function getDocumentNameFromUrl(): string {
  try {
    const url = Office.context.document?.url;
    if (!url) return UNTITLED_DOCUMENT;
    const parts = url.replace(/\\/g, "/").split("/");
    const last = parts[parts.length - 1];
    return last ? decodeURIComponent(last) : UNTITLED_DOCUMENT;
  } catch {
    return UNTITLED_DOCUMENT;
  }
}

async function readDocumentViaSelectedData(): Promise<string> {
  return new Promise<string>((resolve) => {
    try {
      Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve((result.value as string) ?? "");
        } else {
          resolve("");
        }
      });
    } catch {
      resolve("");
    }
  });
}

function tablesToMarkdown(tables: Word.TableCollection): string {
  if (tables.items.length === 0) return "";
  const blocks: string[] = [];
  tables.items.forEach((table, i) => {
    const values: string[][] = table.values;
    if (!values || values.length === 0) return;
    const headerRow = values[0];
    if (!headerRow) return;
    const header = headerRow.map((c) => c.trim().replace(/\r?\n/g, " ") || " ");
    const lines: string[] = [
      `| ${header.join(" | ")} |`,
      `| ${header.map(() => "---").join(" | ")} |`,
    ];
    for (let r = 1; r < values.length; r++) {
      const row = values[r];
      if (!row) continue;
      const cells = row.map((c) => c.trim().replace(/\r?\n/g, " ") || " ");
      lines.push(`| ${cells.join(" | ")} |`);
    }
    blocks.push(`[Table ${i + 1}]\n${lines.join("\n")}`);
  });
  return blocks.join("\n\n");
}

// Word's counter also counts each list paragraph's rendered glyph when
// that glyph is real text: a "1." and a Unicode ●/○/■ bullet each add
// one word, while Word's default Symbol-font bullets do not (private-use
// characters are \p{C}, so countWords drops them). body.text carries no
// list glyphs at all, so without this pass the greeting undercounts every
// bulleted or numbered document. Best-effort in its own sync — ListItem
// needs WordApi 1.3 and some mocks/hosts don't expose it.
async function countListGlyphWords(context: Word.RequestContext): Promise<number> {
  let listGlyphWords = 0;
  try {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/listItemOrNullObject/listString");
    await context.sync();
    for (const p of paragraphs.items) {
      const item = p.listItemOrNullObject;
      if (item && !item.isNullObject) listGlyphWords += countWords(item.listString ?? "");
    }
  } catch {
    /* list metadata unavailable on this host — body tokens still count */
  }
  return listGlyphWords;
}

export async function getWordDocumentContext(): Promise<WordDocumentContext> {
  const hasSelection = await getWordHasSelection();
  try {
    let text = "";
    let bodyText = "";
    let title = "";
    let tableMarkdown = "";
    let listGlyphWords = 0;
    await Word.run(async (context) => {
      const body = context.document.body;
      body.load("text");
      const selection = hasSelection ? context.document.getSelection() : null;
      if (selection) selection.load("text");
      await context.sync();

      // Kept whatever the selection does with `text` below: `wordCount` must
      // describe the DOCUMENT, so a refresh taken while something is selected
      // cannot leave the ST-02 greeting quoting the selection's length.
      bodyText = body.text ?? "";

      if (hasSelection && selection) {
        // Send only the selected text so the backend injects it as
        // "--- Selected Text ---" and the LLM works on the selection only.
        text = (selection.text ?? "").trim();
      } else {
        text = bodyText;
      }

      // Title is best-effort and read in its own sync: reading
      // document.properties throws on some Word hosts, and if it shared the
      // body sync above that failure would abort the whole read — leaving an
      // unselected document with no text to send to chat (the outer catch's
      // selection-only fallback is empty when nothing is selected).
      try {
        const props = context.document.properties;
        props.load("title");
        await context.sync();
        title = props.title ?? "";
      } catch {
        /* document properties not available on this host */
      }

      if (!(hasSelection && selection)) {
        // Tables enrichment is best-effort — keeping it in its own try means a
        // host (or a test mock) that doesn't expose body.tables still returns
        // the title + plain text we already loaded, instead of falling all the
        // way down to the URL-based filename in the outer catch.
        try {
          const tables = body.tables;
          tables.load("items");
          await context.sync();
          for (const table of tables.items) {
            table.load("values");
          }
          await context.sync();
          tableMarkdown = tablesToMarkdown(tables);
        } catch {
          /* no tables on this host — fall through with plain text only */
        }
      }

      listGlyphWords = await countListGlyphWords(context);
    });

    const enrichedText = tableMarkdown
      ? `${text}\n\n---\n[STRUCTURED TABLES]\n${tableMarkdown}`
      : text;

    const name = title || getDocumentNameFromUrl();
    return {
      name,
      text: enrichedText,
      charCount: enrichedText.length,
      // Counted off the body, not `enrichedText`: body.text already contains the
      // table cell text, so counting the re-serialised markdown as well double
      // counts every table and inflates the figure the ST-02 greeting quotes.
      // List glyphs ride on top (see the listGlyphWords pass above) because
      // body.text omits them while Word's counter includes the textual ones.
      // The fallback covers a host whose body.text omits table cells — there a
      // table-only document would otherwise count zero and read as empty, which
      // also gates the document context out of the chat payload entirely.
      wordCount: countWords(bodyText) + listGlyphWords || countWords(enrichedText),
      hasSelection,
    };
  } catch {
    // Primary read failed. With a selection, honour it (send only the selected
    // text). With no selection, prefer the full document body — reading just
    // the (empty) selection here is why an unselected document previously sent
    // no context to chat at all. getWordDocumentText does its own body read via
    // a fresh, minimal Word.run and only falls back to the selection if that
    // also fails.
    const text = hasSelection
      ? await readDocumentViaSelectedData()
      : await getWordDocumentText();
    return {
      name: getDocumentNameFromUrl(),
      text,
      charCount: text.length,
      wordCount: countWords(text),
      hasSelection,
    };
  }
}

export async function getWordHasSelection(): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    try {
      Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const text = (result.value as string) ?? "";
          resolve(text.trim().length > 0);
        } else {
          resolve(false);
        }
      });
    } catch {
      resolve(false);
    }
  });
}

/**
 * Runs `fn` with Track Changes suspended and restores the document's own mode
 * afterwards — in a finally, so an aborted swap can never leave the user's
 * tracking silently off. Replacements made under it land as clean in-place
 * text: undoable via Ctrl+Z, no strikethrough/accept step even in documents
 * that have tracking enabled.
 */
async function withTrackingSuspended<T>(context: Word.RequestContext, fn: () => Promise<T>): Promise<T> {
  context.document.load("changeTrackingMode");
  await context.sync();
  const previousMode = context.document.changeTrackingMode;
  context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
  try {
    return await fn();
  } finally {
    await restoreTrackingMode(context, previousMode);
  }
}

/**
 * Put the document's tracking mode back after a suspend or a force.
 *
 * This used to be a swallowed best-effort inside the caller's own context, and
 * that is the one way Track Changes ends up ON for a user who never turned it
 * on: when `fn` fails part-way the context is already faulted, so the restore's
 * own sync throws too and the document keeps whatever mode the wrapper set.
 * Nothing surfaces — the user just finds every later edit marked as a revision.
 *
 * A fresh `Word.run` does not inherit the faulted context, so the retry is the
 * attempt that actually lands. Both attempts stay non-throwing: a restore
 * failure must not replace whatever error sent us into the finally.
 */
async function restoreTrackingMode(
  context: Word.RequestContext,
  // Not `Word.ChangeTrackingMode`: the property is typed as the enum UNION its
  // string literals, so reading it back and passing it on needs the same union.
  mode: Word.Document["changeTrackingMode"],
): Promise<void> {
  try {
    context.document.changeTrackingMode = mode;
    await context.sync();
    return;
  } catch (err) {
    logOfficeError(err, "word-tracking-restore");
  }
  try {
    await Word.run(async (fresh) => {
      fresh.document.changeTrackingMode = mode;
      await fresh.sync();
    });
  } catch (err) {
    logOfficeError(err, "word-tracking-restore-retry");
  }
}

/** The mirror of withTrackingSuspended: RV-01's whole point is that the edits
 *  land AS revisions, so tracking is forced on for the duration and the user's
 *  own mode restored afterwards. Exported so other services (docReview) can
 *  apply the same forced-tracking guarantee without duplicating it. */
export async function withTrackingForced<T>(
  context: Word.RequestContext,
  fn: () => Promise<T>,
): Promise<{ result: T; tracked: boolean }> {
  context.document.load("changeTrackingMode");
  await context.sync();
  const previousMode = context.document.changeTrackingMode;
  let tracked = false;
  try {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    await context.sync();
    tracked = true;
  } catch {
    // A host that cannot set the mode still gets the comments — but the
    // replacements will NOT be revisions, and the caller has to say so rather
    // than let the user believe there is an audit trail.
  }
  try {
    return { result: await fn(), tracked };
  } finally {
    await restoreTrackingMode(context, previousMode);
  }
}

export interface ReviewEditFinding {
  matchText: string;
  problem: string;
  suggestion?: string;
  /** Anchor by exact case. A terminology finding ("receiving party" →
   *  "Receiving Party") differs from the CORRECT form only by case, so a
   *  case-blind search returns the right occurrences too and the finding is
   *  rejected as ambiguous — the thing it exists to fix is what hides it. */
  matchCase?: boolean;
  /** Apply the suggestion to EVERY occurrence of matchText (inside the
   *  selection when the phrase is in it, document-wide otherwise), with one
   *  comment on the first. A /terms finding names a FORM, not a place: the
   *  second "receiving party" is as wrong as the first, and treating
   *  repetition as ambiguity skipped exactly the terms that are inconsistent
   *  more than once. */
  allOccurrences?: boolean;
}


type ReviewTarget =
  | { ok: true; range: Word.Range }
  | { ok: false; reason: "not_found" }
  | { ok: false; reason: "too_long" }
  | { ok: false; reason: "ambiguous"; matches: number };

function settleReviewTarget(items: Word.Range[]): ReviewTarget {
  const first = items[0];
  if (!first) return { ok: false, reason: "not_found" };
  if (items.length > 1) return { ok: false, reason: "ambiguous", matches: items.length };
  return { ok: true, range: first };
}


type ReviewScope = { selection: Word.Range; text: string };

async function captureReviewScope(context: Word.RequestContext): Promise<ReviewScope> {
  const selection = context.document.getSelection();
  selection.load("text");
  await context.sync();
  return { selection, text: (selection.text ?? "").replace(/\s+/g, " ").trim() };
}


function reviewAnchorCandidates(text: string): string[] {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const variant of [trimmed, trimmed.replace(/'/g, "’"), trimmed.replace(/’/g, "'")]) {
    const key = variant.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(variant);
    }
  }
  return out;
}

/** Every hit for ONE candidate string, selection first then body-wide. */
async function locateReviewHits(
  context: Word.RequestContext,
  scope: ReviewScope,
  candidate: string,
  options: { matchCase: boolean; matchWholeWord: boolean },
): Promise<Word.Range[]> {
  const find = escapeWordSearch(candidate);
  const inSelection = scope.text.length > 0 && scope.text.toLowerCase().includes(candidate.toLowerCase());

  if (inSelection) {
    const selectionResults = scope.selection.search(find, options);
    selectionResults.load("items");
    await context.sync();
    if (selectionResults.items.length > 0) return selectionResults.items;
  }

  const bodyResults = context.document.body.search(find, options);
  bodyResults.load("items");
  await context.sync();
  if (!inSelection || bodyResults.items.length === 0) return bodyResults.items;

  // Range.search can miss text that spans formatting runs even though the
  // plain text is verifiably inside the selection (see highlightWordText's
  // identical rescue below) — filter the body-wide hits down to the ones
  // actually inside the selection instead of taking the document's first
  // occurrence.
  const relations = bodyResults.items.map((item) => item.compareLocationWith(scope.selection));
  await context.sync();
  const inside = bodyResults.items.filter((_, i) => {
    const relation = relations[i]?.value;
    return relation === Word.LocationRelation.inside || relation === Word.LocationRelation.equal;
  });
  return inside.length > 0 ? inside : bodyResults.items;
}

/** Locate ONE candidate string — exactly one hit, or the reason there isn't. */
async function locateReviewCandidate(
  context: Word.RequestContext,
  scope: ReviewScope,
  candidate: string,
  options: { matchCase: boolean; matchWholeWord: boolean },
): Promise<ReviewTarget> {
  return settleReviewTarget(await locateReviewHits(context, scope, candidate, options));
}

type ReviewTargets =
  | { ok: true; ranges: Word.Range[] }
  | { ok: false; reason: "not_found" }
  | { ok: false; reason: "too_long" };

/**
 * Every occurrence of a finding's matchText — the anchor set for an
 * `allOccurrences` finding. Same apostrophe variants, length guard and
 * selection-first scoping as findReviewTarget; the one difference is that
 * repetition is the expected case here rather than an ambiguity.
 */
async function findReviewTargets(
  context: Word.RequestContext,
  scope: ReviewScope,
  text: string,
  options: { matchCase: boolean; matchWholeWord: boolean },
): Promise<ReviewTargets> {
  const candidates = reviewAnchorCandidates(text);
  if (candidates.length === 0) return { ok: false, reason: "not_found" };
  const searchable = candidates.filter((c) => escapeWordSearch(c).length <= WORD_SEARCH_MAX_CHARS);
  if (searchable.length === 0) return { ok: false, reason: "too_long" };
  for (const candidate of searchable) {
    const ranges = await locateReviewHits(context, scope, candidate, options);
    if (ranges.length > 0) return { ok: true, ranges };
  }
  return { ok: false, reason: "not_found" };
}


async function findReviewTarget(
  context: Word.RequestContext,
  scope: ReviewScope,
  text: string,
  options: { matchCase: boolean; matchWholeWord: boolean },
): Promise<ReviewTarget> {
  const candidates = reviewAnchorCandidates(text);
  if (candidates.length === 0) return { ok: false, reason: "not_found" };

  // Word's search takes at most WORD_SEARCH_MAX_CHARS, so an over-long anchor
  // is filtered rather than being allowed to decide the outcome for the whole
  // finding. It used to be tested on candidates[0] alone, which returned
  // `too_long` immediately and never tried the remaining variants — and since
  // the variants differ only in apostrophe shape they are all the same length,
  // so a long sentence could never anchor at all (AIVA-7724). The reported
  // symptom was "None of the 1 finding could be located in the current text"
  // on a clause the user could see perfectly well: /passive is told to send the
  // WHOLE sentence as matchText, and a legal sentence runs past 255 characters
  // as a matter of course.
  const searchable = candidates.filter((c) => escapeWordSearch(c).length <= WORD_SEARCH_MAX_CHARS);
  if (searchable.length === 0) return { ok: false, reason: "too_long" };

  for (const candidate of searchable) {
    const target = await locateReviewCandidate(context, scope, candidate, options);
    if (target.ok || target.reason !== "not_found") return target;
  }
  return { ok: false, reason: "not_found" };
}

export interface ReviewEditResult {
  /** Findings anchored and written. */
  applied: number;
  /** Findings whose matchText no longer occurs in the document (G-7). */
  skipped: number;
  /** Findings that carried a usable matchText — the denominator the user sees. */
  attempted: number;
  /** Whether Track Changes was actually in force for the writes. */
  tracked: boolean;
  /** Comment-only findings whose text was highlighted so it can be found. */
  highlighted: number;
  /** Findings whose matchText is longer than Word's search accepts, so they
   *  could never be anchored however unchanged the document is. Counted apart
   *  from `skipped` because the cause and the fix are different: the wording did
   *  NOT shift, the anchor is simply too long to search for (AIVA-7724). */
  tooLong: number;
  /** Findings dropped because another finding's matchText contained theirs, so
   *  the larger rewrite already covers them. Not a failure and not a loss —
   *  counted so the message can say so, rather than reporting text as "could not
   *  be located" when it was deliberately never searched for. */
  overlapping: number;
}

type ReviewFindingOutcome = "applied" | "skipped" | "too_long";

/**
 * Anchor and write ONE finding inside the tracked batch: a comment on the
 * first occurrence, then the suggested wording as a replacement. Extracted
 * from executeReviewEdits's loop so that function stays under Sonar's
 * cognitive-complexity ceiling; the loop only tallies what this returns.
 */
async function applyReviewFinding(
  context: Word.RequestContext,
  scope: ReviewScope,
  finding: ReviewEditFinding,
): Promise<ReviewFindingOutcome> {
  const options = { matchCase: finding.matchCase === true, matchWholeWord: false };
  // A form-level finding anchors to every occurrence; a place-level one to
  // exactly one — see ReviewEditFinding.allOccurrences.
  const target = finding.allOccurrences
    ? await findReviewTargets(context, scope, finding.matchText, options)
    : await findReviewTarget(context, scope, finding.matchText, options);
  // Not anchorable, or anchorable to several places at once — either way the
  // caller counts it as skipped rather than guessing.
  if (!target.ok) return target.reason === "too_long" ? "too_long" : "skipped";
  const ranges = "ranges" in target ? target.ranges : [target.range];
  const first = ranges[0];
  if (!first) return "skipped";

  // Comment BEFORE the replacement: it anchors to the range as it stands, and
  // replacing the text first would leave the comment pointing at the revision
  // rather than the phrase the finding names. One comment per FINDING, on its
  // first occurrence — a margin note on every repeat of a term would bury the
  // review in itself.
  try {
    // AC3 — every comment AiVA leaves carries the configured author, whatever
    // name Word stamps on it.
    first.insertComment(attributeComment(finding.problem));
  } catch { /* host without the comments API — the revision still lands */ }
  if (finding.suggestion) {
    for (const range of ranges) range.insertText(finding.suggestion, Word.InsertLocation.replace);
  }
  await context.sync();
  return "applied";
}

/**
 * Apply a review as comments plus tracked changes (RV-01, AC-2).
 *
 * Deliberately NOT applyWordReview. That one suspends tracking and overwrites
 * the flagged text outright — a silent rewrite with no audit trail, which is
 * the thing this path exists to avoid. Here every finding leaves a comment
 * saying what is wrong, and a suggested wording lands as a REVISION the
 * reviewer can accept or reject.
 *
 * Findings are anchored by exact `matchText`. One that no longer occurs — the
 * model paraphrased the slice, or the document moved since the review ran —
 * cannot be placed, and is counted rather than dropped: the count is what the
 * caller reports so a reviewer is never left believing a document is fully
 * marked up when findings were lost (G-7).
 */
export async function executeReviewEdits(findings: ReviewEditFinding[]): Promise<ReviewEditResult> {
  const usable = findings.filter((f) => f?.matchText);
  let applied = 0;
  let tracked = false;
  let tooLong = 0;

  // Drop a finding whose matchText sits INSIDE another finding's matchText.
  //
  // Findings are applied one after another and each one rewrites the document,
  // so a span that contains a later span destroys that later anchor before it is
  // ever searched for. Reported on /passive (AIVA-7724): four constructions in
  // one clause came back "Applied 3 of 4 findings; 1 could not be located in the
  // current text", and the finding that failed was the one whose phrase lay
  // inside the first finding's sentence — the text really was gone, replaced by
  // the rewrite the user had just approved.
  //
  // The containing span is kept because it carries the fuller rewrite, and that
  // replacement already covers the passive the dropped finding named. Sorted
  // longest-first only to decide what contains what; the survivors are put back
  // into the model's own order, because that is document order and the comments
  // should read down the page.
  //
  // Case-blind by default (a sentence-initial capital in the model's quote is
  // not a different span), but EXACT when either finding is case-sensitive:
  // "receiving party" and "receiving Party" are two wrong forms of one term,
  // not a span inside a span. Folding them collapsed a three-finding /terms
  // review to two and reported the third as "already covered by a larger
  // rewrite" when nothing had covered it.
  const byLengthDesc = [...usable].sort((a, b) => b.matchText.length - a.matchText.length);
  const keep = new Set<ReviewEditFinding>();
  for (const finding of byLengthDesc) {
    const covered = [...keep].some((k) =>
      finding.matchCase || k.matchCase
        ? k.matchText.includes(finding.matchText)
        : k.matchText.toLowerCase().includes(finding.matchText.toLowerCase()),
    );
    if (!covered) keep.add(finding);
  }
  const overlapping = usable.length - keep.size;
  const anchorable = usable.filter((f) => keep.has(f));

  // Highlight the findings that leave NO visible mark in the body, so they can
  // be found.
  //
  // A finding with a suggestion becomes a tracked change, which Word already
  // shows in place. A comment-only finding does not: it lands as a margin note
  // beside text that looks untouched, so on a long document the reviewer has a
  // comment and no way to see what it refers to.
  //
  // Only the comment-only ones, deliberately. Highlighting text that is then
  // replaced transfers the colour onto the replacement, and clearWordHighlights
  // cannot lift it afterwards because it re-searches the original phrase — the
  // trap applyWordReview documents below. Skipping them costs nothing, since the
  // revision marks those locations anyway.
  //
  // Runs BEFORE tracking is forced on, so the colour is a cosmetic session
  // highlight that Clear highlights can remove, rather than a formatting
  // revision the reviewer has to accept or reject.
  let highlighted = 0;
  for (const finding of anchorable) {
    if (finding.suggestion) continue;
    // highlightWordText rather than a plain search: it already handles the LLM
    // quoting a longer span than the document holds, and the straight-vs-curly
    // apostrophe mismatch Word's autocorrect creates.
    if ((await highlightWordText(finding.matchText)) > 0) highlighted++; // NOSONAR - highlights are applied one Word.run at a time, in order
  }

  // Strip any highlight already sitting on text this run is about to REPLACE.
  //
  // The skills that drive this path (/passive, /clarity, /citations) highlight
  // every finding from their own word_highlight_text step, before the pill is
  // ever clicked. A replacement inherits the character formatting of the phrase
  // it overwrites, so without this the colour survives on the new wording — and
  // Clear highlights can never lift it, because that works by re-searching the
  // ORIGINAL phrase, which no longer exists once applied. applyWordReview solves
  // the same problem inline; here it has to happen in this pre-pass instead,
  // because inside withTrackingForced the strip would itself land as a
  // formatting revision for the reviewer to accept or reject.
  try {
    const replacing = anchorable.filter((f) => f.suggestion);
    if (replacing.length > 0) {
      await Word.run(async (context) => {
        const found = replacing.map((f) =>
          context.document.body.search(f.matchText, { matchCase: f.matchCase === true, matchWholeWord: false }),
        );
        for (const results of found) results.load("items");
        await context.sync();
        for (const results of found) {
          for (const item of results.items) {
            (item.font as { highlightColor: string | null }).highlightColor = null;
          }
        }
        await context.sync();
      });
    }
  } catch (err) {
    // Cosmetic only — a stranded highlight is not worth failing the review over.
    logOfficeError(err, "word-review-edits-clear-highlight");
  }

  try {
    const outcome = await Word.run((context) =>
      withTrackingForced(context, async () => {
        const scope = await captureReviewScope(context);
        for (const finding of anchorable) {
          try {
            // Sequential by necessity, not oversight: each finding rewrites the
            // document the next one is anchored in, and the sync inside is what
            // says whether THIS finding landed before the count moves on.
            const outcome = await applyReviewFinding(context, scope, finding); // NOSONAR: typescript:S9382
            if (outcome === "applied") applied++;
            else if (outcome === "too_long") tooLong++;
          } catch { /* continue with the next finding */ }
        }
      }),
    );
    tracked = outcome.tracked;
  } catch (err) {
    logOfficeError(err, "word-execute-review-edits");
  }
  return {
    applied,
    // Against `anchorable`, not `usable`: a finding dropped for overlapping was
    // never searched for, so counting it as skipped would report a locate
    // failure that never happened. It is reported as `overlapping` instead.
    skipped: Math.max(0, anchorable.length - applied),
    attempted: usable.length,
    tracked,
    highlighted,
    tooLong,
    overlapping,
  };
}

export interface ReviewSkip {
  original: string;
  reason: "not_found" | "ambiguous" | "too_long" | "failed";
  matches?: number;
}

export interface ReviewApplyResult {
  applied: number;
  skips: ReviewSkip[];
}

export async function applyWordReview(
  changes: Array<{ original: string; replacement: string }>,
): Promise<ReviewApplyResult> {
  let applied = 0;
  const skips: ReviewSkip[] = [];
  try {
    await Word.run((context) => withTrackingSuspended(context, async () => {
      const scope = await captureReviewScope(context);
      for (const change of changes) {
        if (!change.original) continue;
        try {
          const target = await findReviewTarget(context, scope, change.original, { // NOSONAR - shared Word context, edits must stay ordered
            matchCase: false,
            matchWholeWord: false,
          });
          if (!target.ok) {
            skips.push({
              original: change.original,
              reason: target.reason,
              ...(target.reason === "ambiguous" ? { matches: target.matches } : {}),
            });
          } else {
            const first = target.range;
            const inserted = first.insertText(change.replacement ?? "", Word.InsertLocation.replace);
            // The replacement inherits the review highlight (character formatting)
            // from the flagged phrase it replaces — and clearWordHighlights can't
            // remove it afterwards because it re-searches the ORIGINAL phrase,
            // which no longer exists once applied. Strip the highlight here so
            // applied text always lands clean.
            ((inserted ?? first).font as { highlightColor: string | null }).highlightColor = null;
            await context.sync(); // NOSONAR - each replacement must land before the next search
            applied++;
          }
        } catch {
          skips.push({ original: change.original, reason: "failed" });
        }
      }
    }));
  } catch { /* return partial counts */ }
  return { applied, skips };
}

export interface WordSelectionRead {
  /** False when the host refused the read. Distinct from a successful read of
   *  an empty selection: the caller must not treat a failure as "the user
   *  deselected", or a stale selection silently survives into the next turn. */
  ok: boolean;
  /** Paragraph breaks normalised to \n. Word delimits them with \r, which
   *  downstream prompt building and word counting read as one run-on
   *  paragraph. */
  text: string;
  /** `text` with each heading paragraph prefixed by its markdown marker, or ""
   *  when the selection has no headings or is not paragraph-aligned.
   *
   *  AIVA-7080: this reader loaded only `text`, so a selected section reached
   *  the model as a flat run of lines with its headings indistinguishable from
   *  its body. The edit skills are told never to invent a heading that was not
   *  in the original — correct on its own, but with style-blind input it left
   *  them no way to KEEP one either, so every slash command flattened the
   *  section. Callers use this for the PROMPT text and keep `text` for counts
   *  and for any tool that must return the selection verbatim.
   *
   *  Empty means "nothing to add", not a failure: a heading-free selection is
   *  byte-for-byte what it always was. */
  markdown: string;
}

/** The markdown marker that round-trips a Word heading style back to itself.
 *
 *  `# ` here becomes "Heading 1" again when parseMdBlocks re-inserts the model's
 *  reply, so structure survives the whole round trip in ONE vocabulary — the
 *  same one the edit skills are already told to emit ("Use # / ## / ### for
 *  headings").
 *
 *  Heading-ness comes from `headingLevelFrom`, the one predicate every reader
 *  shares, so the selection readers and the structure reader cannot disagree
 *  about what counts as a heading. That matters most on a document pasted in
 *  from HTML: it carries no paragraph styles at all, so a style-only marker
 *  returned "" for every paragraph and the model was handed flat prose with the
 *  document's whole structure stripped out. */
function _headingMarker(style: string | undefined, outlineLevel?: number | null): string {
  const trimmed = (style ?? "").trim();
  if (/^Title$/i.test(trimmed)) return "# ";
  const level = headingLevelFrom(trimmed, outlineLevel);
  // Markdown stops at h6. A level 7-9 heading marks as '######' rather than
  // losing its heading-ness to a marker parseMdBlocks cannot round-trip.
  return level === null ? "" : `${"#".repeat(Math.min(level, 6))} `;
}

/** Mark the heading paragraphs of a selection, or "" when that cannot be done
 *  faithfully.
 *
 *  Two guards, both yielding "":
 *
 *  * **Not paragraph-aligned.** `Range.paragraphs` yields every paragraph the
 *    selection TOUCHES, and each one's `.text` is the WHOLE paragraph — so for a
 *    part-selected paragraph the marked-up version carries text the user never
 *    selected. Handing that to the model would silently widen what it edits,
 *    which is a worse failure than losing a heading, so the plain selection
 *    stands instead.
 *  * **No headings.** Nothing to add, and "" keeps the byte-identical previous
 *    behaviour for the ordinary case. */
function _markSelectionStructure(items: readonly Word.Paragraph[], plain: string): string {
  const norm = (value: string) => value.replace(/\s+/g, " ").trim();
  const paragraphs = items.map((item) => ({
    text: (item.text ?? "").trim(),
    style: item.style,
    outlineLevel: item.outlineLevel ?? null,
  }));
  if (norm(paragraphs.map((para) => para.text).join(" ")) !== norm(plain)) return "";
  if (!paragraphs.some((para) => _headingMarker(para.style, para.outlineLevel))) return "";
  return paragraphs
    .filter((para) => para.text)
    .map((para) => _headingMarker(para.style, para.outlineLevel) + para.text)
    .join("\n");
}

/**
 * The single selection read for the pane (AIVA-6925).
 *
 * `Word.run` + `getSelection().text` rather than `getSelectedDataAsync`: it
 * returns the whole multi-paragraph selection, and it is the same path
 * `getWordDocumentContext` uses — two APIs for one fact is what let them
 * disagree about how much was selected.
 */
export async function readWordSelection(): Promise<WordSelectionRead> {
  try {
    let text = "";
    let markdown = "";
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.load("text");
      const paragraphs = selection.paragraphs;
      paragraphs.load("items");
      await context.sync();
      for (const paragraph of paragraphs.items) paragraph.load("text, style, outlineLevel");
      await context.sync();
      text = selection.text ?? "";
      markdown = _markSelectionStructure(paragraphs.items, text);
    });
    return { ok: true, text: text.replace(/\r\n?/g, "\n"), markdown };
  } catch (err) {
    logOfficeError(err, "word-read-selection");
    return { ok: false, text: "", markdown: "" };
  }
}

export async function getWordSelection(): Promise<string> {
  const { text } = await readWordSelection();
  return text;
}

export async function getWordDocumentText(): Promise<string> {
  try {
    let text = "";
    await Word.run(async (context) => {
      const body = context.document.body;
      body.load("text");
      await context.sync();
      text = body.text ?? "";
    });
    return text;
  } catch {
    return readDocumentViaSelectedData();
  }
}


type MdBlock =
  | { kind: "para"; text: string; style: string }
  | { kind: "table"; headers: string[]; rows: string[][] };

// Direct font formatting captured from the original selection so inserted
// content preserves it. Assigning a paragraph style resets size/family AND
// direct character overrides (bold/italic/underline/color) to the style
// defaults, which drops formatting the user had applied on the selection —
// we re-apply the captured values afterwards. Word returns "" for mixed-name
// selections and null for mixed booleans/underline/color, so those come back
// as undefined here and we skip re-applying them.
interface CapturedFont {
  name?: string;
  size?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: Word.UnderlineType;
  color?: string;
  strikeThrough?: boolean;
  highlightColor?: string;
  // PARAGRAPH alignment, not a font property — it rides on this record because
  // it has exactly the same lifetime and index mapping as the font: captured
  // once per selected paragraph in document order, then re-applied to inserted
  // paragraph N via fontForBlock, tail rule included (AIVA-7903). Threading a
  // second parallel array through queueBlock/blocksToHtml instead would give
  // two arrays that must stay the same length and the same order — which is
  // how they drift. The font appliers only read the font keys, so this is inert
  // to them; applyCapturedAlignment is the only consumer.
  alignment?: CapturedAlignment;
}

/**
 * A paragraph alignment concrete enough to re-apply.
 *
 * The strings are `Word.Alignment`'s own values, but spelled as literals rather
 * than read off the enum: the alignment travels through generated HTML and test
 * mocks that have no `Word` global, and a `Word.Alignment.justified` reference
 * evaluated at module scope is undefined there. "Mixed" and "Unknown" — what
 * Word reports for a range whose paragraphs disagree, or a host that will not
 * answer — are deliberately NOT in the union: normaliseAlignment drops them so
 * nothing is re-applied, exactly as normaliseFont drops a mixed font.
 */
type CapturedAlignment = "Left" | "Centered" | "Right" | "Justified";

/** Everything Word itself accepts on `Paragraph.alignment` — the enum plus the
 *  bare string literals, "Mixed" and "Unknown" included. Taken from the API
 *  surface rather than restated, so a typings bump cannot leave this behind. */
type WordAlignmentValue = Word.Paragraph["alignment"];

const ALIGNMENT_BY_WORD_NAME: Readonly<Record<string, CapturedAlignment>> = {
  left: "Left",
  centered: "Centered",
  center: "Centered",
  centre: "Centered",
  right: "Right",
  justified: "Justified",
  justify: "Justified",
  // Word's own "distribute this line to the margins" variants. They are not
  // Word.Alignment members, so they can only arrive from a host reporting a
  // richer value than the enum documents; folding them to Justified keeps the
  // flush-both-margins look the user is asking for rather than dropping to the
  // Normal-style default.
  justifiedlow: "Justified",
  justifiedmedium: "Justified",
  justifiedhigh: "Justified",
};

/** A Word alignment read → the value to re-apply, or undefined when Word
 *  reported mixed/unknown/nothing and the destination default must stand. */
function normaliseAlignment(value: unknown): CapturedAlignment | undefined {
  if (typeof value !== "string") return undefined;
  return ALIGNMENT_BY_WORD_NAME[value.trim().toLowerCase()];
}

/** The CSS `text-align` for a captured alignment. Only ever emitted on a BLOCK
 *  element — see paraBlockToHtml. */
function alignmentToCss(alignment: CapturedAlignment): string {
  if (alignment === "Centered") return "center";
  if (alignment === "Justified") return "justify";
  return alignment.toLowerCase();
}

function isRealHighlight(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "" && value !== "No color";
}

// The subset of Word.Font we read. Word hands back "" for a mixed name and
// null for a mixed boolean/underline/color, hence the widened types.
interface FontLike {
  name?: string | null;
  size?: number | null;
  bold?: boolean | null;
  italic?: boolean | null;
  underline?: string | null;
  color?: string | null;
  strikeThrough?: boolean | null;
  highlightColor?: string | null;
}

// Paragraph.font.load form ("name, size, …") and the selection.load form
// ("font/name, font/size, …") of the same property list.
const FONT_LOAD_SPEC =
  "name, size, bold, italic, underline, color, strikeThrough, highlightColor";
const SELECTION_FONT_LOAD_SPEC = FONT_LOAD_SPEC.split(", ").map((f) => `font/${f}`).join(", ");

// Turn a raw Word font read into a CapturedFont, dropping any property Word
// reported as mixed ("" / null / 0) so applyCapturedFont leaves the paragraph
// style's default in place for it rather than overwriting with a junk value.
function normaliseFont(font: FontLike): CapturedFont {
  const { name, size, bold, italic, underline, color, strikeThrough, highlightColor } = font;
  return {
    name: name?.trim() ? name : undefined,
    size: typeof size === "number" && size > 0 ? size : undefined,
    bold: typeof bold === "boolean" ? bold : undefined,
    italic: typeof italic === "boolean" ? italic : undefined,
    underline: typeof underline === "string" ? (underline as Word.UnderlineType) : undefined,
    color: typeof color === "string" && color ? color : undefined,
    strikeThrough: typeof strikeThrough === "boolean" ? strikeThrough : undefined,
    highlightColor: isRealHighlight(highlightColor) ? highlightColor : undefined,
  };
}

// Fill the properties `base` could not resolve from a second, narrower read.
// A plain spread cannot do this: normaliseFont always emits every key, so
// `{...fallback, ...base}` would put base's `undefined` back over a concrete
// fallback value.
function fillMissingFont(base: CapturedFont, fallback: CapturedFont): CapturedFont {
  const merged: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(fallback)) {
    if (merged[key] === undefined && value !== undefined) merged[key] = value;
  }
  return merged as CapturedFont;
}

async function captureSelectionFont(context: Word.RequestContext): Promise<CapturedFont> {
  try {
    const sel = context.document.getSelection();
    sel.load(SELECTION_FONT_LOAD_SPEC);
    await context.sync();
    return normaliseFont(sel.font);
  } catch {
    return {};
  }
}

// Capture the font of EACH paragraph in the selection, in document order.
//
// AIVA-6926: a whole-selection font read (captureSelectionFont) collapses to
// undefined for every property that differs across the selected paragraphs —
// and Word reports a MULTI-paragraph range's font as mixed even when the runs
// look uniform, because the range spans paragraph marks. Re-applying that to a
// multi-paragraph replacement dropped the original colour and font family back
// to the Normal-style default (black, body font). Reading each paragraph on its
// own gives a concrete value per paragraph, so the replacement can re-apply the
// right formatting paragraph by paragraph. A single-paragraph selection is
// unaffected — it returns one font, exactly as before.
//
// Falls back to a single whole-selection capture (wrapped in an array) on hosts
// that reject the per-paragraph read, and to [] if even that fails.
async function captureSelectionParagraphFonts(context: Word.RequestContext): Promise<CapturedFont[]> {
  try {
    const paragraphs = context.document.getSelection().paragraphs;
    paragraphs.load("items");
    await context.sync();
    if (paragraphs.items.length === 0) return [];
    for (const p of paragraphs.items) {
      p.font.load(FONT_LOAD_SPEC);
      // Queued into the SAME sync as the font, so preserving alignment costs no
      // extra round trip. It shares the font's fate if a host rejects the
      // property — but Paragraph.alignment is WordApi 1.1, as fundamental as
      // Paragraph.font on the line above, so there is no host that serves one
      // and not the other.
      p.load("alignment");
    }
    await context.sync();
    const fonts = paragraphs.items.map((p) => ({
      ...normaliseFont(p.font),
      alignment: normaliseAlignment(p.alignment),
    }));
    const withContent = await fillFromContentRanges(context, paragraphs.items, fonts);
    return await fillFromStyleFonts(context, paragraphs.items, withContent);
  } catch {
    const single = await captureSelectionFont(context);
    return Object.keys(single).length > 0 ? [single] : [];
  }
}

// AIVA-7074 second pass: re-read the paragraphs whose font came back MIXED.
//
// `Paragraph.font` reads the paragraph's whole range, which includes the
// end-of-paragraph mark. That mark carries its own formatting and very often
// disagrees with the text — so Word reports the name as "" and normaliseFont
// drops it, even for a paragraph whose visible text is uniformly one font. The
// caller then emits no font-family, and the paste lands in the destination's
// theme body font: the reported "pasted as Aptos (Body) instead of the font at
// the cursor".
//
// RangeLocation.content is the range BETWEEN the paragraph's start and end, so
// it excludes that mark and reports the concrete font of the text itself.
//
// Only the properties still unresolved are filled in — a paragraph Word already
// answered for is left exactly as it was, so this cannot override a real
// per-paragraph read (AIVA-6926). Paragraphs that read cleanly cost nothing:
// with nothing to retry the function returns before issuing a sync.
async function fillFromContentRanges(
  context: Word.RequestContext,
  items: Word.Paragraph[],
  fonts: CapturedFont[],
): Promise<CapturedFont[]> {
  const mixed = fonts.flatMap((f, i) => (f.name === undefined ? [i] : []));
  if (mixed.length === 0) return fonts;
  try {
    const ranges = mixed.map((i) => items[i]!.getRange(Word.RangeLocation.content));
    for (const r of ranges) r.font.load(FONT_LOAD_SPEC);
    await context.sync();
    const filled = [...fonts];
    mixed.forEach((paraIndex, n) => {
      filled[paraIndex] = fillMissingFont(fonts[paraIndex]!, normaliseFont(ranges[n]!.font));
    });
    return filled;
  } catch {
    // Host without RangeLocation.content (or a range it refuses to read) —
    // keep the paragraph-level result rather than losing it.
    return fonts;
  }
}

// AIVA-7074 third pass: resolve the paragraph STYLE's font for anything the
// direct reads could not answer.
//
// Both prior passes read DIRECT formatting. Text whose face comes entirely
// from its paragraph style — Normal defined as Times New Roman is the reported
// case — has none, so both passes return "" and the clipboard HTML ships with
// no font-family: the paste then lands in the destination default, visibly
// wrong. The style object itself knows its font, so the last resort is to read
// it there. Only unresolved properties are filled; a concrete direct read is
// never overridden (same contract as fillFromContentRanges).
//
// getStyles needs WordApi 1.5 — wrapped like every other newer-API read, so an
// older host simply keeps the two-pass result.
async function fillFromStyleFonts(
  context: Word.RequestContext,
  items: Word.Paragraph[],
  fonts: CapturedFont[],
): Promise<CapturedFont[]> {
  const unresolved = fonts.flatMap((f, i) => (f.name === undefined ? [i] : []));
  if (unresolved.length === 0) return fonts;
  try {
    for (const i of unresolved) items[i]!.load("style");
    await context.sync();
    const styleNames = [...new Set(unresolved.map((i) => items[i]!.style).filter(Boolean))];
    if (styleNames.length === 0) return fonts;
    const collection = context.document.getStyles();
    const byName = new Map<string, Word.Style>();
    for (const name of styleNames) {
      const s = collection.getByNameOrNullObject(name);
      s.load("isNullObject");
      s.font.load(FONT_LOAD_SPEC);
      byName.set(name, s);
    }
    await context.sync();
    const filled = [...fonts];
    for (const i of unresolved) {
      const s = byName.get(items[i]!.style);
      if (s && !s.isNullObject) filled[i] = fillMissingFont(fonts[i]!, normaliseFont(s.font));
    }
    return filled;
  } catch {
    // Host without getStyles, or a style it refuses to read — keep the
    // direct-formatting result rather than losing it.
    return fonts;
  }
}

// The captured font to re-apply to inserted block `index`. Maps response
// paragraph N to selection paragraph N; once the response has MORE paragraphs
// than the selection, the last captured font carries on so the tail still
// inherits the selection's formatting rather than the style default (AIVA-6926).
function fontForBlock(fonts: CapturedFont[], index: number): CapturedFont {
  if (fonts.length === 0) return {};
  return fonts[Math.min(index, fonts.length - 1)] ?? {};
}

/** Takes anything with a `font` — a Paragraph, or the Range insertHtml returns. */
function applyCapturedFont(p: { font: Word.Font }, font: CapturedFont): void {
  try {
    if (font.size !== undefined) p.font.size = font.size;
    if (font.name !== undefined) p.font.name = font.name;
    if (font.bold !== undefined) p.font.bold = font.bold;
    if (font.italic !== undefined) p.font.italic = font.italic;
    if (font.underline !== undefined) p.font.underline = font.underline;
    if (font.color !== undefined) p.font.color = font.color;
    if (font.strikeThrough !== undefined) p.font.strikeThrough = font.strikeThrough;
    if (font.highlightColor !== undefined) p.font.highlightColor = font.highlightColor;
  } catch { /* font not settable on this host */ }
}

/**
 * Re-apply the captured PARAGRAPH alignment (AIVA-7903).
 *
 * Separate from applyCapturedFont because it needs a Paragraph, not just
 * something with a `font` — alignment is not a character property, so the Range
 * that insertHtml returns has no `alignment` to set (see
 * stampAlignmentOnInsertedHtml for how the HTML route gets there instead).
 *
 * Assigning a paragraph style resets alignment to that style's default the same
 * way it resets size and family, and a NEW paragraph starts at the Normal
 * style's default — left. That is the whole defect: replacing a justified
 * paragraph handed back ragged-right text, because nothing ever asked for the
 * alignment the selection had. Undefined (Word reported mixed/unknown) leaves
 * the destination default alone, exactly as a mixed font does.
 */
function applyCapturedAlignment(p: { alignment?: WordAlignmentValue }, font: CapturedFont): void {
  if (font.alignment === undefined) return;
  try {
    p.alignment = font.alignment;
  } catch { /* alignment not settable on this host */ }
}

function stripInlineMd(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/gs, "$1")
    .replace(/\*(.*?)\*/gs, "$1")
    .replace(/`([^`]*)`/g, "$1")
    .trim();
}

const TABLE_SEP_RE = /^\|[\s\-:|]+\|/;

function parseTableBlock(
  lines: string[],
  startIdx: number,
): { block: MdBlock | null; nextIdx: number } | null {
  const t = (lines[startIdx] ?? "").trim();
  const nextT = (lines[startIdx + 1] ?? "").trim();
  if (!t.startsWith("|") || !TABLE_SEP_RE.test(nextT)) return null;
  const parseRow = (l: string): string[] =>
    l.split("|").slice(1, -1).map((c) => stripInlineMd(c));
  const headers = parseRow(t);
  let i = startIdx + 2;
  const rows: string[][] = [];
  while (i < lines.length) {
    const rowT = (lines[i] ?? "").trim();
    if (!rowT.startsWith("|")) break;
    rows.push(parseRow(rowT));
    i++;
  }
  return { block: headers.length > 0 ? { kind: "table", headers, rows } : null, nextIdx: i };
}

/** Longest a clause-numbered line may be and still read as a section NAME.
 *
 *  Exported so the tests pin the boundary against this value rather than against
 *  a hand-counted sentence — a fixture that happens to straddle a literal 12 goes
 *  quietly wrong the moment the limit moves. */
export const NUMBERED_HEADING_MAX_WORDS = 12;

/** Punctuation that ends a SENTENCE, and so rules a line out as a heading. */
const SENTENCE_TERMINATOR_RE = /[.:;,]$/;

/** Does a clause-numbered line look like a HEADING rather than numbered prose?
 *
 *  A leading "1.1 " says nothing about structure on its own. A numbered legal
 *  document is mostly numbered BODY paragraphs — the number is how a clause is
 *  cited, not a sign that it titles anything. Promoting on the number alone
 *  styled every clause as Heading 2/3, which put whole sentences into the
 *  navigation pane and any generated TOC, and buried the real headings among
 *  them.
 *
 *  `strippedNumberWords` re-adds the leading number when the caller has ALREADY
 *  removed it (the "1. " branch strips it into the List Number glyph), so both
 *  callers measure the same original line against the same limit.
 *
 *  The two signals that separate them are the ones a reader uses:
 *
 *  * **Terminal punctuation.** A heading does not end in a full stop; a sentence
 *    does. This settles most lines on its own.
 *  * **Length.** A heading names a section in a few words, so
 *    NUMBERED_HEADING_MAX_WORDS still catches an unpunctuated run-on without
 *    rejecting a genuinely long section name.
 *
 *  Both must hold. Explicit "## " markers are matched BEFORE this in
 *  classifyParaLine, so a heading the model marked itself never reaches here —
 *  this governs only the unmarked lines the parser has to guess about. */
function isNumberedHeading(line: string, strippedNumberWords = 0): boolean {
  if (SENTENCE_TERMINATOR_RE.test(line)) return false;
  return countWords(line) + strippedNumberWords <= NUMBERED_HEADING_MAX_WORDS;
}

function classifyParaLine(t: string): MdBlock {
  const h3 = /^### (.+)/.exec(t);
  if (h3) return { kind: "para", text: stripInlineMd(h3[1]!), style: "Heading 3" };
  const h2 = /^## (.+)/.exec(t);
  if (h2) return { kind: "para", text: stripInlineMd(h2[1]!), style: "Heading 2" };
  const h1 = /^# (.+)/.exec(t);
  if (h1) return { kind: "para", text: stripInlineMd(h1[1]!), style: "Heading 1" };
  const bullet = /^[-*] (.+)/.exec(t);
  if (bullet) return { kind: "para", text: stripInlineMd(bullet[1]!), style: "List Bullet" };
  if (/^\d+\.\d+\.\d+[ \t]/.exec(t) && isNumberedHeading(t)) return { kind: "para", text: stripInlineMd(t), style: "Heading 3" };
  if (/^\d+\.\d+[ \t]/.exec(t) && isNumberedHeading(t)) return { kind: "para", text: stripInlineMd(t), style: "Heading 2" };
  const sec1 = /^\d+\.[ \t](.+)/.exec(t);
  if (sec1) {
    const captured = sec1[1]!;
    // Strip a second leading "N. " when the LLM emits nested numbering (e.g. "6. 1. Item").
    const inner = /^\d+\.[ \t](.+)/.exec(captured);
    return { kind: "para", text: stripInlineMd(inner ? inner[1]! : captured), style: "List Number" };
  }
  return { kind: "para", text: stripInlineMd(t), style: "Normal" };
}

function upgradeSectionHeadings(result: MdBlock[]): void {
  // `1.` items are section headings when the block also contains 1.1/1.1.1
  // sub-level notation — upgrade them from "List Number" to "Heading 1".
  //
  // Guarded like the 1.1 / 1.1.1 branches: sub-level notation ANYWHERE in the
  // reply makes the whole block a "section context", so in a numbered document
  // a one-level numbered SENTENCE was promoted to Heading 1 on the strength of
  // some other line's numbering. That cost more than the style — classifyParaLine
  // strips the number into the List Number glyph, so the promoted paragraph also
  // lost the "2." a reader cites it by. Pass 1 for that stripped number so this
  // measures the line the user actually wrote.
  for (const block of result) {
    if (block.kind === "para" && block.style === "List Number" && isNumberedHeading(block.text, 1)) {
      block.style = "Heading 1";
    }
  }
}

function parseMdBlocks(raw: string): MdBlock[] {
  // Ensure heading markers always start a new line even when the LLM omits it.
  // Decode HTML non-breaking spaces before processing — the LLM sometimes uses
  // &nbsp; for indentation (e.g. "&nbsp;&nbsp;1.1 Title"), which would otherwise
  // appear as literal text in Word and block the sub-heading regex.
  const src = raw.replace(/\r/g, "").replace(/&nbsp;/g, " ").replace(/([^\n#])(#{1,3} )/g, "$1\n$2");
  const lines = src.split("\n");
  // Pre-scan: `1.` items are section headings only when the block also contains
  // sub-level section notation (1.1 / 1.1.1). Without those, they are plain
  // ordered-list items and should use "List Number" style instead of "Heading 1".
  const sectionContext = lines.some((l) => /^\s*\d+\.\d+(\.\d+)?[ \t]/.test(l));
  const result: MdBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const t = (lines[i] ?? "").trim();
    if (!t) { i++; continue; }
    const table = parseTableBlock(lines, i);
    if (table) {
      if (table.block) result.push(table.block);
      i = table.nextIdx;
      continue;
    }
    if (TABLE_SEP_RE.test(t)) { i++; continue; }
    result.push(classifyParaLine(t));
    i++;
  }
  if (sectionContext) upgradeSectionHeadings(result);
  return result;
}

// Both Word.Paragraph and Word.Table expose insertParagraph / insertTable so
// we can chain insertions without knowing which was last.
type WordInsertable = Word.Paragraph | Word.Table;

/**
 * Paragraph styles whose OWN formatting is the point — never overwrite them with
 * the cursor's captured font.
 *
 * `queueBlock` re-applies the font it read at the insertion point so inserted
 * body text matches the surrounding prose. That must not happen to structural
 * styles: stamping the body font over "Title" flattens it to look like an
 * ordinary paragraph, and the document then has no visible title and nothing at
 * the top of the navigation pane.
 *
 * AIVA-7019: the original guard was `style.startsWith("Heading")`, which covers
 * Heading 1-3 but silently misses "Title" — so an inserted outline lost exactly
 * its title line while every other heading survived. Naming the set explicitly
 * keeps the next structural style (Subtitle, TOC Heading) from falling through
 * the same crack.
 */
const SELF_FORMATTING_STYLES = new Set(["Title", "Subtitle", "TOC Heading"]);

function keepsOwnFormatting(style: string): boolean {
  return style.startsWith("Heading") || SELF_FORMATTING_STYLES.has(style);
}

/** Word style DISPLAY name → locale-independent styleBuiltIn id. Declared here
 *  rather than beside OOXML_STYLE_ID because applyBlockStyle runs above it. */
const BUILT_IN_STYLE_ID: Readonly<Record<string, string>> = {
  Title: "Title",
  Subtitle: "Subtitle",
  "Heading 1": "Heading1",
  "Heading 2": "Heading2",
  "Heading 3": "Heading3",
  "Heading 4": "Heading4",
  Normal: "Normal",
};

/**
 * Put a style on a paragraph by BOTH routes.
 *
 * `paragraph.style` takes the LOCALISED display name — "Heading 1" in English,
 * "Überschrift 1" in German — so setting it by the English name throws on a
 * non-English Word, and that throw was swallowed. The paragraph stayed Normal and
 * an inserted outline arrived as flat body text, losing TO-02 AC-2.
 *
 * `styleBuiltIn` takes the locale-independent id ("Heading1"), which is what Office
 * documents for exactly this case. Both are attempted: the display name keeps
 * working where it already did, and styleBuiltIn covers the locales where it never
 * could.
 */
function applyBlockStyle(paragraph: Word.Paragraph, style: string): void {
  try { paragraph.style = style; } catch { /* localised name unavailable in this host */ }
  const builtIn = BUILT_IN_STYLE_ID[style];
  if (!builtIn) return;
  try {
    (paragraph as unknown as { styleBuiltIn: string }).styleBuiltIn = builtIn;
  } catch { /* host without styleBuiltIn — the display name above stands */ }
}

function queueBlock(
  anchor: { kind: "body"; ctx: Word.RequestContext } | { kind: "after"; prev: WordInsertable } | { kind: "range"; range: Word.Range },
  block: MdBlock,
  font: CapturedFont,
): WordInsertable {
  if (block.kind === "para") {
    let p: Word.Paragraph;
    if (anchor.kind === "body") {
      p = anchor.ctx.document.body.insertParagraph(block.text, Word.InsertLocation.end);
    } else if (anchor.kind === "range") {
      p = anchor.range.insertParagraph(block.text, Word.InsertLocation.after);
    } else {
      p = anchor.prev.insertParagraph(block.text, Word.InsertLocation.after);
    }
    applyBlockStyle(p, block.style);
    // Re-apply the selection's direct font over the style default — but leave
    // structural styles at their own (larger) size to keep document structure.
    // Alignment rides the SAME exemption: a heading in a justified document is
    // normally left-aligned, and its own style already says so, so stamping the
    // body's alignment over it would be the AIVA-5835 flattening again.
    if (!keepsOwnFormatting(block.style)) {
      applyCapturedFont(p, font);
      applyCapturedAlignment(p, font);
    }
    return p;
  }
  // table block
  const allRows = [block.headers, ...block.rows];
  const colCount = block.headers.length;
  const tableData = allRows.map((row) => {
    const padded = [...row];
    while (padded.length < colCount) padded.push("");
    return padded.slice(0, colCount);
  });
  let t: Word.Table;
  if (anchor.kind === "body") {
    t = anchor.ctx.document.body.insertTable(allRows.length, colCount, Word.InsertLocation.end, tableData);
  } else if (anchor.kind === "range") {
    t = anchor.range.insertTable(allRows.length, colCount, Word.InsertLocation.after, tableData);
  } else {
    t = anchor.prev.insertTable(allRows.length, colCount, Word.InsertLocation.after, tableData);
  }
  try { (t as { style?: string }).style = "Table Grid"; } catch { /* style not available */ }
  return t;
}

function queueMdBlocksToBody(context: Word.RequestContext, blocks: MdBlock[], font: CapturedFont = {}): WordInsertable | null {
  let prev: WordInsertable | null = null;
  for (const block of blocks) {
    const anchor = prev === null
      ? { kind: "body" as const, ctx: context }
      : { kind: "after" as const, prev };
    prev = queueBlock(anchor, block, font);
  }
  return prev;
}

async function scrollToAppended(
  context: Word.RequestContext,
  // Also takes the Range insertOoxml returns — selection is not an undoable
  // edit, so scrolling costs the single-undo guarantee nothing.
  target: WordInsertable | Word.Range | null,
): Promise<void> {
  if (!target) return;
  try {
    target.getRange(Word.RangeLocation.whole).select();
    await context.sync();
  } catch (err) {
    logOfficeError(err, "word-scroll-to-appended");
  }
}

async function queueMdBlocksInline(
  context: Word.RequestContext,
  range: Word.Range,
  blocks: MdBlock[],
  fonts: CapturedFont[] = [],
): Promise<void> {
  const first = blocks[0];
  if (first?.kind === "para" && first.style === "Normal") {
    const inserted = range.insertText(first.text, Word.InsertLocation.replace);
    const p = inserted.paragraphs.getFirst();
    applyCapturedFont(p, fontForBlock(fonts, 0));
    // A no-op when this is the selection's own surviving paragraph (it already
    // has the alignment we captured from it), and the fix when the replace
    // collapsed a multi-paragraph selection into a fresh one.
    applyCapturedAlignment(p, fontForBlock(fonts, 0));
    // Commit the replacement before anchoring the follow-on paragraphs. When
    // the selection spans MULTIPLE paragraphs, "Replace" deletes them all and
    // collapses the range down to the inserted line — but that collapse only
    // happens on sync. Without this sync the queued insertParagraph("After")
    // calls resolve against the not-yet-collapsed multi-paragraph range and are
    // lost, so only the first paragraph of the response ever lands. A single
    // paragraph selection survived because there was nothing to collapse
    // (AIVA-6927).
    await context.sync();
    let prev: WordInsertable = p;
    for (let i = 1; i < blocks.length; i++) {
      prev = queueBlock({ kind: "after", prev }, blocks[i]!, fontForBlock(fonts, i));
    }
    return;
  }

  const start = range.insertText("", Word.InsertLocation.replace);
  // Same reason as above: commit the cleared (possibly multi-paragraph)
  // selection before inserting the parsed blocks after it (AIVA-6927).
  await context.sync();
  let prev: WordInsertable | null = null;
  let i = 0;
  for (const block of blocks) {
    const anchor = prev === null
      ? { kind: "range" as const, range: start }
      : { kind: "after" as const, prev };
    prev = queueBlock(anchor, block, fontForBlock(fonts, i));
    i++;
  }
}

// ---------------------------------------------------------------------------
// Single-undo replacement rendering (AIVA-6928)
// ---------------------------------------------------------------------------
//
// replaceWordSelection used to apply a transformation-command result through
// several separate Word edits inside one Word.run: insertText to swap the first
// line, a formatting call to re-apply the captured font, then insertParagraph
// (plus a style and font set) for EACH further paragraph — with a mid-run sync
// (AIVA-6927) in between. Word records ONE undo entry per edit and offers no way
// to group them, so pressing Ctrl+Z restored the original in stages: 2 presses
// for a single paragraph (delete + re-format), 3+ for several. Rendering the
// whole result as one HTML fragment and inserting it with a single
// Range.insertHtml collapses that to a single undo — and because insertHtml
// replaces the (possibly multi-paragraph) selection atomically, the AIVA-6927
// mid-run sync the paragraph-by-paragraph path needed is no longer required.

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Turn a captured font into inline CSS so the inserted HTML carries the same
// direct formatting applyCapturedFont used to set on the paragraph. Returns ""
// when nothing usable was captured (a mixed/blank selection), which leaves the
// paragraph style's own defaults in place — exactly as the old path did.
function fontToInlineStyle(font: CapturedFont): string {
  const decls: string[] = [];
  if (font.name) decls.push(`font-family:'${font.name.replace(/'/g, "")}'`);
  if (font.size !== undefined) decls.push(`font-size:${font.size}pt`);
  if (font.bold) decls.push("font-weight:bold");
  if (font.italic) decls.push("font-style:italic");
  const decoration: string[] = [];
  // Word.UnderlineType.none is the string "None"; compare via String() so this
  // works whether Word reports the enum member or the raw string.
  if (font.underline && String(font.underline) !== "None") decoration.push("underline");
  if (font.strikeThrough) decoration.push("line-through");
  if (decoration.length > 0) decls.push(`text-decoration:${decoration.join(" ")}`);
  if (font.color) decls.push(`color:${font.color}`);
  if (font.highlightColor) decls.push(`background-color:${font.highlightColor}`);
  return decls.join(";");
}

const HEADING_TAG: Readonly<Record<string, string>> = {
  "Heading 1": "h1",
  "Heading 2": "h2",
  "Heading 3": "h3",
};

// `style` is the run-level font, and is set for the clipboard only — a table
// cell with no font of its own pastes in Word's HTML default (Times New Roman)
// rather than the source's, the same AIVA-7074 failure as a bare <p>. The
// insert path passes "" and its markup is unchanged.
// Word applies the DESTINATION paragraph's list formatting to pasted content
// that does not say otherwise. Copying a table out of a chat reply and pasting
// it into a document whose outline is auto-numbered therefore numbered every
// cell — "1. Purpose and scope / 2. Item / 3. Description ..." — even though
// the clipboard HTML contains no list markup at all (AIVA-8038). `mso-list:none`
// is Word's own opt-out for exactly that inheritance.
//
// UNVERIFIED IN THE HOST. It is a different hint from the `mso-style-name`
// attempt documented in markdownToClipboardHtml (that one asked Word to ADOPT a
// style; this one asks it to STOP adopting one), and it is inert everywhere
// else, so it cannot regress a non-Word target. But it needs confirming by
// pasting into a numbered document before it is treated as fixed.
const CELL_NO_LIST = "mso-list:none";

function tableCell(text: string, tag: "th" | "td", style: string): string {
  const inner = style ? `<span style="${style}">${escapeHtml(text)}</span>` : escapeHtml(text);
  return `<${tag} style="${CELL_NO_LIST}">${inner}</${tag}>`;
}

function tableRow(cells: string[], tag: "th" | "td", style: string): string {
  const inner = cells.map((c) => tableCell(c, tag, style)).join("");
  return `<tr>${inner}</tr>`;
}

/** Square a ragged row off against the header count. Shared by the HTML and
 *  OOXML renderers so a short row cannot come out as a different table shape
 *  depending on which one wrote it. */
function padRow(row: string[], colCount: number): string[] {
  const padded = [...row];
  while (padded.length < colCount) padded.push("");
  return padded.slice(0, colCount);
}

function tableBlockToHtml(
  block: { headers: string[]; rows: string[][] },
  style = "",
): string {
  const colCount = block.headers.length;
  const head = tableRow(block.headers, "th", style);
  const body = block.rows.map((r) => tableRow(padRow(r, colCount), "td", style)).join("");
  return `<table><thead>${head}</thead><tbody>${body}</tbody></table>`;
}

/**
 * How a block run is rendered.
 *
 * `inlineFirst` (AIVA-6915) emits a leading plain-prose paragraph as inline
 * content so an INSERT folds it into the paragraph the cursor sat in.
 *
 * `runLevelFont` (AIVA-7074) repeats the font on a `<span>` inside each block,
 * as well as on the block element itself.
 *
 * The span matters only on paste. A style on `<p>` reaches Word as PARAGRAPH
 * formatting, and Word drops the pasted paragraph's properties whenever it
 * merges that paragraph into an existing one — which is what happens when the
 * paste lands mid-line rather than on an empty line. The destination's font
 * then wins, so the same clipboard pasted two ways gave two different fonts:
 * the source font on a new line, the destination's theme font on an existing
 * line. A span is CHARACTER formatting and survives the merge, so both land the
 * same. Off for the insert paths, whose HTML goes in through insertHtml and
 * never takes the clipboard's merge rules.
 */
interface HtmlRenderOptions {
  inlineFirst?: boolean;
  runLevelFont?: boolean;
}

// Render one paragraph block. Body text and list items carry the captured font
// as inline CSS (AIVA-6926/6081/5835).
//
// Headings are the ONE deliberate exception to "the paste keeps the source
// font" (AIVA-7074): they keep their style with no direct font, because the
// font captured from a prose selection is the BODY font, and stamping its
// family and size over a heading flattens the heading to look like ordinary
// text — losing the size, the weight and the document structure with it
// (AIVA-5835). A pasted heading takes the destination's Heading style instead,
// which is the sane answer for a heading.
function paraBlockToHtml(
  block: { text: string; style: string },
  font: CapturedFont,
  runLevelFont = false,
): string {
  const escaped = escapeHtml(block.text);
  const heading = HEADING_TAG[block.style];
  if (heading) return `<${heading}>${escaped}</${heading}>`;
  const style = fontToInlineStyle(font);
  // text-align goes on the BLOCK element only, never into the run-level span
  // below: it is paragraph formatting, and a `text-align` on an inline span is
  // meaningless to Word's importer (AIVA-7903).
  const blockStyle = font.alignment
    ? [style, `text-align:${alignmentToCss(font.alignment)}`].filter(Boolean).join(";")
    : style;
  const styleAttr = blockStyle ? ` style="${blockStyle}"` : "";
  const body = runLevelFont && style ? `<span style="${style}">${escaped}</span>` : escaped;
  if (block.style === "List Bullet" || block.style === "List Number") {
    return `<li${styleAttr}>${body}</li>`;
  }
  return `<p${styleAttr}>${body}</p>`;
}

function isListBlock(block: MdBlock): boolean {
  return block.kind === "para" && (block.style === "List Bullet" || block.style === "List Number");
}

// Inline-first (AIVA-6915): a leading plain-prose paragraph is emitted as inline
// content (a span, not a block element) so Word folds it into the paragraph the
// selection sat in rather than pushing it onto a new line. A structured first
// block (heading/list/table) starts its own paragraph. Returns the index of the
// first block still to render as block-level content.
/**
 * True when `blocksToHtml` would fold the first block into the paragraph the
 * selection sat in — a leading plain-prose paragraph (AIVA-6915).
 *
 * Extracted because `replaceWordSelection` needs the SAME question to pick its
 * renderer, and two spellings of one rule is how they drift.
 */
function renderInlineFirst(blocks: MdBlock[], fonts: CapturedFont[], parts: string[]): number {
  const first = blocks[0];
  if (first?.kind !== "para" || first.style !== "Normal") return 0;
  const style = fontToInlineStyle(fontForBlock(fonts, 0));
  const text = escapeHtml(first.text);
  parts.push(style ? `<span style="${style}">${text}</span>` : text);
  return 1;
}

// Group the run of consecutive list items of the same kind starting at `start`
// into one <ul>/<ol> so Word renders a single list. Returns the list HTML and
// the index of the next block that is not part of this list.
function renderList(
  blocks: MdBlock[],
  fonts: CapturedFont[],
  start: number,
  runLevelFont: boolean,
): { html: string; next: number } {
  const firstItem = blocks[start]!;
  const listStyle = firstItem.kind === "para" ? firstItem.style : "";
  const listTag = listStyle === "List Bullet" ? "ul" : "ol";
  const items: string[] = [];
  let i = start;
  while (i < blocks.length) {
    const b = blocks[i]!;
    if (b.kind !== "para" || b.style !== listStyle) break;
    items.push(paraBlockToHtml(b, fontForBlock(fonts, i), runLevelFont));
    i++;
  }
  return { html: `<${listTag}>${items.join("")}</${listTag}>`, next: i };
}

function blocksToHtml(
  blocks: MdBlock[],
  fonts: CapturedFont[],
  { inlineFirst = true, runLevelFont = false }: HtmlRenderOptions = {},
): string {
  const parts: string[] = [];
  let i = inlineFirst ? renderInlineFirst(blocks, fonts, parts) : 0;
  while (i < blocks.length) {
    const block = blocks[i]!;
    if (block.kind === "table") {
      parts.push(
        tableBlockToHtml(block, runLevelFont ? fontToInlineStyle(fontForBlock(fonts, i)) : ""),
      );
      i++;
    } else if (isListBlock(block)) {
      const { html, next } = renderList(blocks, fonts, i, runLevelFont);
      parts.push(html);
      i = next;
    } else {
      parts.push(paraBlockToHtml(block, fontForBlock(fonts, i), runLevelFont));
      i++;
    }
  }
  return parts.join("");
}

/**
 * Clipboard HTML for the Copy pill, carrying the font of the text the user is
 * working on.
 *
 * AIVA-7074: Copy had its own miniature renderer (`markdownToClipboardHtml`)
 * that emitted bare `<p>` / `<h1>` / `<ul>` with no font on them at all, so Word
 * pasted the content in the HTML default face and size instead of the
 * document's — the reported "font and formatting are missing". Having no table,
 * numbered-list or inline-marker handling either, it also pasted `**bold**` and
 * `| a | b |` as literal characters. Routing Copy through the same
 * parseMdBlocks + blocksToHtml pipeline the placement pills use puts on the
 * clipboard exactly what an insert would have written into the document.
 *
 * The fonts come from the selection for the same reason `replaceWordSelection`
 * reads them there: the user selected text, ran a slash command over it, and
 * expects the result back in the formatting it went in with.
 *
 * `inlineFirst` is off. That rendering exists so an insert folds its first
 * paragraph into the paragraph the cursor sat in — a property of the insertion
 * point, which the clipboard does not have.
 *
 * `runLevelFont` is on, so the font survives a paste that lands mid-line — see
 * HtmlRenderOptions.
 *
 * Never throws: a host that refuses the font read still gets the structural
 * HTML, just without the direct formatting. Returns "" when there is nothing to
 * render, leaving the caller on its own fallback.
 */
export async function buildWordClipboardHtml(text: string): Promise<string> {
  const blocks = parseMdBlocks(text);
  if (blocks.length === 0) return "";
  let fonts: CapturedFont[] = [];
  try {
    fonts = await Word.run((context) => captureSelectionParagraphFonts(context));
  } catch {
    // No Word host (or a selection read this host rejects) — structure only.
  }
  return blocksToHtml(blocks, fonts, { inlineFirst: false, runLevelFont: true });
}

// ---------------------------------------------------------------------------
// The single insertion routine (W41)
// ---------------------------------------------------------------------------
//
// One renderer and one insert call, shared by the canned template frames (W40)
// and the AI outline path, so heading styles, [Square Brackets] stubs and undo
// cannot drift between them.
//
// OOXML rather than the HTML rendering above, for one reason: HTML has no
// element that maps to Word's "Title" style — `mso-style-name` was tried in this
// host and ignored (see markdownToClipboardHtml) — so an HTML-rendered outline
// loses exactly its title line, which TO-02 AC-2 requires as a real style.
// OOXML names the style directly (`<w:pStyle w:val="Title"/>`), and a single
// insertOoxml is a single Word operation, so the whole insertion is ONE undo
// entry (AIVA-6928). The paragraph-by-paragraph path stays as the fallback for
// a host that rejects the package: it costs the single undo, but the content
// still lands.

/** Word style DISPLAY name → OOXML styleId. Word matches on the id, which has
 *  no spaces — "Heading 1" is `Heading1`. An unmapped style falls back to
 *  Normal rather than emitting an id Word would silently ignore. */
const OOXML_STYLE_ID: Readonly<Record<string, string>> = {
  Title: "Title",
  Subtitle: "Subtitle",
  "Heading 1": "Heading1",
  "Heading 2": "Heading2",
  "Heading 3": "Heading3",
  "List Bullet": "ListBullet",
  "List Number": "ListNumber",
  Normal: "Normal",
};

function escapeXml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** Run properties carrying the captured font, as `applyCapturedFont` would have
 *  set them. Empty for the structural styles whose own formatting is the point
 *  (see keepsOwnFormatting) and when nothing usable was captured. */
/** Word reports enum values capitalised ("Single", "Yellow"); OOXML spells them
 *  in camelCase. */
function ooxmlEnumValue(value: string): string {
  return value.charAt(0).toLowerCase() + value.slice(1);
}

/** The on/off run properties. `undefined` means the selection was mixed and the
 *  property was never captured, which is different from captured-as-false —
 *  hence the explicit `w:val="0"` rather than simply omitting the element. */
function fontToggleProps(font: CapturedFont): string[] {
  const toggle = (on: boolean | undefined, tag: string): string => {
    if (on === undefined) return "";
    return on ? `<w:${tag}/>` : `<w:${tag} w:val="0"/>`;
  };
  return [toggle(font.bold, "b"), toggle(font.italic, "i"), toggle(font.strikeThrough, "strike")].filter(
    Boolean,
  );
}

/** Colour and highlight. Both are dropped unless they are in a form OOXML can
 *  express: `w:color` takes bare hex, so a named colour ("Red") has no spelling
 *  and writing one would emit a value Word silently ignores. */
function fontColourProps(font: CapturedFont): string[] {
  const parts: string[] = [];
  const hex = /^#?([0-9a-fA-F]{6})$/.exec(font.color ?? "");
  if (hex) parts.push(`<w:color w:val="${hex[1]}"/>`);
  if (font.highlightColor && /^[a-zA-Z]+$/.test(font.highlightColor)) {
    parts.push(`<w:highlight w:val="${ooxmlEnumValue(font.highlightColor)}"/>`);
  }
  return parts;
}

function fontToRunProps(font: CapturedFont, style: string): string {
  if (keepsOwnFormatting(style)) return "";
  const parts: string[] = [];
  if (font.name) parts.push(`<w:rFonts w:ascii="${escapeXml(font.name)}" w:hAnsi="${escapeXml(font.name)}"/>`);
  parts.push(...fontToggleProps(font));
  // Word.UnderlineType.none is the string "None", which OOXML spells "none".
  if (font.underline !== undefined) {
    parts.push(`<w:u w:val="${escapeXml(ooxmlEnumValue(String(font.underline)))}"/>`);
  }
  parts.push(...fontColourProps(font));
  // w:sz is HALF-points — 11pt is 22.
  if (font.size !== undefined) parts.push(`<w:sz w:val="${Math.round(font.size * 2)}"/>`);
  return parts.length > 0 ? `<w:rPr>${parts.join("")}</w:rPr>` : "";
}

function paraToOoxml(block: { text: string; style: string }, font: CapturedFont): string {
  const styleId = OOXML_STYLE_ID[block.style] ?? "Normal";
  const runProps = fontToRunProps(font, block.style);
  return (
    `<w:p><w:pPr><w:pStyle w:val="${styleId}"/></w:pPr>` +
    `<w:r>${runProps}<w:t xml:space="preserve">${escapeXml(block.text)}</w:t></w:r></w:p>`
  );
}

function tableToOoxml(block: { headers: string[]; rows: string[][] }): string {
  const colCount = block.headers.length;
  const cell = (text: string, bold: boolean): string =>
    `<w:tc><w:tcPr><w:tcW w:w="0" w:type="auto"/></w:tcPr>` +
    `<w:p><w:pPr><w:pStyle w:val="Normal"/></w:pPr>` +
    `<w:r>${bold ? "<w:rPr><w:b/></w:rPr>" : ""}<w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r></w:p></w:tc>`;
  const row = (cells: string[], bold: boolean): string =>
    `<w:tr>${padRow(cells, colCount).map((c) => cell(c, bold)).join("")}</w:tr>`;
  // Borders written out rather than left to the TableGrid style: the style may
  // not exist in the target document, and a borderless "grid" reads as a layout
  // accident. Matches what queueBlock asks for by name.
  const borders = ["top", "left", "bottom", "right", "insideH", "insideV"]
    .map((side) => `<w:${side} w:val="single" w:sz="4" w:space="0" w:color="auto"/>`)
    .join("");
  return (
    `<w:tbl><w:tblPr><w:tblStyle w:val="TableGrid"/><w:tblW w:w="0" w:type="auto"/>` +
    `<w:tblBorders>${borders}</w:tblBorders></w:tblPr>` +
    row(block.headers, true) +
    block.rows.map((r) => row(r, false)).join("") +
    `</w:tbl>`
  );
}

const OOXML_RELS =
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" ' +
  'Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" ' +
  'Target="/word/document.xml"/></Relationships>';

/**
 * Render parsed blocks as a single OOXML package.
 *
 * Exported for the tests: this is the half that can be asserted without a Word
 * host, and the styles it names are the acceptance criterion.
 */
export function blocksToOoxml(blocks: MdBlock[], fonts: CapturedFont[] = []): string {
  const body = blocks
    .map((block, i) =>
      block.kind === "para"
        ? paraToOoxml(block, fontForBlock(fonts, i))
        // A trailing empty paragraph after a table: two adjacent OOXML tables
        // merge into one without a paragraph between them.
        : `${tableToOoxml(block)}<w:p/>`,
    )
    .join("");
  return wrapOoxmlPackage(body);
}

/** Wrap document-body XML in the Flat OPC package `insertOoxml` requires. */
function wrapOoxmlPackage(body: string): string {
  return (
    '<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">' +
    '<pkg:part pkg:name="/_rels/.rels" ' +
    'pkg:contentType="application/vnd.openxmlformats-package.relationships+xml" pkg:padding="512">' +
    `<pkg:xmlData>${OOXML_RELS}</pkg:xmlData></pkg:part>` +
    '<pkg:part pkg:name="/word/document.xml" ' +
    'pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">' +
    '<pkg:xmlData><w:document ' +
    'xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
    `<w:body>${body}</w:body></w:document></pkg:xmlData></pkg:part></pkg:package>`
  );
}

/**
 * Insert blocks as ONE Word operation — the routine both outline paths use.
 *
 * `where` is the only difference between them: a template appends to the body,
 * an outline replaces the cursor range. Everything downstream — the styles, the
 * stubs, the single undo — is identical by construction.
 */
async function insertBlocksAsOoxml(
  blocks: MdBlock[],
  where: "body-end" | "cursor",
  fonts?: (context: Word.RequestContext) => Promise<CapturedFont[]>,
): Promise<boolean> {
  if (blocks.length === 0) return true;
  try {
    await Word.run(async (context) => {
      const captured = fonts ? await fonts(context) : [];
      const ooxml = blocksToOoxml(blocks, captured);
      const range =
        where === "body-end"
          ? context.document.body.insertOoxml(ooxml, Word.InsertLocation.end)
          : context.document.getSelection().getRange(Word.RangeLocation.end).insertOoxml(ooxml, Word.InsertLocation.replace);
      await context.sync();
      await scrollToAppended(context, range);
    });
    return true;
  } catch (err) {
    logOfficeError(err, "word-insert-ooxml");
    return false;
  }
}

/**
 * True when the LAST block carries a paragraph style the host paragraph mark
 * would override on an HTML replace.
 *
 * That merge is the whole problem: Word's HTML importer folds the final
 * inserted block into the paragraph mark of the range it replaced, and a
 * paragraph mark owns the paragraph style — so the mark's style wins. When the
 * final block is Normal (ordinary prose, the common /improve or /grammar
 * reply) the mark is already Normal and the merge costs nothing. When it is a
 * bullet or a heading, the style is silently lost.
 */
function endsWithStyledParagraph(blocks: MdBlock[]): boolean {
  const last = blocks[blocks.length - 1];
  return last?.kind === "para" && last.style !== "Normal";
}

/** Replace the selection paragraph-by-paragraph, styling each one explicitly. */
async function replaceSelectionByParagraph(blocks: MdBlock[]): Promise<boolean> {
  try {
    await Word.run(async (context) => {
      const fonts = await captureSelectionParagraphFonts(context);
      await queueMdBlocksInline(context, context.document.getSelection(), blocks, fonts);
      await context.sync();
    });
    return true;
  } catch {
    return false;
  }
}

/** The one font every captured paragraph shares, or null when they differ. */
function uniformFont(fonts: CapturedFont[]): CapturedFont | null {
  const first = fonts[0];
  if (!first) return null;
  const same = fonts.every(
    (f) =>
      f.name === first.name &&
      f.size === first.size &&
      f.bold === first.bold &&
      f.italic === first.italic &&
      f.color === first.color,
  );
  return same ? first : null;
}

/**
 * Set the captured font on the range `insertHtml` returned.
 *
 * AIVA-7077: Word's HTML importer does not keep the character formatting the
 * fragment carries. The inline CSS is right — the tests below assert
 * `font-family` is in the generated HTML, and reading the host confirms the
 * captured font is the selection's real one — but the inserted run still lands
 * with NO font of its own. That is what "Aptos (Body)" in the ribbon means: not
 * a font Word chose, but the theme's Body slot showing through because nothing
 * explicit was set. A Times New Roman list item came back in the theme font on
 * every replace.
 *
 * A property set through the API cannot be dropped the way a CSS declaration
 * can. It is queued into the same sync as the insert, so it costs no extra
 * round-trip — only a second undo entry, which is the price of the content
 * arriving in the right font at all (AIVA-6928 kept the insert itself atomic).
 *
 * Deliberately narrow: it runs only when ONE font covers the whole insertion.
 *  - A reply containing a heading or other self-formatting style is skipped — a
 *    range-wide stamp cannot exempt it, and headings keep their own size and
 *    family on purpose (AIVA-5835).
 *  - A selection whose paragraphs carried DIFFERENT fonts is skipped rather than
 *    flattened to the first one, which would undo AIVA-6926.
 * Both of those keep exactly the behaviour that is there today.
 */
function stampFontOnInsertedHtml(inserted: Word.Range, blocks: MdBlock[], fonts: CapturedFont[]): void {
  if (blocks.some((b) => b.kind === "para" && keepsOwnFormatting(b.style))) return;
  const font = uniformFont(fonts);
  if (font) applyCapturedFont(inserted, font);
}

/** The one alignment every captured paragraph shares, or undefined when they
 *  differ or none was resolved. Separate from uniformFont because the font
 *  comparison there ignores alignment: paragraphs can share an alignment while
 *  disagreeing on colour, and that case must still keep the alignment. */
function uniformAlignment(fonts: CapturedFont[]): CapturedAlignment | undefined {
  const first = fonts[0]?.alignment;
  if (!first) return undefined;
  return fonts.every((f) => f.alignment === first) ? first : undefined;
}

/**
 * Set the captured alignment on the paragraphs `insertHtml` produced
 * (AIVA-7903).
 *
 * The `text-align` in the fragment is the primary mechanism and usually
 * survives — paragraph properties import better than the character formatting
 * AIVA-7077 had to work around. "Usually" is not good enough for the one
 * property the user is complaining about, so the API sets it too; when the CSS
 * did land this re-sets the same value and is invisible.
 *
 * Unlike the font stamp this cannot ride the insert's own sync: alignment lives
 * on Paragraph, not on the Range insertHtml returns, so the paragraphs have to
 * be enumerated first. That costs two extra round trips and an extra undo entry
 * — so it is spent ONLY when there is a non-left alignment to restore. A
 * left-aligned document (the overwhelming majority) takes the same single-sync,
 * single-undo path it takes today and pays nothing.
 */
async function stampAlignmentOnInsertedHtml(
  context: Word.RequestContext,
  inserted: Word.Range,
  blocks: MdBlock[],
  fonts: CapturedFont[],
): Promise<void> {
  if (blocks.some((b) => b.kind === "para" && keepsOwnFormatting(b.style))) return;
  const alignment = uniformAlignment(fonts);
  // "Left" is already what a new paragraph inherits from Normal, so restoring
  // it explicitly would buy nothing for the round trips it costs.
  if (!alignment || alignment === "Left") return;
  try {
    const paragraphs = inserted.paragraphs;
    paragraphs.load("items");
    await context.sync();
    for (const p of paragraphs.items) p.alignment = alignment as Word.Alignment;
    await context.sync();
  } catch { /* alignment not settable on this host — the fragment's CSS stands */ }
}

/** Replace the selection with one insertHtml — one Word operation, one undo. */
async function replaceSelectionByHtml(blocks: MdBlock[]): Promise<boolean> {
  try {
    await Word.run(async (context) => {
      const fonts = await captureSelectionParagraphFonts(context);
      const sel = context.document.getSelection();
      // One insertHtml = one undo entry (AIVA-6928). It also replaces a
      // multi-paragraph selection atomically, so the AIVA-6927 mid-run sync is
      // not needed here.
      const inserted = sel.insertHtml(blocksToHtml(blocks, fonts), Word.InsertLocation.replace);
      // The HTML's own font declarations do not survive the importer — see
      // stampFontOnInsertedHtml (AIVA-7077).
      stampFontOnInsertedHtml(inserted, blocks, fonts);
      await context.sync();
      // After the insert's sync, not queued into it: the paragraphs it created
      // have to exist before they can be enumerated (AIVA-7903).
      await stampAlignmentOnInsertedHtml(context, inserted, blocks, fonts);
    });
    return true;
  } catch {
    return false;
  }
}

export async function replaceWordSelection(text: string): Promise<void> {
  if (!text.trim()) return;
  const blocks = parseMdBlocks(text);
  // Content ENDING in a styled paragraph goes paragraph-by-paragraph; everything
  // else keeps the single-undo insertHtml route.
  //
  // Only the ending matters, because only the last block is at risk — see
  // endsWithStyledParagraph. A three-bullet summary arrived as two bullets plus
  // a flush-left sentence; the HTML we sent was correct (`<ul>` with three
  // `<li>`), so the loss was the importer's. The paragraph API sets
  // `paragraph.style` itself and never asks Word to infer one, which is why the
  // same text placed by "Insert at cursor" keeps every bullet in the host today.
  //
  // insertOoxml was tried instead and is WORSE: `blocksToOoxml` emits only
  // document.xml with no numbering.xml, and in OOXML the bullet glyph comes from
  // `<w:numPr>` and a numbering definition, not from `<w:pStyle w:val="ListBullet"/>`.
  // Every bullet was lost, not just the last. Headings survive that route (they
  // need no numbering), which is why insertWordOutline is right to keep it.
  //
  // The narrow split is deliberate. Prose replies — /improve, /grammar, /formal,
  // the bulk of Word usage — end in a Normal paragraph, so they stay on
  // insertHtml and keep AIVA-6928's single undo and AIVA-6915's inline-first
  // fold. Only the shape that is actually broken changes mechanism, and it pays
  // the multi-operation undo that "Insert at cursor" already pays today.
  const preferred = endsWithStyledParagraph(blocks) ? replaceSelectionByParagraph : replaceSelectionByHtml;
  const other = preferred === replaceSelectionByParagraph ? replaceSelectionByHtml : replaceSelectionByParagraph;
  if (await preferred(blocks)) return;
  // The other in-place route before the body append: either keeps the content
  // WHERE THE SELECTION WAS, which the append below does not.
  if (await other(blocks)) return;
  try {
    await Word.run(async (context) => {
      queueMdBlocksToBody(context, blocks);
      await context.sync();
    });
  } catch { /* best effort */ }
}

/** Insert parsed blocks inline at the cursor with the captured selection font,
 *  falling back to a plain body append when the host rejects the inline route.
 *  Shared by insertAtWordCursor and insertWordOutline's paragraph fallback so
 *  the two cascades cannot drift. */
async function insertBlocksAtCursorWithFallback(blocks: MdBlock[]): Promise<void> {
  try {
    await Word.run(async (context) => {
      const font = await captureSelectionFont(context);
      const cursor = context.document.getSelection().getRange(Word.RangeLocation.end);
      await queueMdBlocksInline(context, cursor, blocks, [font]);
      await context.sync();
    });
  } catch {
    try {
      await Word.run(async (context) => {
        queueMdBlocksToBody(context, blocks);
        await context.sync();
      });
    } catch { /* best effort */ }
  }
}

export async function insertAtWordCursor(text: string): Promise<void> {
  if (!text.trim()) return;
  await insertBlocksAtCursorWithFallback(parseMdBlocks(text));
}

/** Contract levels to real Word styles (TO-02 AC-2). */
const OUTLINE_LEVEL_STYLE: Record<string, string> = {
  title: "Title",
  "1": "Heading 1",
  "2": "Heading 2",
  "3": "Heading 3",
};

export interface OutlineSectionInput {
  heading: string;
  level: "title" | 1 | 2 | 3;
  stub?: string;
}

/**
 * A read outline's rows as insertable sections.
 *
 * Pure, and shared by the two places that need it: the insert pill's handler,
 * and the render layer that synthesises that pill without waiting for the model
 * to call it. One definition, so the button and the card can never disagree
 * about what would be inserted.
 *
 * `stub` is empty on purpose — a read outline has no guidance to add, and
 * inventing one would write text into the document that was never in it. Levels
 * clamp at 3 because the insert path styles Title/1/2/3 only, so a deeper row
 * lands as Heading 3 rather than losing its heading-ness entirely.
 */
export function outlineRowsToSections(rows: readonly OutlineRow[]): OutlineSectionInput[] {
  return rows.map((row) => ({
    heading: row.text,
    level: Math.min(row.level, 3) as 1 | 2 | 3,
    stub: "",
  }));
}

/**
 * Sections as markdown — what the Copy pill puts on the clipboard.
 *
 * Heading MARKERS rather than indentation: `markdownToClipboardHtml` converts
 * them, so a paste lands as real Word headings. The plain-text version this
 * replaced had none, so Copy produced an unstyled block while Insert — from the
 * same outline — produced real headings.
 */
export function outlineSectionsToMarkdown(sections: readonly OutlineSectionInput[]): string {
  const hashes: Record<string, string> = { title: "#", "1": "#", "2": "##", "3": "###" };
  const lines: string[] = [];
  for (const s of sections) {
    lines.push(`${hashes[String(s.level)] ?? "#"} ${s.heading}`);
    if (s.stub?.trim()) lines.push("", s.stub.trim());
    lines.push("");
  }
  return lines.join("\n").trim();
}

/**
 * Insert a parsed outline, mapping each contract level to its Word style.
 *
 * Goes through the SAME parseMdBlocks -> queueBlock pipeline every other
 * insertion uses, so heading styles, stub handling and undo behave identically
 * however the outline was produced — the single-routine requirement. Only the
 * SOURCE differs: here the levels arrive already structured, so nothing has to
 * be inferred from '#' markers.
 *
 * That is what makes `title` expressible at all: markdown has no way to
 * distinguish a document Title from a Heading 1, so the heading-count path
 * could never satisfy AC-2. Title is a real Word style and shows in the
 * navigation pane differently.
 */
export async function insertWordOutline(sections: OutlineSectionInput[]): Promise<void> {
  if (!sections.length) return;
  const blocks: MdBlock[] = [];
  for (const s of sections) {
    const style = OUTLINE_LEVEL_STYLE[String(s.level)] ?? "Heading 1";
    blocks.push({ kind: "para", text: s.heading, style });
    // Stubs are Normal body text carrying the [Square Brackets] slots verbatim
    // (AC-3) — they are what makes an inserted skeleton visibly unfinished.
    if (s.stub?.trim()) blocks.push({ kind: "para", text: s.stub, style: "Normal" });
  }
  // The shared single-undo routine first (W41); the paragraph path below is the
  // fallback for a host that rejects the OOXML package.
  if (await insertBlocksAsOoxml(blocks, "cursor", async (ctx) => [await captureSelectionFont(ctx)])) return;
  await insertBlocksAtCursorWithFallback(blocks);
}

export async function appendToWordEnd(text: string): Promise<void> {
  if (!text.trim()) return;
  const blocks = parseMdBlocks(text);
  // Same routine the AI outline path uses (W41) — a canned template frame and a
  // generated outline reach the document through identical code from here on.
  if (await insertBlocksAsOoxml(blocks, "body-end", async (ctx) => [await captureSelectionFont(ctx)])) return;
  try {
    await Word.run(async (context) => {
      const font = await captureSelectionFont(context);
      const last = queueMdBlocksToBody(context, blocks, font);
      await context.sync();
      await scrollToAppended(context, last);
    });
  } catch { /* best effort */ }
}

export function getWordDocumentAsBase64(): Promise<string> {
  return Word.run(async (context) => {
    const body = context.document.body;
    const ooxmlResult = body.getOoxml();
    await context.sync();
    const ooxml = ooxmlResult.value;
    if (!ooxml) {
      throw new Error("getOoxml returned empty content");
    }
    return uint8ArrayToBase64(new TextEncoder().encode(ooxml));
  });
}

async function readDocumentHasContent(): Promise<boolean> {
  try {
    let hasContent = false;
    await Word.run(async (context) => {
      const range = context.document.body.getRange(Word.RangeLocation.whole);
      range.load("text");
      await context.sync();
      hasContent = (range.text ?? "").trim().length > 0;
    });
    return hasContent;
  } catch {
    return false;
  }
}

export interface InsertFileResult {
  replaced: boolean;
}

export async function insertFileIntoWord(base64: string): Promise<InsertFileResult> {
  const hasContent = await readDocumentHasContent();
  if (hasContent) {
    await Word.run(async (context) => {
      context.document.body.clear();
      await context.sync();
    });
  }

  await Word.run(async (context) => {
    context.document.insertFileFromBase64(base64, Word.InsertLocation.replace);
    await context.sync();
  });

  await Word.run(async (context) => {
    const startRange = context.document.body.getRange(Word.RangeLocation.start);
    startRange.select(Word.SelectionMode.start);
    await context.sync();
  });

  return { replaced: hasContent };
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCodePoint(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  return uint8ArrayToBase64(new Uint8Array(buffer));
}

export async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  return arrayBufferToBase64(buffer);
}

export function isEmptyDocContext(ctx: WordDocumentContext | null): boolean {
  return !ctx || (ctx === EMPTY_WORD_DOC_CONTEXT) || ctx.wordCount === 0;
}

// ---------------------------------------------------------------------------
// Comment insertion (AC1-AC4)
// ---------------------------------------------------------------------------

/**
 * Word's Find engine rejects a search string longer than this.
 *
 * An anchor quoted from a long clause routinely exceeds it, and the rejection
 * comes back as a generic InvalidArgument that reads exactly like "the phrase
 * is not in the document" — which is why `commentAnchorCandidates` shortens the
 * anchor rather than letting its length surface as a false "not found".
 */
const WORD_SEARCH_MAX_CHARS = 255;

/** Shortest anchor worth searching for; below this a match means nothing. */
const MIN_ANCHOR_WORDS = 3;

/**
 * The author AC3 asks every AiVA comment to carry.
 *
 * `Word.Comment.authorName` is READ-ONLY in Office.js — Word stamps the comment
 * with whoever is signed in to Word and gives the add-in no way to override it.
 * The attribution therefore rides in the comment body, the only field the API
 * lets us set. Owning the author field itself needs a server-side OOXML path
 * (python-docx `add_comment(author=...)`), not this one.
 */
export const AIVA_COMMENT_AUTHOR = "AiVA";

/** Prefix a comment body with the configured author (AC3), without doubling it. */
export function attributeComment(text: string, author: string = AIVA_COMMENT_AUTHOR): string {
  const body = String(text ?? "").trim();
  const prefix = `${author}:`;
  if (!body) return prefix;
  return body.toLowerCase().startsWith(prefix.toLowerCase()) ? body : `${prefix} ${body}`;
}

/**
 * Escape the characters Word's Find treats as syntax.
 *
 * With `matchWildcards` off — every search in this module — only `^` is
 * special: it introduces codes such as `^p` (paragraph mark) and `^t` (tab), so
 * an anchor containing a literal caret matches the wrong thing or nothing at
 * all. `^^` is Word's own escape for it.
 */
export function escapeWordSearch(phrase: string): string {
  return phrase.replace(/\^/g, "^^");
}

/**
 * Search strings to try for one anchor, best first.
 *
 * The model is asked for a short distinctive phrase but often quotes a whole
 * sentence, which can blow past Word's 255-character limit. Windows are taken
 * from the START of the phrase, never the middle: the comment has to land on
 * the passage the model named, and a middle window would anchor it somewhere
 * the user did not ask for. Each window also gets an apostrophe variant, since
 * Word autocorrects a straight quote to a curly one in the document while the
 * model emits the straight form, and `search()` does not treat them as equal.
 */
export function commentAnchorCandidates(phrase: string): string[] {
  const trimmed = String(phrase ?? "").replace(/\s+/g, " ").trim();
  if (!trimmed) return [];

  const seen = new Set<string>();
  const out: string[] = [];
  const push = (candidate: string): void => {
    if (!candidate || candidate.length > WORD_SEARCH_MAX_CHARS) return;
    for (const variant of [candidate, candidate.replace(/'/g, "’"), candidate.replace(/’/g, "'")]) {
      const key = variant.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(variant);
      }
    }
  };

  const words = trimmed.split(" ");
  // The longest leading window Word will actually accept: the whole phrase when
  // it fits, otherwise shortened a word at a time until it does.
  let longest = words.length;
  while (longest > 1 && words.slice(0, longest).join(" ").length > WORD_SEARCH_MAX_CHARS) longest--;
  const floor = Math.min(MIN_ANCHOR_WORDS, words.length);
  // Tiered rather than one word at a time: a 60-word anchor would otherwise
  // spend every attempt on near-identical windows that all miss for the same
  // reason, before ever reaching a length short enough to match.
  for (const len of [longest, 12, 8, 6, 5, MIN_ANCHOR_WORDS]) {
    if (len >= floor && len <= longest) push(words.slice(0, len).join(" "));
  }
  return out;
}

/**
 * Whether this host exposes the comments API (WordApi 1.4).
 *
 * Probed rather than inferred from an empty result: on an unsupported host the
 * comment collection can come back EMPTY instead of throwing, which turns "this
 * version of Word cannot do comments" into the false statement "the phrase is
 * not in the document". `getWordComments` makes the same probe for the same
 * reason.
 */
function wordCommentsApiSupported(): boolean {
  try {
    return Office.context.requirements?.isSetSupported("WordApi", "1.4") !== false;
  } catch {
    // No requirements API to ask — attempt the call and let it fail honestly.
    return true;
  }
}

export interface CommentInsertResult {
  ok: boolean;
  /**
   * Why it failed. Distinguished rather than collapsed into `false`, because
   * "this Word cannot do comments", "the phrase is not there" and "the phrase
   * is there four times" each need a different thing from the user — the same
   * shape `resolveWordComment` / `deleteWordComment` already return.
   */
  reason?: "not_found" | "unsupported" | "ambiguous" | "failed";
  /** Occurrences the anchor matched. Set when `reason` is "ambiguous". */
  matches?: number;
  /**
   * The string that actually matched — shorter than the requested phrase when
   * it had to be trimmed to fit Word's search limit, so the caller can report
   * what the comment was anchored to rather than what was asked for.
   */
  anchor?: string;
}

export interface CommentRequest {
  phrase: string;
  comment: string;
  /** 1-based occurrence to comment on when the phrase appears more than once. */
  occurrence?: number;
}

export interface BatchCommentResult {
  inserted: number;
  attempted: number;
  /** One entry per request, in the order given — partial failure stays legible. */
  results: Array<CommentInsertResult & { phrase: string }>;
  /** Set when the whole batch failed before any comment could land. */
  reason?: "unsupported" | "failed";
}

type PendingComment = {
  request: CommentRequest;
  candidates: string[];
  settled: boolean;
  target?: Word.Range;
  result: CommentInsertResult & { phrase: string };
};

function tallyComments(
  results: Array<CommentInsertResult & { phrase: string }>,
  attempted: number,
): BatchCommentResult {
  return { inserted: results.filter((r) => r.ok).length, attempted, results };
}

type QueuedSearch = {
  pending: PendingComment;
  tries: Array<{ candidate: string; results: ReturnType<Word.Body["search"]> }>;
};

/**
 * One PendingComment per request, with blank anchors already settled.
 *
 * A blank anchor has nothing to search for, so it is settled here rather than
 * occupying a slot in either search tier.
 */
function buildPendingComments(requests: CommentRequest[]): PendingComment[] {
  return requests.map((request) => {
    const candidates = commentAnchorCandidates(request.phrase);
    return {
      request,
      candidates,
      settled: candidates.length === 0,
      result: { phrase: String(request.phrase ?? ""), ok: false, reason: "not_found" as const },
    };
  });
}

/**
 * Queue this tier's searches for every request still unsettled, loaded ready for
 * the caller's sync.
 *
 * Tier 0 tries each request's best candidate; tier 1 tries the shorter fallback
 * windows, and only for the requests tier 0 could not place.
 */
function queueTierSearches(body: Word.Body, pending: PendingComment[], tier: 0 | 1): QueuedSearch[] {
  const queued = pending
    .filter((p) => !p.settled)
    .map((p) => ({
      pending: p,
      tries: (tier === 0 ? p.candidates.slice(0, 1) : p.candidates.slice(1)).map((candidate) => ({
        candidate,
        results: body.search(escapeWordSearch(candidate), { matchCase: false, matchWholeWord: false }),
      })),
    }))
    .filter((q) => q.tries.length > 0);
  for (const q of queued) for (const t of q.tries) t.results.load("items");
  return queued;
}

/**
 * Decide one request's outcome from the ranges its anchor matched.
 *
 * Never guesses between occurrences. Taking `items[0]` used to comment on the
 * first match of a phrase that may appear forty times in a long agreement, and
 * did it silently — the wrong paragraph ends up carrying a confident-looking
 * note with nothing to say it is misplaced.
 */
function settleFromMatches(p: PendingComment, candidate: string, items: Word.Range[]): void {
  const phrase = p.result.phrase;
  const occurrence = p.request.occurrence;
  p.settled = true;

  if (items.length > 1 && !occurrence) {
    p.result = { phrase, ok: false, reason: "ambiguous", matches: items.length, anchor: candidate };
    return;
  }
  const chosen = items[occurrence ? occurrence - 1 : 0];
  if (!chosen) {
    // An occurrence past the end of the match list — the document moved on since
    // whatever listed the occurrences for the user.
    p.result = { phrase, ok: false, reason: "not_found", matches: items.length, anchor: candidate };
    return;
  }
  p.target = chosen;
  p.result = { phrase, ok: true, anchor: candidate };
}

/** Settle every queued request this tier managed to match. */
function settleTier(queued: QueuedSearch[]): void {
  for (const { pending, tries } of queued) {
    const hit = tries.find((t) => t.results.items.length > 0);
    // No hit leaves it unsettled, so the next tier can try the shorter windows.
    if (hit) settleFromMatches(pending, hit.candidate, hit.results.items);
  }
}

/**
 * Resolve every anchor and insert every comment inside ONE `Word.run`.
 *
 * Cost is a fixed three syncs however many comments were asked for: one for the
 * best candidate of every request, one for the fallback candidates of only the
 * requests that missed, one for the inserts. The old single-insert path spent
 * two round trips per comment, so a 20-finding review cost 40.
 */
async function runCommentBatch(
  context: Word.RequestContext,
  requests: CommentRequest[],
  author: string,
): Promise<BatchCommentResult> {
  const body = context.document.body;
  const pending = buildPendingComments(requests);

  for (const tier of [0, 1] as const) {
    const queued = queueTierSearches(body, pending, tier);
    if (queued.length === 0) continue;
    await context.sync(); // NOSONAR - tier 1 searches only run for what tier 0 left unmatched
    settleTier(queued);
  }

  const toInsert = pending.filter((p) => p.target);
  for (const p of toInsert) p.target!.insertComment(attributeComment(p.request.comment, author));
  if (toInsert.length > 0) await context.sync();

  return tallyComments(
    pending.map((p) => p.result),
    requests.length,
  );
}

/** Retry one request at a time so a single rejected anchor cannot lose the batch. */
async function insertCommentsSequentially(
  requests: CommentRequest[],
  author: string,
): Promise<BatchCommentResult> {
  const results: Array<CommentInsertResult & { phrase: string }> = [];
  for (const request of requests) {
    const phrase = String(request.phrase ?? "");
    try {
      const single = await Word.run((context) => runCommentBatch(context, [request], author)); // NOSONAR - one request per Word.run, in order
      results.push(single.results[0] ?? { phrase, ok: false, reason: "failed" });
    } catch (err) {
      logOfficeError(err, "word-insert-comment");
      results.push({ phrase, ok: false, reason: "failed" });
    }
  }
  return tallyComments(results, requests.length);
}

/**
 * Insert many comments in one pass (AC1), attributed to the configured author
 * (AC3), leaving the document's own text and formatting untouched (AC2).
 */
export async function insertWordComments(
  requests: CommentRequest[],
  author: string = AIVA_COMMENT_AUTHOR,
): Promise<BatchCommentResult> {
  if (requests.length === 0) return { inserted: 0, attempted: 0, results: [] };
  if (!wordCommentsApiSupported()) {
    return {
      inserted: 0,
      attempted: requests.length,
      reason: "unsupported",
      results: requests.map((r) => ({ phrase: String(r.phrase ?? ""), ok: false, reason: "unsupported" as const })),
    };
  }
  try {
    return await Word.run((context) => runCommentBatch(context, requests, author));
  } catch (err) {
    logOfficeError(err, "word-insert-comments");
    // The batch shares one sync, so a single anchor Word rejects takes every
    // other comment down with it. Fall back to one run per request: the other
    // nineteen still land, and the failure is reported against the request that
    // actually caused it rather than against the whole batch.
    return insertCommentsSequentially(requests, author);
  }
}

/** Single-comment convenience wrapper over `insertWordComments`. */
export async function insertWordComment(
  phrase: string,
  comment: string,
  occurrence?: number,
): Promise<CommentInsertResult> {
  const batch = await insertWordComments([{ phrase, comment, occurrence }]);
  return batch.results[0] ?? { ok: false, reason: batch.reason ?? "failed" };
}

/**
 * Locate the anchor paragraph a context window is built around.
 *
 * Two modes, in priority order:
 *   1. `anchorPhrase` — find the paragraph that best matches a TOPIC, so a draft
 *      request can be answered from the right part of a long document without
 *      the user having to put the cursor there first.
 *   2. the selection — the original behaviour, kept for every existing caller.
 *
 * Matching is deliberately literal (lowercased substring, then a token-overlap
 * fallback) rather than semantic. There is no embedding service in this codebase,
 * and a wrong-but-confident semantic hit is worse than an honest miss: the draft
 * would be written against a section nobody chose and nothing would say so.
 * `matchedParagraph` is returned for exactly that reason — the caller can show
 * WHERE it anchored, so a bad match is visible before anything is written.
 */
function findAnchorIndex(paras: readonly string[], selectedText: string, anchorPhrase: string): number {
  const phrase = anchorPhrase.trim().toLowerCase();
  if (phrase) {
    const exact = paras.findIndex((p) => p.toLowerCase().includes(phrase));
    if (exact >= 0) return exact;
    // Nothing contains the whole phrase — score paragraphs by how many of its
    // words they carry. A request phrased "the indemnity obligations" should
    // still find a heading that reads "Indemnities".
    //
    // Three characters minimum, not four: "nda obligations" used to discard
    // "nda" entirely, leaving "obligations" — a word appearing in dozens of
    // paragraphs across an agreement — to anchor on whichever came first. Half
    // the request was thrown away before matching began.
    const words = phrase.split(/\s+/).filter((w) => w.length >= 3);
    if (words.length) {
      // One matching word is NOT a match. A single common word ("obligations",
      // "party", "notice") hits almost everywhere, so scoring on it produced a
      // confident draft written against an unrelated clause — the exact failure
      // `anchored` exists to prevent. Ask for half the words, never fewer than
      // two, unless the user only gave one word to begin with.
      const needed = words.length === 1 ? 1 : Math.max(2, Math.ceil(words.length / 2));
      let bestIdx = -1;
      let bestScore = 0;
      paras.forEach((p, i) => {
        const lower = p.toLowerCase();
        const score = words.filter((w) => lower.includes(w)).length;
        if (score > bestScore) {
          bestScore = score;
          bestIdx = i;
        }
      });
      if (bestScore >= needed) return bestIdx;
    }
    return -1;
  }
  if (!selectedText) return -1;
  return paras.findIndex((p) => p.includes(selectedText.slice(0, 60)));
}

/** Most paragraphs one call will return. A window, not a way to siphon the document:
 *  reading a section is the use case, and no clause runs to fifty paragraphs. */
const MAX_READ_PARAGRAPHS = 50;

export interface ParagraphWindow {
  /** Index of the first paragraph returned — echoes back what was asked for. */
  start: number;
  /** The paragraphs, in document order. */
  paragraphs: string[];
  /** Paragraphs in the document, so the caller knows where the window sits. */
  total: number;
  /** True when the request was longer than MAX_READ_PARAGRAPHS and was clipped. */
  clipped: boolean;
}

/**
 * Read a window of paragraphs by INDEX (AIVA-7018).
 *
 * Indexes come from `TocEntry.startParagraph` / `endParagraph`, so a section is
 * read exactly. Searching for the heading text instead is what fails on a
 * repeating agreement: "834. Compliance" is one of eighty-four Compliance
 * headings and a text match cannot tell them apart.
 *
 * Returns null when the read is refused, never [] — the caller must be able to
 * tell "this section is empty" from "I could not read the document".
 */
export async function readWordParagraphs(start: number, count: number): Promise<ParagraphWindow | null> {
  const from = Math.max(0, Math.floor(start));
  const asked = Math.max(1, Math.floor(count));
  const take = Math.min(asked, MAX_READ_PARAGRAPHS);
  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();
      const total = paragraphs.items.length;
      const window = paragraphs.items.slice(from, from + take);
      for (const p of window) p.load("text");
      await context.sync();
      return {
        start: from,
        paragraphs: window.map((p) => (p.text ?? "").trim()),
        total,
        clipped: asked > take,
      };
    });
  } catch (err) {
    logOfficeError(err, "word-read-paragraphs");
    return null;
  }
}

export async function getWordSelectionContext(
  paragraphsBefore = 1,
  paragraphsAfter = 1,
  anchorPhrase = "",
  anchorIndex?: number,
): Promise<{ selected: string; before: string; after: string; matchedParagraph: string; anchored: boolean }> {
  try {
    return await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.load("text");
      const selectedParas = selection.paragraphs;
      selectedParas.load("items");
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();
      // "text, style, outlineLevel" rather than "text": this tool is the router's
      // fallback for a selection it cannot see in the injected context, and
      // answering it without heading levels reproduces the very flattening
      // AIVA-7080 is about. `outlineLevel` is the half that survives an HTML
      // paste, where no paragraph carries a style at all.
      for (const p of paragraphs.items) p.load("text, style, outlineLevel");
      for (const p of selectedParas.items) p.load("text, style, outlineLevel");
      await context.sync();

      const selectedPlain = (selection.text ?? "").trim();
      const selectedText = _markSelectionStructure(selectedParas.items, selectedPlain) || selectedPlain;
      // The window paragraphs are always WHOLE paragraphs, so their styles can be
      // marked unconditionally — the part-selection hazard _markSelectionStructure
      // guards against cannot arise here.
      const allParas = paragraphs.items.map((p) => _headingMarker(p.style, p.outlineLevel) + (p.text ?? ""));
      // An index from the outline is EXACT, so it wins over phrase matching. That
      // is the whole point of reading the table of contents first: `findAnchorIndex`
      // by design, and a reworded request can miss it — an index cannot. Only a
      // real, in-range index counts; anything else falls through to the phrase so
      // a stale or invented index degrades to the old behaviour instead of
      // silently anchoring at 0.
      const indexed =
        typeof anchorIndex === "number"
        && Number.isInteger(anchorIndex)
        && anchorIndex >= 0
        && anchorIndex < allParas.length;
      const idx = indexed
        ? (anchorIndex as number)
        : findAnchorIndex(allParas, selectedText, anchorPhrase);

      const before = idx > 0
        ? allParas.slice(Math.max(0, idx - paragraphsBefore), idx).join("\n")
        : "";
      const after = idx >= 0
        ? allParas.slice(idx + 1, idx + 1 + paragraphsAfter).join("\n")
        : "";

      return {
        selected: selectedText,
        before,
        after,
        // The paragraph the window is centred on. Empty when nothing matched —
        // which the caller must treat as "no anchor", not as "anchored at 0".
        matchedParagraph: idx >= 0 ? (allParas[idx] ?? "") : "",
        anchored: idx >= 0,
      };
    });
  } catch {
    return { selected: "", before: "", after: "", matchedParagraph: "", anchored: false };
  }
}

/** One match of a find/replace, with enough surrounding text to judge it. */
export interface WordOccurrenceSample {
  /** Text immediately before the match (leading "…" when clipped). */
  before: string;
  /** The matched text, exactly as it appears in the document. */
  match: string;
  /** Text immediately after the match (trailing "…" when clipped). */
  after: string;
}

export interface WordOccurrences {
  count: number;
  /** Context for the first few matches — for display, capped by `maxSamples`. */
  samples: WordOccurrenceSample[];
}

/** How much text to show either side of a match in the approval card. */
const OCCURRENCE_CONTEXT_CHARS = 45;

/**
 * Pulls context around the first `limit` hits of `find`.
 *
 * Pure and separately testable. Whitespace is collapsed so a match spanning a
 * line break still renders as one readable line. `matchCase` mirrors the flag
 * passed to `body.search`, so the samples shown in the approval card are the
 * same matches the swap will make (G-11).
 */
export function extractOccurrenceSamples(
  text: string,
  find: string,
  limit: number,
  matchCase = false,
): WordOccurrenceSample[] {
  const samples: WordOccurrenceSample[] = [];
  if (!text || !find || limit <= 0) return samples;
  const haystack = matchCase ? text : text.toLowerCase();
  const needle = matchCase ? find : find.toLowerCase();
  let from = 0;
  while (samples.length < limit) {
    const at = haystack.indexOf(needle, from);
    if (at === -1) break;
    const end = at + needle.length;
    const ctxStart = Math.max(0, at - OCCURRENCE_CONTEXT_CHARS);
    const ctxEnd = Math.min(text.length, end + OCCURRENCE_CONTEXT_CHARS);
    const tidy = (s: string) => s.replace(/\s+/g, " ");
    samples.push({
      before: (ctxStart > 0 ? "…" : "") + tidy(text.slice(ctxStart, at)),
      match: text.slice(at, end),
      after: tidy(text.slice(end, ctxEnd)) + (ctxEnd < text.length ? "…" : ""),
    });
    from = end;
  }
  return samples;
}

/**
 * Finds every occurrence of `find` WITHOUT changing anything, and returns both
 * the count and context for the first few.
 *
 * Backs the approval card for `word.replace_all`: the user sees how many matches
 * there are AND what each one looks like in situ, before authorising a
 * document-wide swap. Read-only — no `withTrackingSuspended`, no edit sync.
 *
 * The count comes from `body.search`, the same call `replaceAllInDocument` uses,
 * so the number shown is the number that will be replaced. The samples are
 * derived from the body text purely for display; if the two ever disagree the
 * count is authoritative and the samples are merely illustrative.
 *
 * Returns `null` (not a zero count) when the read can't be taken — no Office
 * host, an older client, or a search the host rejects. Null means "unknown", so
 * the card falls back to uncounted wording instead of claiming zero matches.
 */
export async function getWordOccurrences(
  find: string,
  maxSamples = 5,
  matchCase = false,
): Promise<WordOccurrences | null> {
  if (!find.trim()) return { count: 0, samples: [] };
  try {
    return await Word.run(async (context) => {
      const body = context.document.body;
      const results = body.search(find, { matchCase, matchWholeWord: false });
      results.load("items");
      body.load("text");
      await context.sync();
      const count = results.items.length;
      return {
        count,
        samples: extractOccurrenceSamples(body.text ?? "", find, Math.min(count, maxSamples), matchCase),
      };
    });
  } catch (err) {
    logOfficeError(err, "word-count-occurrences");
    return null;
  }
}

export async function replaceAllInDocument(
  find: string,
  replace: string,
  matchCase = false,
): Promise<number> {
  if (!find.trim()) return 0;
  try {
    return await Word.run((context) => withTrackingSuspended(context, async () => {
      const results = context.document.body.search(find, { matchCase, matchWholeWord: false });
      results.load("items");
      await context.sync();
      const count = results.items.length;
      for (const item of results.items) {
        item.insertText(replace, Word.InsertLocation.replace);
      }
      await context.sync();
      return count;
    }));
  } catch {
    return 0;
  }
}

// Tracks phrase+color entries AIVA has highlighted this session so
// `clearWordHighlights` can undo only its own work, leaving any
// user-applied highlights (any colour, anywhere) untouched. Each entry
// carries an id so a single response's "Clear highlights" pill can claim
// (and later clear) only the highlights IT added — without ids, two reviews
// that both touch the same paragraph (e.g. /passive followed by /risk) would
// share one undifferentiated pool, and clearing either pill would wipe both
// (AIVA-7725).
let _nextHighlightId = 1;
const _aivaHighlightedPhrases: Array<{
  id: number;
  turn: number;
  phrase: string;
  color: string;
  cleared: boolean;
}> = [];
// Which response each highlight belongs to. A "Clear highlights" pill owns a
// TURN, not the id list that happened to exist when the pill tool ran: the
// model is told to call pill_word_clear_highlights last, and when it does not,
// a frozen id list covers only the phrases highlighted before that call and the
// button silently leaves the rest of its own response coloured (AIVA-7725).
let _highlightTurnSeq = 0;
// Highest id already handed out to an emitted "Clear highlights" pill —
// entries with a higher id than this are unclaimed, i.e. belong to a
// response whose pill has not been built yet.
let _lastClaimedHighlightId = 0;

let _turnStartHighlightId = 0;


export function beginHighlightTurn(): void {
  _turnStartHighlightId = _nextHighlightId - 1;
  _highlightTurnSeq += 1;
}

/** The turn a "Clear highlights" pill emitted now belongs to. Resolved to the
 *  turn's highlights when the pill is CLICKED, so phrases highlighted after the
 *  pill tool ran are still the pill's to clear. */
export function currentHighlightTurn(): number {
  return _highlightTurnSeq;
}

/**
 * Hands the calling "Clear highlights" pill the ids of the highlights added
 * by the current turn, and marks them claimed. Returns [] when this turn
 * highlighted nothing new, which the pill emitter uses to suppress a
 * pointless button (e.g. the LLM calling the clear pill tool in a turn that
 * never highlighted anything).
 */
export function claimSessionHighlights(): number[] {
  const floor = Math.max(_lastClaimedHighlightId, _turnStartHighlightId);
  const ids = _aivaHighlightedPhrases
    .filter((h) => !h.cleared && h.id > floor)
    .map((h) => h.id);
  if (ids.length > 0) _lastClaimedHighlightId = Math.max(_lastClaimedHighlightId, ...ids);
  return ids;
}

/**
 * The phrases this turn highlighted, in the order they were highlighted.
 *
 * A READ, unlike `claimSessionHighlights` — nothing is marked claimed, because
 * the caller is the approval card deciding how to DISPLAY a review, not the
 * Clear highlights pill taking ownership of them. Claiming here would steal the
 * ids that pill needs and leave the user's highlights unclearable.
 *
 * Filtered the same way `realignTurnHighlights` filters (this turn, not yet
 * cleared) rather than by claim state: by the time the card renders, the clear
 * pill for the same turn may already have claimed them.
 */
export function getTurnHighlightPhrases(): string[] {
  return _aivaHighlightedPhrases
    .filter((h) => !h.cleared && h.id > _turnStartHighlightId)
    .map((h) => h.phrase);
}

export async function realignTurnHighlights(phrases: string[]): Promise<number> {
  const mine = _aivaHighlightedPhrases.filter((h) => !h.cleared && h.id > _turnStartHighlightId);
  const first = mine[0];
  if (!first) return 0;

  const wanted = phrases.map((p) => String(p ?? "").trim()).filter((p) => p.length > 0);
  if (wanted.length === 0) return 0;

  let highlighted = 0;
  for (const phrase of wanted) {
    if ((await highlightWordText(phrase, first.color)) > 0) highlighted++; // NOSONAR - highlights are applied one Word.run at a time, in order
  }
  // Only the markers this call did NOT just re-apply.
  //
  // Clearing works by re-searching the phrase, so handing it a phrase that is
  // also in `wanted` blanks the colour this call has just painted onto those
  // same characters — and `partitionForClear` then retires the new entry as a
  // duplicate of the old one. The review skills usually pass the review's
  // `original` strings, which ARE the strings they highlighted, so every marker
  // matched: the turn ended with no colour in the document and nothing for its
  // "Clear highlights" pill to claim, so no button was rendered at all. A turn
  // whose phrases all survive the realignment has nothing stale to clear.
  const reapplied = new Set(wanted.map((p) => p.toLowerCase()));
  const stale = mine.filter((h) => !reapplied.has(h.phrase.toLowerCase()));
  if (highlighted > 0 && stale.length > 0) await clearWordHighlights(stale.map((h) => h.id));
  return highlighted;
}

const HIGHLIGHT_SEARCH_OPTIONS = { matchCase: false, matchWholeWord: false };

async function _searchWithinSelection(
  context: Word.RequestContext,
  selection: Word.Range,
  candidate: string,
): Promise<Word.Range[]> {
  const bodyResults = context.document.body.search(candidate, HIGHLIGHT_SEARCH_OPTIONS);
  bodyResults.load("items");
  await context.sync();
  const relations = bodyResults.items.map((item) => item.compareLocationWith(selection));
  await context.sync();
  return bodyResults.items.filter((_, i) => {
    const relation = relations[i]?.value;
    return relation === Word.LocationRelation.inside || relation === Word.LocationRelation.equal;
  });
}

async function _highlightTargets(
  context: Word.RequestContext,
  selection: Word.Range,
  selectionText: string,
  candidate: string,
): Promise<Word.Range[]> {
  const hasSelection = selectionText.length > 0;
  if (hasSelection && !selectionText.toLowerCase().includes(candidate.toLowerCase())) return [];

  const scope: Word.Range | Word.Body = hasSelection ? selection : context.document.body;
  const results = scope.search(candidate, HIGHLIGHT_SEARCH_OPTIONS);
  results.load("items");
  await context.sync();
  if (results.items.length === 0 && hasSelection) {
    return _searchWithinSelection(context, selection, candidate);
  }
  return results.items;
}

/** Forms of *be* and *get* that carry an English passive construction. */
const PASSIVE_AUX = /^(?:am|is|are|was|were|be|been|being|get|gets|got|gotten)$/i;

/** Upper bound on distinct fallback windows per phrase. Each one costs a
 *  `search()` round trip (and up to three, with the apostrophe variants), so a
 *  40-word sentence quoted by the model must not turn into hundreds of syncs. */
const MAX_HIGHLIGHT_WINDOWS = 20;

/** Window sizes to try from an auxiliary, in the order a passive core is most
 *  likely to end: auxiliary + optional adverb + participle, plus a trailing
 *  "by <agent>". Narrow sizes come first because narrowing to the flagged verb
 *  is the whole point of this family — 12 and 8 trail as last resorts. */
const CORE_WINDOW_LENGTHS = [4, 5, 3, 6, 2, 8, 12];

/** Window sizes for the generic leading-window family, widest first. */
const LEADING_WINDOW_LENGTHS = [12, 8, 6, 5, 4, 3, 2];

/** A word with its leading/trailing punctuation removed, for the auxiliary
 *  test — "was," and "(was" are still the auxiliary. */
function bareWord(word: string): string {
  // Trim leading/trailing non-letters with index scans - no backtracking
  // regex, so no ReDoS on adversarial punctuation runs.
  const first = word.search(/[A-Za-z]/);
  if (first === -1) return "";
  let last = word.length - 1;
  while (last > first && !/[A-Za-z]/.test(word.charAt(last))) last -= 1;
  return word.slice(first, last + 1);
}

/**
 * The word windows to try for `phrase`, in the order they should be searched.
 *
 * The full phrase comes first and is the only `exact` candidate: a multi-hit
 * exact phrase is still highlighted, while a multi-hit shorter window is
 * skipped as too ambiguous to place.
 *
 * The fallbacks then run in two families. First, windows ANCHORED ON A PASSIVE
 * AUXILIARY, narrowest first — the auxiliary plus the participle after it is
 * what the reader needs to see coloured. Only then the old leading windows,
 * widest first, which still cover the findings that carry no auxiliary at all
 * (weak language, a reduced passive, /clarity's own findings).
 *
 * Leading windows ALONE were the /passive defect. The skill quotes a whole
 * sentence as matchText whenever the active rewrite moves the subject, and when
 * that exact sentence missed — one curly apostrophe, a paragraph mark, a double
 * space — the fallback highlighted the sentence's OPENING words, i.e. the
 * subject noun phrase rather than the passive verb. That is both the "three
 * words or more highlighted" symptom and half the run-to-run inconsistency: for
 * one and the same finding, whether the highlight landed on the right words
 * depended on the model's quoting fidelity, which varies between runs.
 */
function highlightWindows(words: string[]): Array<{ text: string; exact: boolean }> {
  const out: Array<{ text: string; exact: boolean }> = [{ text: words.join(" "), exact: true }];
  const seen = new Set<string>([words.join(" ").toLowerCase()]);

  const push = (start: number, len: number): boolean => {
    if (len >= words.length || len < 2) return true;
    // Clamp so a window anchored near the end still has its full length rather
    // than being silently truncated.
    const from = Math.min(Math.max(start, 0), words.length - len);
    const text = words.slice(from, from + len).join(" ");
    const key = text.toLowerCase();
    if (seen.has(key)) return true;
    seen.add(key);
    out.push({ text, exact: false });
    return out.length < MAX_HIGHLIGHT_WINDOWS;
  };

  // Anchored family: one start per auxiliary, in document order, so the window
  // BEGINS on the auxiliary instead of merely containing it somewhere.
  const auxStarts = words.map((w, i) => (PASSIVE_AUX.test(bareWord(w)) ? i : -1)).filter((i) => i >= 0);
  for (const start of auxStarts) {
    for (const len of CORE_WINDOW_LENGTHS) {
      if (!push(start, len)) return out;
    }
  }
  // Generic family: the leading windows, unchanged.
  for (const len of LEADING_WINDOW_LENGTHS) {
    if (!push(0, len)) return out;
  }
  return out;
}

export async function highlightWordText(phrase: string, color = "Yellow"): Promise<number> {
  const trimmed = phrase.trim();
  if (!trimmed) return 0;

  const words = trimmed.split(/\s+/);

  // Each candidate also gets a curly-apostrophe variant: Word autocorrects
  // ' to ’ in the document while the LLM emits the straight form, and
  // search() does not treat them as equal.
  const seen = new Set<string>();
  const candidates: Array<{ text: string; exact: boolean }> = [];
  const pushCandidate = (candidate: string, exact: boolean) => {
    for (const variant of [candidate, candidate.replace(/'/g, "’"), candidate.replace(/’/g, "'")]) {
      const key = variant.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        candidates.push({ text: variant, exact });
      }
    }
  };
  for (const w of highlightWindows(words)) pushCandidate(w.text, w.exact);

  try {
    return await Word.run(async (context) => {
      // Scope the search to the user's selection when one exists — a review of
      // selected text must not highlight matching phrases elsewhere in the
      // document. Falls back to the whole body when nothing is selected.
      const selection = context.document.getSelection();
      selection.load("text");
      await context.sync();
      const selectionText = (selection.text ?? "").replace(/\s+/g, " ").trim();

      for (const { text: candidate, exact } of candidates) {
        const targets = await _highlightTargets(context, selection, selectionText, candidate);
        if (targets.length === 0 || (targets.length > 1 && !exact)) continue;
        for (const item of targets) {
          item.font.highlightColor = color;
        }
        await context.sync();
        _aivaHighlightedPhrases.push({
          id: _nextHighlightId++,
          turn: _highlightTurnSeq,
          phrase: candidate,
          color,
          cleared: false,
        });
        return targets.length;
      }
      return 0;
    });
  } catch {
    return 0;
  }
}

async function _insertTableWithStyle(
  context: Word.RequestContext,
  table: Word.Table,
): Promise<void> {
  try {
    table.style = "Table Grid";
  } catch { /* style not available in this host */ }
  try {
    table.load("rows");
    await context.sync();
    const headerRow = table.rows.items[0];
    if (headerRow) {
      headerRow.load("cells");
      await context.sync();
      for (const cell of headerRow.cells.items) {
        cell.body.paragraphs.getFirst().font.bold = true;
      }
      await context.sync();
    }
  } catch { /* header bolding not available in this context — table is still inserted */ }
}

export async function insertWordTable(
  headers: string[],
  rows: string[][],
  position: "at_cursor" | "append_to_end" | "replace_selection" = "at_cursor",
): Promise<{ inserted: boolean; rows: number; cols: number }> {
  const colCount = headers.length;
  if (colCount === 0) return { inserted: false, rows: 0, cols: 0 };

  const normalizedRows = rows.map((r) => {
    const padded = [...r];
    while (padded.length < colCount) padded.push("");
    return padded.slice(0, colCount);
  });
  const tableData: string[][] = [headers, ...normalizedRows];
  const rowCount = tableData.length;

  try {
    await Word.run(async (context) => {
      let table: Word.Table;
      if (position === "append_to_end") {
        table = context.document.body.insertTable(rowCount, colCount, Word.InsertLocation.end, tableData);
        await _insertTableWithStyle(context, table);
        await scrollToAppended(context, table);
      } else if (position === "replace_selection") {
        const sel = context.document.getSelection();
        const selTables = sel.tables;
        selTables.load("items");
        await context.sync();
        if (selTables.items.length > 0) {
          const afterSelection = sel.getRange(Word.RangeLocation.after);
          table = afterSelection.insertTable(rowCount, colCount, Word.InsertLocation.after, tableData);
          await _insertTableWithStyle(context, table);
          for (const oldTable of selTables.items) {
            oldTable.delete();
          }
          sel.insertText("", Word.InsertLocation.replace);
        } else {
          sel.insertText("", Word.InsertLocation.replace);
          await context.sync();
          const cursor = context.document.getSelection().getRange(Word.RangeLocation.end);
          table = cursor.insertTable(rowCount, colCount, Word.InsertLocation.after, tableData);
          await _insertTableWithStyle(context, table);
        }
        await context.sync();
      } else {
        table = context.document.getSelection().insertTable(rowCount, colCount, Word.InsertLocation.after, tableData);
        await _insertTableWithStyle(context, table);
      }
    });
  } catch {
    // Fallback: append to end — body insertion is the most reliable path
    await Word.run(async (context) => {
      const table = context.document.body.insertTable(rowCount, colCount, Word.InsertLocation.end, tableData);
      await _insertTableWithStyle(context, table);
    });
  }

  return { inserted: true, rows: rowCount, cols: colCount };
}

/** Word's highlight palette by name. `font.highlightColor` is written by name
 *  but READ BACK as a hex string on most hosts, so the two forms have to meet
 *  somewhere before a clear can ask "is this still my colour?". */
const HIGHLIGHT_HEX_BY_NAME: Readonly<Record<string, string>> = {
  yellow: "ffff00", brightgreen: "00ff00", green: "008000", turquoise: "00ffff",
  cyan: "00ffff", pink: "ff00ff", magenta: "ff00ff", blue: "0000ff", red: "ff0000",
  darkblue: "000080", teal: "008080", violet: "800080", purple: "800080",
  darkred: "800000", darkyellow: "808000", gray25: "c0c0c0", gray50: "808080",
  black: "000000", white: "ffffff",
};

/** A highlight value reduced to bare lowercase hex, or undefined when it is not
 *  a highlight at all (`null`, `"No color"`) or a name outside the palette. */
function canonicalHighlight(value: unknown): string | undefined {
  if (!isRealHighlight(value)) return undefined;
  const raw = value.trim().toLowerCase();
  const hex = raw.startsWith("#") ? raw.slice(1) : raw;
  if (/^[0-9a-f]{6}$/.test(hex)) return hex;
  return HIGHLIGHT_HEX_BY_NAME[raw.replace(/\s+/g, "")];
}

/**
 * Whether a clear for `applied` may blank the run currently showing `current`.
 *
 * This is the invariant the registry always claimed and never enforced: a
 * clear removes the colour IT painted and nothing else. Blanking by phrase
 * search cannot honour that on its own, because `font.highlightColor` is one
 * value per character run — when /risks colours a clause /passive already
 * flagged, the two findings share characters, and blanking /passive's phrase
 * takes /risks' red with it. The re-apply of the other still-tracked entries
 * repaired that only while BOTH entries stayed tracked; the moment /risks'
 * entry is gone — an id-less clear marked it cleared, the pane remounted, or
 * the colour was the user's own all along — nothing repainted it and one
 * click stripped both colours (AIVA-7725).
 *
 * Undecidable cases (a colour outside the palette, a host that will not report
 * one) deliberately answer TRUE, so this can only ever remove FEWER wrong
 * highlights than before — never fewer right ones.
 */
function clearOwnsRun(current: unknown, applied: string): boolean {
  const mine = canonicalHighlight(applied);
  const there = canonicalHighlight(current);
  if (mine === undefined || there === undefined) return true;
  return mine === there;
}

/** A range whose highlight colour a clear can read and write. */
interface WordFontRange {
  font: { highlightColor: string | null };
}

/** A range that can be broken into smaller ranges — `getTextRanges` is WordApi
 *  1.3, so a host without it simply keeps the whole-range behaviour. */
interface WordSplittableRange extends WordFontRange {
  getTextRanges(endingMarks: string[], trimSpacing: boolean): {
    items: WordFontRange[];
    load(option: string): void;
  };
}

/** Identity of a highlight as the DOCUMENT sees it: the text it covers and the
 *  colour on it. Two tracked entries sharing this are the same mark. */
function highlightKey(h: { phrase: string; color: string }): string {
  return `${canonicalHighlight(h.color) ?? h.color.toLowerCase()}|${h.phrase.toLowerCase()}`;
}

function canSplitRange(range: unknown): range is WordSplittableRange {
  return typeof (range as WordSplittableRange | null)?.getTextRanges === "function";
}

/**
 * The entries an id-less `clearWordHighlights` owns.
 *
 * A review turn that highlights and then clears means "drop the markings I
 * just made", so the scope is that turn's own batch — the same
 * `_turnStartHighlightId` boundary `realignTurnHighlights` uses, and the same
 * one the pill claims. A turn that highlighted NOTHING and still asks to
 * clear is the user requesting it outright ("clear the highlights"), which
 * legitimately means every batch AIVA still tracks; there is no turn batch to
 * scope to in that case, so the whole registry stays the answer.
 */
function _idlessClearScope(): (h: { id: number }) => boolean {
  const turnIds = new Set(
    _aivaHighlightedPhrases.filter((h) => !h.cleared && h.id > _turnStartHighlightId).map((h) => h.id),
  );
  if (turnIds.size === 0) return () => true;
  return (h) => turnIds.has(h.id);
}

/*
 * Clearing highlights, and why it is not just "blank what I coloured".
 *
 * `font.highlightColor` is one value per character run, not a stack. Two
 * review commands over one paragraph — /passive and /risks, say — flag
 * overlapping text, so the later highlight physically overwrites the earlier
 * one the moment it lands, well before any pill is clicked. A clear therefore
 * has to answer two questions per hit: is this run still MINE (`clearOwnsRun`,
 * plus `splitMixedHits` when the run is only partly mine), and is any other
 * tracked entry describing the same mark (`partitionForClear`). Getting either
 * wrong is what made one pill strip both responses' colours, or strip nothing
 * at all (AIVA-7725).
 */
type ClearHit = { range: WordFontRange; color: string };
type ClearMatch = (h: { id: number; turn: number }) => boolean;

/** Which tracked entries this clear owns: its turn, its ids, or — for the
 *  id-less tool, which can name neither — the current turn. */
function resolveClearScope(ids?: number[], turn?: number): ClearMatch {
  const byTurn = typeof turn === "number" ? turn : undefined;
  const byId = ids ? new Set(ids) : undefined;
  if (byTurn === undefined && byId === undefined) return _idlessClearScope();
  return (h) => h.turn === byTurn || (byId?.has(h.id) ?? false);
}

/**
 * Split the registry for one clear.
 *
 * `duplicates` are entries another response tracked for the SAME phrase and
 * colour. They describe one physical mark, so they retire together — repainting
 * one would undo this clear in the same sync and the button would look dead.
 * `remaining` is everything genuinely different, which still needs repainting
 * where the blanking took its characters.
 */
function partitionForClear(matches: ClearMatch) {
  const toClear = _aivaHighlightedPhrases.filter(matches);
  const clearedKeys = new Set(toClear.map(highlightKey));
  const others = _aivaHighlightedPhrases.filter((h) => !h.cleared && !matches(h));
  return {
    toClear,
    duplicates: others.filter((h) => clearedKeys.has(highlightKey(h))),
    remaining: others.filter((h) => !clearedKeys.has(highlightKey(h))),
  };
}

/** What this clear may do with one search hit. "split" means the hit is partly
 *  someone else's colour — Word reports such a mixed range as null — so it has
 *  to be judged a word at a time instead of as a whole. */
function classifyBlankHit(item: WordFontRange, color: string): "blank" | "skip" | "split" {
  if (canonicalHighlight(item.font.highlightColor) === undefined) {
    return canSplitRange(item) ? "split" : "blank";
  }
  return clearOwnsRun(item.font.highlightColor, color) ? "blank" : "skip";
}

function sortBlankHits(blanks: Array<{ results: { items: WordFontRange[] }; color: string }>) {
  const targets: ClearHit[] = [];
  const mixed: Array<{ range: WordSplittableRange; color: string }> = [];
  for (const { results, color } of blanks) {
    for (const item of results.items) {
      const verdict = classifyBlankHit(item, color);
      if (verdict === "blank") targets.push({ range: item, color });
      else if (verdict === "split") mixed.push({ range: item as WordSplittableRange, color });
    }
  }
  return { targets, mixed };
}

/** Break each mixed hit into word ranges and keep only the ones still wearing
 *  this clear's colour. Costs one extra READ sync; the writes stay in the
 *  caller's single sync, so one Ctrl+Z still undoes the whole clear. */
async function splitMixedHits(
  context: Word.RequestContext,
  mixed: Array<{ range: WordSplittableRange; color: string }>,
): Promise<ClearHit[]> {
  const parts = mixed.map(({ range, color }) => ({ pieces: range.getTextRanges([" "], false), color }));
  for (const { pieces } of parts) pieces.load("items/font/highlightColor");
  await context.sync();
  const targets: ClearHit[] = [];
  for (const { pieces, color } of parts) {
    for (const piece of pieces.items) {
      if (clearOwnsRun(piece.font.highlightColor, color)) targets.push({ range: piece, color });
    }
  }
  return targets;
}

const CLEAR_SEARCH_OPTIONS = { matchCase: false, matchWholeWord: false };

function searchForClear(
  context: Word.RequestContext,
  entries: Array<{ phrase: string; color: string }>,
  load: string,
) {
  const found = entries.map(({ phrase, color }) => ({
    results: context.document.body.search(phrase, CLEAR_SEARCH_OPTIONS),
    color,
  }));
  for (const { results } of found) results.load(load);
  return found as Array<{ results: { items: WordFontRange[] }; color: string }>;
}

/**
 * Clears AIVA's own highlights, leaving every other response's — and the
 * user's own — exactly as they were. See `clearOwnsRun` for the invariant and
 * `partitionForClear` for how one response's marks are told from another's.
 */
export async function clearWordHighlights(ids?: number[], turn?: number): Promise<boolean> {
  const matches = resolveClearScope(ids, turn);
  const { toClear, duplicates, remaining } = partitionForClear(matches);
  if (toClear.length === 0) return false;

  let applied = false;
  try {
    await Word.run(async (context) => {
      // The blanks need each hit's CURRENT colour so `clearOwnsRun` can leave a
      // run another response has since recoloured alone.
      const blanks = searchForClear(context, toClear, "items/font/highlightColor");
      const keeps = searchForClear(context, remaining, "items");
      await context.sync();

      const { targets, mixed } = sortBlankHits(blanks);
      if (mixed.length > 0) targets.push(...(await splitMixedHits(context, mixed)));

      for (const { range } of targets) {
        (range.font as { highlightColor: string | null }).highlightColor = null;
      }
      for (const { results, color } of keeps) {
        for (const item of results.items) {
          item.font.highlightColor = color;
        }
      }
      await context.sync();
    });
    applied = true;
  } catch (err) {
    logOfficeError(err, "word-clear-highlights");
  }
  if (applied) {
    for (const entry of [...toClear, ...duplicates]) entry.cleared = true;
  }
  return true;
}

export interface WordTrackingResult {
  /** False when the host rejected the change (e.g. API unavailable). */
  ok: boolean;
  /** The tracking state after the call. */
  enabled: boolean;
  /** True when the call actually flipped the mode (false = already there). */
  changed: boolean;
  /** Why `ok` is false — "unsupported" only when the pre-flight probe caught it. */
  reason?: "unsupported" | "failed";
}

/**
 * Turns Word's native Track Changes on (TrackAll) or off for the current
 * document. The mode is a property of the DOCUMENT, so once enabled it keeps
 * marking every edit — by any author, across sessions — until turned off.
 * Driven by the /start-tracking and /stop-tracking slash commands via
 * word_set_tracking.
 */
export async function setWordChangeTracking(enabled: boolean): Promise<WordTrackingResult> {
  // changeTrackingMode is WordApi 1.4. Probing first means an unsupported host
  // gets told so explicitly, rather than the caller reading "ok: false" and
  // assuming the document is protected or read-only.
  try {
    if (Office.context.requirements?.isSetSupported("WordApi", "1.4") === false) {
      return { ok: false, enabled: false, changed: false, reason: "unsupported" };
    }
  } catch (err) {
    // No requirements API on this host — log it, then fall through and try
    // the write anyway rather than treating an unrelated probe failure as
    // "tracking unsupported".
    logOfficeError(err, "word-set-tracking-probe");
  }
  try {
    return await Word.run(async (context) => {
      context.document.load("changeTrackingMode");
      await context.sync();
      const current = context.document.changeTrackingMode;
      const isOn =
        current === Word.ChangeTrackingMode.trackAll ||
        current === Word.ChangeTrackingMode.trackMineOnly;
      let changed = false;
      if (isOn !== enabled) {
        context.document.changeTrackingMode = enabled
          ? Word.ChangeTrackingMode.trackAll
          : Word.ChangeTrackingMode.off;
        await context.sync();
        changed = true;
      }
      if (enabled) {
        // Match Word's classic review look (All Markup + Show Revisions in
        // Balloons): insertions inline underlined, deletions in margin
        // balloons with author + "Deleted:" text, change bars on the left.
        // Desktop-only API (WordApiDesktop 1.4) — web/older hosts keep
        // whatever view the user has.
        try {
          const win = (
            context.document as unknown as {
              activeWindow?: {
                view?: { markupMode?: string; revisionsFilter?: { markup: string } };
              };
            }
          ).activeWindow;
          const view = win?.view;
          if (view?.revisionsFilter) {
            view.markupMode = "Balloon";
            view.revisionsFilter.markup = "All";
            await context.sync();
          }
        } catch { /* markup view not settable on this host */ }
      }
      return { ok: true, enabled, changed };
    });
  } catch {
    return { ok: false, enabled: false, changed: false, reason: "failed" };
  }
}


export async function getWordTrackingEnabled(): Promise<boolean | null> {
  try {
    return await Word.run(async (context) => {
      context.document.load("changeTrackingMode");
      await context.sync();
      const current = context.document.changeTrackingMode;
      return (
        current === Word.ChangeTrackingMode.trackAll ||
        current === Word.ChangeTrackingMode.trackMineOnly
      );
    });
  } catch (err) {
    logOfficeError(err, "word-tracking-enabled");
    return null;
  }
}

export interface WordTrackedChange {
  /** Revision kind as reported by Word (e.g. "Added", "Deleted", "Formatted"). */
  type: string;
  author: string;
  date: string;
  text: string;
}

export interface WordDocComment {
  author: string;
  date: string;
  text: string;
  resolved: boolean;
}

export interface WordReviewItems {
  /** Whether Word's Track Changes is currently on for this document. */
  trackingOn: boolean;
  /** False when this Word build doesn't expose the tracked-changes API (WordApi < 1.6). */
  trackedChangesSupported: boolean;
  trackedChanges: WordTrackedChange[];
  comments: WordDocComment[];
}

/**
 * One tracked revision's text as a single quotable line — never the empty
 * string when Word actually removed something (AIVA-8042).
 *
 * `TrackedChange.text` is the raw added/removed run, and `.trim()` alone turned
 * every whitespace-only deletion into `Deleted: ""` — a bullet that names a
 * change and then shows nothing, which reads to the user as AIVA failing to
 * read the document. Verified against real Word over COM: deleting a paragraph
 * mark yields a revision whose text is exactly "\r", and deleting a single
 * space yields " ". Both are genuine deletions, so they are named, not blanked.
 *
 * Ordinary text is whitespace-COLLAPSED rather than merely trimmed, so a
 * deletion spanning paragraphs still renders as one bullet line.
 *
 * "" is returned only when Word gave us nothing at all — there is no removed
 * content to describe, and inventing a description would be worse than a blank.
 */
export function describeRevisionText(raw: unknown): string {
  const text = typeof raw === "string" ? raw : "";
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (collapsed) return collapsed;
  if (!text) return "";
  // Whitespace-only: say WHAT came out. Word uses a lone \r for a paragraph
  // mark; \r\n is matched first so a CRLF host doesn't double the tally.
  const breaks = (text.match(/\r\n|\r|\n/g) ?? []).length;
  if (breaks > 0) return breaks === 1 ? "a paragraph break" : `${breaks} paragraph breaks`;
  const tabs = (text.match(/\t/g) ?? []).length;
  if (tabs > 0) return tabs === 1 ? "a tab" : `${tabs} tabs`;
  return text.length === 1 ? "a space" : `${text.length} spaces`;
}

/**
 * Choose whichever of Word's answers actually describes the removed content
 * (AIVA-8042).
 *
 * The rule is about SUBSTANCE, not about which property it came from — the
 * first candidate carrying real words wins, and only if none does do we fall
 * back to naming whitespace. Candidates are passed most-authoritative first.
 *
 * Ordering them by property was the mistake that made this bug survive two
 * fixes. Preferring `text` reported a deletion of 'Confidential Information"'
 * as "a paragraph break", because Office.js had put whitespace on the change
 * while the words sat elsewhere.
 */
export function pickRevisionText(...candidates: unknown[]): string {
  const strings = candidates.map((c) => (typeof c === "string" ? c : ""));
  const withWords = strings.find((s) => s.trim());
  if (withWords) return withWords;
  // Nothing but whitespace anywhere — describeRevisionText names the first we
  // actually have, so a deleted paragraph mark still reads as one.
  return strings.find(Boolean) ?? "";
}

/**
 * The text a deletion removed, pulled out of that range's OOXML (AIVA-8042).
 *
 * This is the only route that works, and the reason is structural rather than a
 * quirk to work around. Word does not store deleted content as document text —
 * it stores it in `w:delText` inside a `w:del`, precisely so the text is NOT
 * part of the body any more. Every text-property read therefore returns empty
 * or whitespace, correctly: as far as the body is concerned the words are gone.
 * Confirmed on the reported document, whose XML holds exactly the three phrases
 * the report was blanking — 'Confidential Information"', 'formation' and
 * 'please review' — while both `TrackedChange.text` and the range's `text` came
 * back empty for all three.
 *
 * Reads only `w:delText`, so an insertion's OOXML yields "" and callers can
 * treat it like any other empty candidate. `w:del` itself is deliberately not
 * matched: a deleted paragraph mark is a self-closing `w:del` inside `w:rPr`
 * with no `w:delText`, and trying to pair those up positionally is how you
 * attribute one revision's words to another.
 */
export function extractDeletedTextFromOoxml(xml: unknown): string {
  if (typeof xml !== "string" || !xml) return "";
  const runs = xml.match(/<w:delText[^>]*>([\s\S]*?)<\/w:delText>/g) ?? [];
  if (runs.length === 0) return "";
  return runs
    .map((run) => run.replace(/^<w:delText[^>]*>/, "").replace(/<\/w:delText>$/, ""))
    .join("")
    // XML entities, innermost-last so a literal "&amp;lt;" survives as "&lt;".
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

/**
 * Reads the document's native review state in one pass: tracking mode, tracked
 * revisions (insertions/deletions with author + date), and margin comments.
 * Read-only — powers the /changes report. Each section is best-effort so an
 * older host missing one API still returns the rest.
 *
 * `sinceMs` scopes the result to items dated at/after that time (the pane
 * session start) — without it the DOCUMENT's full revision history would be
 * reported, including leftovers from earlier sessions that were never
 * accepted/rejected. Undated items are kept (can't tell how old they are).
 *
 * The cutoff is floored to its whole minute (AIVA-8040). Word stamps a revision
 * with the minute it was made and DISCARDS the seconds — verified against real
 * Word over COM: a deletion made at 15:01:28 is stored as 15:01:00. Compared
 * against a millisecond-precise `Date.now()` session start, every revision made
 * in the pane's first minute reads as up to 59s OLDER than the session and was
 * dropped as "a leftover from an earlier session". That is the reported bug:
 * turn tracking on, delete a line, /stop-tracking, /start-tracking — all inside
 * a minute — and the deletion was missing from the report.
 */
/**
 * A revision's timestamp to the MINUTE — date plus hh:mm, no seconds.
 *
 * `toLocaleString()` renders seconds, and Word stores a revision stamped to the
 * minute with the seconds discarded (the same truncation `getWordReviewItems`
 * floors its cutoff against, verified against real Word over COM). So every
 * revision rendered ":00" — a constant that looks like precision the timestamp
 * does not have.
 *
 * Falls back to `toLocaleString()` if the locale options throw, so a host with
 * a restricted Intl build keeps today's output rather than an empty cell.
 */
function formatRevisionTimestamp(raw: string | Date): string {
  const when = new Date(raw);
  if (Number.isNaN(when.getTime())) return "";
  try {
    return when.toLocaleString(undefined, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return when.toLocaleString();
  }
}

export async function getWordReviewItems(sinceMs?: number): Promise<WordReviewItems> {
  // Floor to the minute so Word's own truncation cannot push an in-session
  // revision behind the cutoff. Widens the window by at most 59s, which can
  // only ever admit a revision made in the same minute the pane opened.
  const cutoffMs = sinceMs ? Math.floor(sinceMs / 60_000) * 60_000 : sinceMs;
  const isRecent = (raw: unknown): boolean => {
    if (!cutoffMs || !raw) return true;
    const when = new Date(raw as string | Date).getTime();
    return Number.isNaN(when) || when >= cutoffMs;
  };
  const result: WordReviewItems = {
    trackingOn: false,
    trackedChangesSupported: true,
    trackedChanges: [],
    comments: [],
  };
  try {
    await Word.run(async (context) => {
      const doc = context.document;
      try {
        doc.load("changeTrackingMode");
        await context.sync();
        result.trackingOn =
          doc.changeTrackingMode === Word.ChangeTrackingMode.trackAll ||
          doc.changeTrackingMode === Word.ChangeTrackingMode.trackMineOnly;
      } catch { /* tracking mode unavailable on this host */ }

      // Tracked revisions — WordApi 1.6.
      try {
        const tracked = doc.body.getTrackedChanges();
        tracked.load("items");
        await context.sync();
        for (const tc of tracked.items) tc.load("type, author, date, text");
        await context.sync();

        // A DELETION's removed words are in NEITHER text property. Word stores
        // them in `w:delText` inside a `w:del` — deliberately outside the body
        // text, because as far as the document is concerned they are gone. So
        // `TrackedChange.text` and the range's `text` both come back empty or
        // whitespace, and reporting either is AIVA-8042: the bullet named a
        // deletion and then showed nothing. Confirmed against the reported
        // document, whose XML held the three phrases the report was blanking.
        //
        // Hence three candidates per revision, and pickRevisionText takes the
        // first with real words in it: the change's own text, its range's text,
        // then the range's OOXML, which is the one that actually answers for a
        // deletion. The first two are kept because an INSERTION answers on them
        // and needs no XML parsing.
        //
        // Each lookup gets its own sync and its own try: getRange and getOoxml
        // are surfaces a host can stub out, and a failure must cost the deleted
        // words, never the whole revision list.
        let ranges: (Word.Range | null)[] = tracked.items.map(() => null);
        try {
          ranges = tracked.items.map((tc) => {
            const range = tc.getRange();
            range.load("text");
            return range;
          });
          await context.sync();
        } catch {
          ranges = tracked.items.map(() => null);
        }

        let ooxml: (OfficeExtension.ClientResult<string> | null)[] = tracked.items.map(() => null);
        try {
          ooxml = ranges.map((range) => (range ? range.getOoxml() : null));
          await context.sync();
        } catch {
          ooxml = tracked.items.map(() => null);
        }

        result.trackedChanges = tracked.items
          // Paired before filtering: `ranges` and `ooxml` are indexed by the
          // UNFILTERED items, so narrowing first would slide every lookup one
          // revision off and attribute one deletion's words to another.
          .map((tc, index) => ({ tc, rangeText: ranges[index]?.text, xml: ooxml[index]?.value }))
          .filter(({ tc }) => isRecent(tc.date))
          .map(({ tc, rangeText, xml }) => ({
            type: String(tc.type ?? ""),
            author: tc.author ?? "",
            // Minute precision, not `toLocaleString()`'s seconds: Word stamps a
            // revision to the minute, so the seconds it reports are ALWAYS ":00"
            // — a constant that reads like real precision and is not.
            date: tc.date ? formatRevisionTimestamp(tc.date as unknown as string | Date) : "",
            text: describeRevisionText(
              pickRevisionText(tc.text, rangeText, extractDeletedTextFromOoxml(xml)),
            ),
          }));
      } catch {
        result.trackedChangesSupported = false;
      }

      // Margin comments — WordApi 1.4.
      try {
        const comments = doc.body.getComments();
        comments.load("items");
        await context.sync();
        for (const c of comments.items) c.load("authorName, creationDate, content, resolved");
        await context.sync();
        result.comments = comments.items
          .filter((c) => isRecent(c.creationDate))
          .map((c) => ({
            author: c.authorName ?? "",
            date: c.creationDate ? new Date(c.creationDate as unknown as string | Date).toLocaleString() : "",
            text: (c.content ?? "").trim(),
            resolved: c.resolved === true,
          }));
      } catch { /* comments API unavailable on this host */ }
    });
  } catch { /* return whatever sections were populated */ }
  return result;
}

export interface InsertTOCResult {
  inserted: boolean;
  /** Set only when a pre-flight probe refused before writing anything. */
  reason?: "unsupported_host";
  /** The TOC landed but the page-number refresh failed, so its numbers may be short. */
  stalePageNumbers?: true;
}

/**
 * Word's "Update Field → Update page numbers only". Desktop exposes it directly
 * (WordApiDesktop 1.4) — on every TOC, since inserting a multi-page list moves
 * the body under all of them. Elsewhere it is done by hand: each entry's page
 * number is its own PAGEREF field inside the TOC's result.
 */
async function refreshTocPageNumbers(context: Word.RequestContext, field: Word.Field): Promise<void> {
  if (Office.context.requirements.isSetSupported("WordApiDesktop", "1.4")) {
    const tocs = context.document.tablesOfContents;
    tocs.load("items");
    await context.sync();
    tocs.items.forEach((toc) => toc.updatePageNumbers());
    await context.sync();
    return;
  }
  const refs = field.result.fields;
  refs.load("items/type");
  await context.sync();
  refs.items.filter((f) => f.type === Word.FieldType.pageRef).forEach((f) => f.updateResult());
  await context.sync();
}

/**
 * Inserts Word's native Table of Contents at the cursor — the same live field
 * as References → Table of Contents in the Word UI: a "Contents" title plus
 * heading levels 1-3 with dot leaders and page numbers that Word computes and
 * keeps up to date as the document changes. Requires WordApi 1.5
 * (Range.insertField); returns { inserted: false } on hosts without it.
 *
 * Probed with `supportsFieldInsertion()` BEFORE the "Contents" title paragraph
 * is written, not just wrapped in try/catch: the title and the field land in
 * two separate calls below, so a host that fails only on `insertField` would
 * otherwise be left with an orphan "Contents" heading and no field beneath
 * it — a half-written result with no indication of why, on the manifest that
 * shares a host with PowerPoint and so carries no WordApi floor of its own.
 */
export async function insertWordTOC(): Promise<InsertTOCResult> {
  if (!supportsFieldInsertion()) {
    return { inserted: false, reason: "unsupported_host" };
  }
  let stalePageNumbers = false;
  try {
    await Word.run(async (context) => {
      const cursor = context.document.getSelection().getRange(Word.RangeLocation.end);
      const title = cursor.insertParagraph("Contents", Word.InsertLocation.after);
      try {
        // Word's built-in gallery style for the TOC title (the blue "Contents").
        title.style = "TOC Heading";
      } catch {
        try { title.style = "Heading 1"; } catch { /* keep Normal */ }
      }
      // Field lands in its own paragraph after the title. Field switches:
      // \o "1-3" = build from heading levels 1-3, \h = hyperlink entries,
      // \z = hide leaders/page numbers in web view, \u = use outline levels.
      const fieldPara = title.insertParagraph("", Word.InsertLocation.after);
      const field = fieldPara
        .getRange(Word.RangeLocation.whole)
        .insertField(Word.InsertLocation.replace, Word.FieldType.toc, String.raw`\o "1-3" \h \z \u`, false);
      // updateResult is not present on every host — the TOC still populates
      // on Word's own next field refresh (print preview / Update Field).
      try {
        (field as { updateResult?: () => void }).updateResult?.();
      } catch { /* field renders on Word's next refresh */ }
      await context.sync();
      // A FULL update numbers the entries before the rebuilt list has settled,
      // so they come out a page short. Measured in Word desktop on a 171-heading
      // document: 86 entries wrong after a full update (updateResult, or Word's
      // own "Update entire table"), 0 after "Update page numbers only" — which is
      // what this does, now that the list is final. The TOC is already in the
      // document by now, so a failure here must not report "not inserted".
      try {
        await refreshTocPageNumbers(context, field);
      } catch {
        stalePageNumbers = true;
      }
    });
    return stalePageNumbers ? { inserted: true, stalePageNumbers } : { inserted: true };
  } catch {
    // insertField unavailable (WordApi < 1.5) or insertion failed.
    return { inserted: false };
  }
}


export async function hasWordTableOfContents(): Promise<boolean | null> {
  try {
    if (Office.context.requirements?.isSetSupported("WordApi", "1.4") === false) return null;
  } catch { /* no requirements API — fall through and try the read */ }
  try {
    return await Word.run(async (context) => {
      const fields = context.document.body.fields;
      fields.load("items/type");
      await context.sync();
      return fields.items.some((f) => f.type === Word.FieldType.toc);
    });
  } catch (err) {
    logOfficeError(err, "word-has-toc");
    return null;
  }
}

/** One entry of the document's own Table of Contents. */
export interface TocFieldEntry {
  /** Entry text, dot leaders and trailing page number stripped. */
  readonly title: string;
  /** Outline level from the TOC 1-9 paragraph style; 0 when the style is unreadable. */
  readonly level: number;
  /**
   * The page Word printed against the entry, or null when the field shows none.
   * Null is the NORMAL case on Word on the web: `insertWordTOC` builds the field
   * with the `\z` switch, which suppresses leaders and page numbers in web view.
   * Never treat null as "page 0" and never navigate by this number — it is the
   * page the section STARTS on, as of Word's last layout.
   */
  readonly page: number | null;
}

/**
 * Splits one rendered TOC line into title / level / page.
 *
 * Pure and separately testable, because the page number is the part that goes
 * wrong. Word separates it from the title with a TAB — the dot leaders are tab
 * fill, not characters — so a trailing number counts as a page number only when
 * a tab, or two or more leader characters, sit in front of it. Matching a bare
 * trailing digit would rewrite "Schedule 2" as title "Schedule" on page 2.
 *
 * Splitting on the tab rather than on a digit also keeps roman-numeral front
 * matter ("Preface<TAB>iii") out of the title without pretending "iii" is a page.
 */
/** Dot-leader fill characters Word renders between a TOC title and its page number. */
const TOC_LEADER_CHARS = ".\u00b7\u2026 ";
/** One character that may trail a TOC title: leader fill or any whitespace. */
const TOC_TITLE_TAIL_CHAR = /[\s.\u00b7\u2026]/;

function isAsciiDigit(ch: string): boolean {
  return ch >= "0" && ch <= "9";
}

/**
 * Splits a flattened TOC line ("Clause 4...12") into title and page number.
 *
 * Scanned from the end instead of matched with a regex: the leader class and the
 * title accept the same characters, so a regex for this shape backtracks super-
 * linearly on a long line. The rule is unchanged - a trailing run of digits counts
 * as a page number only when two or more leader characters sit in front of it -
 * and the split lands where the leftmost-shortest title match put it, because the
 * leader run walked back over here is that whole run.
 */
function splitDottedTocLine(line: string): { head: string; page: string } | null {
  let digitsAt = line.length;
  while (digitsAt > 0 && isAsciiDigit(line[digitsAt - 1] as string)) digitsAt -= 1;
  if (digitsAt === line.length) return null;

  let leadersAt = digitsAt;
  while (leadersAt > 0 && TOC_LEADER_CHARS.includes(line[leadersAt - 1] as string)) leadersAt -= 1;
  if (digitsAt - leadersAt < 2) return null;

  return { head: line.slice(0, leadersAt), page: line.slice(digitsAt) };
}

/** Strips trailing leader fill and whitespace off a TOC title, end-first for the same reason. */
function trimTocTitleTail(title: string): string {
  let end = title.length;
  while (end > 0 && TOC_TITLE_TAIL_CHAR.test(title[end - 1] as string)) end -= 1;
  return title.slice(0, end);
}

export function parseTocLine(text: string, style = ""): TocFieldEntry | null {
  // Trailing whitespace only. Trimming the FRONT too would strip the leading tab
  // off a line carrying a page number and no title ("<TAB>9"), leaving the number
  // itself to be read as the title.
  const raw = (text ?? "").replace(/\r/g, "").trimEnd();
  if (!raw.trim()) return null;
  // The "Contents" caption is its own paragraph, not an entry.
  if (/^TOC\s*Heading$/i.test(style.trim())) return null;

  const levelMatch = /^TOC\s*(\d)/i.exec(style);
  const level = levelMatch?.[1] ? Number.parseInt(levelMatch[1], 10) : 0;

  let head = raw;
  let tail = "";
  const tabAt = raw.lastIndexOf("\t");
  if (tabAt >= 0) {
    head = raw.slice(0, tabAt);
    tail = raw.slice(tabAt + 1).trim();
  } else {
    // No tab survived the read — fall back to two-or-more leader characters
    // before a number, which is what a flattened TOC line looks like.
    const dotted = splitDottedTocLine(raw);
    if (dotted) {
      head = dotted.head;
      tail = dotted.page;
    }
  }

  const title = trimTocTitleTail(head).trim();
  if (!title) return null;
  return { title, level, page: /^\d+$/.test(tail) ? Number.parseInt(tail, 10) : null };
}

/**
 * Reads the document's own Table of Contents, entry by entry.
 *
 * This is the author's curated map: the sections THEY consider structural, named
 * as they name them, which is what a drafting request should be matched against
 * first. `getWordDocumentStructure` reads every Heading 1-9 paragraph instead —
 * a superset that includes headings the author deliberately kept out of the
 * contents — so it is the fallback, not the primary.
 *
 * Three-way return, and callers must keep the cases apart:
 *   `[]`   — the document genuinely has no TOC field. Fall back to headings.
 *   `null` — the read could not be taken (older host, no fields API, refused
 *            read). Also fall back, but do NOT report "no TOC" to the user.
 *   items  — entries in document order.
 *
 * `title` is safe to pass as `anchor_phrase`, since a TOC entry is a copy of the
 * heading text and so appears in the body. Word's automatic list numbering is
 * NOT part of `paragraph.text`, so an entry reading "9. Confidentiality" anchors
 * on a body paragraph reading "Confidentiality" — pass the title without the
 * leading number when one is present.
 */
export async function getWordTableOfContents(): Promise<TocFieldEntry[] | null> {
  try {
    if (Office.context.requirements?.isSetSupported("WordApi", "1.4") === false) return null;
  } catch { /* no requirements API — fall through and try the read */ }
  try {
    return await Word.run(async (context) => {
      const fields = context.document.body.fields;
      fields.load("items/type");
      await context.sync();
      const tocFields = fields.items.filter((f) => f.type === Word.FieldType.toc);
      // Not a failure — most documents simply have no contents page.
      if (tocFields.length === 0) return [];

      // The field's RESULT is the rendered TOC. Its paragraphs carry the TOC 1-9
      // styles, which is the only place the outline level survives — the result
      // text on its own is flat.
      const paraSets = tocFields.map((f) => {
        const paras = f.result.paragraphs;
        paras.load("items");
        return paras;
      });
      await context.sync();
      for (const set of paraSets) {
        for (const p of set.items) p.load("text, style");
      }
      await context.sync();

      const entries: TocFieldEntry[] = [];
      for (const set of paraSets) {
        for (const p of set.items) {
          const entry = parseTocLine(p.text ?? "", p.style ?? "");
          if (entry) entries.push(entry);
        }
      }
      return entries;
    });
  } catch (err) {
    // A TOC field exists but its result could not be read — "unknown", not
    // "none", so the caller falls back without telling the user there is no TOC.
    logOfficeError(err, "word-read-toc");
    return null;
  }
}

// ---------------------------------------------------------------------------
// Header/footer regions — shared by DP-02 (Bates) and DP-03 (page numbers)
// ---------------------------------------------------------------------------

/** Where a page number goes. */
export type PageNumberPosition = "footer" | "header";

/** ``simple`` = "3"; ``with_total`` = "Page 3 of 12". */
export type PageNumberFormat = "simple" | "with_total";

/**
 * Which numeral the page field renders in (AIVA-7863).
 *
 * "add page numbers in roman lettering" used to produce 1, 2, 3: nothing in
 * this capability carried a numeral style, so the request was dropped at the
 * tool boundary and the model still announced Roman numerals in chat. Both
 * casings are offered because they are conventionally different things — lower
 * for front matter (i, ii, iii), upper for a title-page or annex series
 * (I, II, III) — and picking one for the user would be a coin toss.
 *
 * AIVA-7866 adds the alphabetic pair for exactly the same reason: "add page
 * numbers in alphabetic numbering" was answered with "using alphabetic
 * numerals" and stamped 1, 2, 3, because letters were not a value this union
 * could hold. Word renders them a, b, c … z, aa, bb (a repeated letter, not
 * ab/ac), which is what the recognisers below are built around.
 */
export type PageNumberNumeralStyle =
  | "arabic"
  | "roman_lower"
  | "roman_upper"
  | "alpha_lower"
  | "alpha_upper";

/**
 * Word's general-format picture switch for a numeral style, or `undefined` for
 * plain Arabic.
 *
 * The switch is passed as ``insertField``'s field-code argument, exactly as
 * `batesPictureSwitch` passes its numeric `\# "0000"` switch — `{ PAGE \* roman }`
 * is the same field Word's own Insert -> Page Number -> Format menu writes.
 *
 * Arabic deliberately returns `undefined` rather than `\* arabic`: that is the
 * literal argument the field insert used before this option existed, so the
 * default path emits a byte-identical field and cannot regress.
 */
export function pageNumberPictureSwitch(style: PageNumberNumeralStyle): string | undefined {
  if (style === "roman_lower") return String.raw`\* roman`;
  if (style === "roman_upper") return String.raw`\* ROMAN`;
  if (style === "alpha_lower") return String.raw`\* alphabetic`;
  if (style === "alpha_upper") return String.raw`\* ALPHABETIC`;
  return undefined;
}

/**
 * Where the number sits across the page (AIVA-7862).
 *
 * ``center`` is the default and what DP-03's AC-1 specifies, but the user can
 * ask for left or right and that has to be honoured — a page number pinned to
 * the outer margin is an ordinary house style, and silently centring it made
 * "put the page numbers on the right" look like the feature was broken.
 */
export type PageNumberAlignment = "left" | "center" | "right";

/**
 * Map to the Office.js enum, defaulting to centred.
 *
 * Read from `Word.Alignment` rather than the string literals so a host that
 * spells a member differently still gets a valid value; the caller already
 * wraps the assignment in try/catch for hosts with no alignment support at all.
 */
function wordAlignmentFor(alignment: PageNumberAlignment): Word.Alignment {
  if (alignment === "left") return Word.Alignment.left;
  if (alignment === "right") return Word.Alignment.right;
  return Word.Alignment.centered;
}

/** What one section's header/footer region already holds. */
export interface RegionState {
  /** 1-based section number, for user-facing messages. */
  readonly section: number;
  /** Existing text in the region, trimmed. Empty when the region is blank. */
  readonly text: string;
  /** True when the text looks like a Bates stamp (prefix + zero-padded digits). */
  readonly looksBatesStamped: boolean;
}

/** Aggregate footer/header state across every section. */
export interface RegionSurvey {
  readonly sectionCount: number;
  /** Only the sections whose region is non-empty. */
  readonly occupied: readonly RegionState[];
  /** True when at least one occupied region looks Bates-stamped. */
  readonly anyBatesStamped: boolean;
}

// Two matchers for two jobs, deliberately different in breadth.
//
// WARNING (broad, contains): flags anything Bates-ISH so the approval card can
// name it before a write — "Bundle DEF-0001 page" in a footer deserves a
// warning even though the line is not purely a stamp. 4+ digits so a short
// reference ("Rev 2") doesn't cry wolf; false positives are acceptable here
// because the caller shows the text verbatim and the user judges.
const BATES_STAMP = /\b[A-Z]{2,}[-_ ]?\d{4,}\b/;

// DELETION (precise, anchored): a line that IS a stamp — a 2+ letter prefix,
// optional separator, digits, NOTHING else. Anchored so a legend or reference
// merely containing prefix+digits ("Invoice INV-20260101 due") can never be
// deleted by the re-stamp sweep or an unscoped removal. Any digit count, not
// 4+: padding 1-3 is legal (AC-3), and a SIM-500 the sweep could not see is
// how a re-stamp ADDED a second series under the old one while the card
// promised "replaced by the new series, not added alongside" (AIVA-7871).
//
// Two forms, because case and padding are each doing a safety job:
//  - uppercase prefix, any digits — the conventional stamp (WL-000001,
//    SIM-500). Case-sensitive so "Page 3" can never match.
//  - ANY-case prefix, ZERO-PADDED digits — the prefix is taken verbatim from
//    the user, so AIVA itself writes "st000001" when asked, and a stamp its
//    own remove-all cannot see is a removal that "finds no Bates stamps" on a
//    visibly stamped page. Zero padding is the tie-breaker that keeps
//    lowercase safe: a stamp's number is zero-padded, "Page 3" never is.
const BATES_STAMP_LINE = /^[A-Z]{2,}[-_ ]?\s*\d+$/;
const BATES_STAMP_LINE_PADDED = /^[A-Za-z]{2,}[-_ ]?\s*0\d+$/;

/** True when this paragraph IS a Bates stamp — the deletion-grade test. */
function isBatesStampParagraph(text: string): boolean {
  const line = text.trim();
  return BATES_STAMP_LINE.test(line) || BATES_STAMP_LINE_PADDED.test(line);
}

/**
 * True when ``text`` (a paragraph, or a whole region's multi-line text) looks
 * Bates-stamped — the warning-grade test the survey uses. Union of the broad
 * contains-match and the precise line-match, so a short-padding stamp
 * (SIM-500) is flagged too: everything the sweep would REPLACE must be
 * something the card WARNED about.
 */
export function looksLikeBatesStamp(text: string): boolean {
  return BATES_STAMP.test(text) || text.split(/\r?\n+/).some((line) => isBatesStampParagraph(line));
}

/** Escape a literal string for safe interpolation into a RegExp. */
function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

/**
 * Is the requirement set behind ``Range.insertField`` available on this host?
 *
 * A page number has to be a live PAGE field — writing a literal "1" would be
 * wrong the moment the document repaginates — so without this API the
 * capability cannot work at all. Probing up-front is what keeps a footer from
 * being cleared and then left empty when the insert throws (the failure mode
 * G-12 warns about).
 */
export function supportsFieldInsertion(): boolean {
  try {
    return Office.context.requirements.isSetSupported("WordApi", "1.5");
  } catch {
    // No requirements API at all — assume unsupported rather than risk a
    // half-written region.
    return false;
  }
}

function regionOf(section: Word.Section, position: PageNumberPosition): Word.Body {
  return position === "header"
    ? section.getHeader(Word.HeaderFooterType.primary)
    : section.getFooter(Word.HeaderFooterType.primary);
}

/**
 * Read what every section's header/footer already contains, without writing.
 *
 * This is the pre-read that lets the approval card state the blast radius
 * before anything changes — "3 sections; the footer already contains
 * ABC-0250" — rather than discovering it after the fact.
 */
export async function surveyWordRegions(position: PageNumberPosition = "footer"): Promise<RegionSurvey | null> {
  try {
    return await Word.run(async (context) => {
      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      const bodies = sections.items.map((s) => regionOf(s, position));
      for (const body of bodies) body.load("text");
      await context.sync();

      const occupied: RegionState[] = [];
      bodies.forEach((body, i) => {
        const text = (body.text ?? "").trim();
        if (!text) return;
        occupied.push({ section: i + 1, text, looksBatesStamped: looksLikeBatesStamp(text) });
      });

      return {
        sectionCount: sections.items.length,
        occupied,
        anyBatesStamped: occupied.some((r) => r.looksBatesStamped),
      };
    });
  } catch {
    // Host cannot enumerate sections — the caller degrades to a generic warning.
    return null;
  }
}

// ---------------------------------------------------------------------------
// Bates numbering (DP-02)
// ---------------------------------------------------------------------------

/** Bates configuration, after normalisation. */
export interface BatesConfig {
  /** Text stamped before the number, e.g. "ABC-". */
  readonly prefix: string;
  /** Digits the number is zero-padded to (1-10). */
  readonly padding: number;
  /** The number the FIRST page carries (>= 1). */
  readonly startAt: number;
  readonly position: PageNumberPosition;
  /** Horizontal placement of the stamp. Defaults to right — see insertWordBates. */
  readonly alignment: PageNumberAlignment;
}

export const BATES_DEFAULTS = { prefix: "WL-", padding: 6, startAt: 1, alignment: "right" } as const;
const BATES_PADDING_MIN = 1;
const BATES_PADDING_MAX = 10;

function clampPadding(value: unknown): number {
  const text = typeof value === "string" ? value : "";
  const n = typeof value === "number" ? Math.trunc(value) : Number.parseInt(text, 10);
  if (!Number.isFinite(n)) return BATES_DEFAULTS.padding;
  // Clamped, not rejected (spec AC-3): a padding of 40 is a typo, not a reason
  // to refuse to stamp a production bundle.
  return Math.min(BATES_PADDING_MAX, Math.max(BATES_PADDING_MIN, n));
}

/**
 * Normalise whatever the model passed into a usable Bates configuration.
 *
 * Every field has a documented default because a half-specified request
 * ("bates number this") must still produce a valid stamp rather than an error.
 */
export function normaliseBatesConfig(raw: {
  prefix?: unknown;
  padding?: unknown;
  start_at?: unknown;
  position?: unknown;
  alignment?: unknown;
}): BatesConfig {
  const prefixRaw = typeof raw.prefix === "string" ? raw.prefix.trim() : "";
  const startText = typeof raw.start_at === "string" ? raw.start_at : "";
  const startRaw = typeof raw.start_at === "number" ? Math.trunc(raw.start_at) : Number.parseInt(startText, 10);
  return {
    prefix: prefixRaw || BATES_DEFAULTS.prefix,
    padding: clampPadding(raw.padding),
    startAt: Number.isFinite(startRaw) && startRaw >= 1 ? startRaw : BATES_DEFAULTS.startAt,
    position: raw.position === "header" ? "header" : "footer",
    alignment: normaliseBatesAlignment(raw.alignment),
  };
}

/** "left" / "centre" / "centered" spellings → the alignment, else the right default. */
function normaliseBatesAlignment(value: unknown): PageNumberAlignment {
  const raw = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (raw === "left") return "left";
  if (raw === "center" || raw === "centre" || raw === "centered") return "center";
  return BATES_DEFAULTS.alignment;
}

/**
 * The corner the stamp lands in, for user-facing text — "bottom-left",
 * "top-right". Every surface that names the placement (impact line, success
 * message, change log) uses this, so the approval card and the result can
 * never disagree about where the stamp goes (AIVA-7871).
 */
export function describeBatesPlacement(config: BatesConfig): string {
  const side = config.alignment === "center" ? "centre" : config.alignment;
  return `${config.position === "header" ? "top" : "bottom"}-${side}`;
}

/** Word's numeric picture switch for `padding` digits — `\# "000000"`. */
export function batesPictureSwitch(padding: number): string {
  return String.raw`\# "${"0".repeat(clampPadding(padding))}"`;
}

/** The stamp the first page will carry, e.g. "ABC-0250". Used in the confirm line. */
export function formatBatesStamp(config: BatesConfig): string {
  return `${config.prefix}${String(config.startAt).padStart(config.padding, "0")}`;
}

export interface BatesResult {
  readonly applied: boolean;
  readonly sections: number;
  /** The stamp the first page carries, when the write landed. */
  readonly firstStamp?: string;
  readonly reason?: "unsupported_host" | "offset_unsupported" | "failed";
  /**
   * True when the field's rendered result was read back and confirmed to be a
   * number rather than field code.
   *
   * False means "could not confirm" — NOT "wrong". Either the host hides
   * ``field.result``, or ``= PAGE + n`` had not been paginated yet when we
   * looked. Both are reported the same way, because they are indistinguishable
   * from here and the honest response to either is to ask the user to check
   * A code-shaped read-back no longer rolls the stamp back: that guess deleted
   * correct numbering on ordinary Word for Windows, and AC-6 asks that the user
   * be told, not that the work be undone. A start offset is refused up front
   * instead, before anything is written — see insertWordBates.
   */
  readonly verified?: boolean;
}

/**
 * Stamp Bates numbering into every section's footer (or header).
 *
 * Same field mechanism as page numbers, plus the three things that make it
 * Bates: a prefix, zero padding, and a start offset so a number range can
 * continue across a document set.
 *
 * Two deliberate safety properties, both from the specification's own gaps:
 *  - the region is **never cleared** (G-12): the stamp is appended on its own
 *    paragraph, so letterhead and confidentiality legends survive, and Bates
 *    and page numbers stop destroying each other (G-14);
 *  - a start offset above 1 takes the **verify-or-fall-back OOXML route**
 *    (G-13) — see insertWordBatesOffset — because `Range.insertField` takes
 *    the field code as a flat STRING and cannot nest the `{ = { PAGE } + n }`
 *    construction the offset needs; the letters "PAGE" in a formula string
 *    read as a bookmark name ("!Undefined Bookmark, PAGE" on Word for
 *    Windows). Rather than refuse everywhere, the offset is written as raw
 *    OOXML — where the nesting IS expressible — and the rendering is read
 *    back: garbage rolls back to a refusal, so a page reading ABC-000001 when
 *    the bundle should start at ABC-000250 (a citation error in a court
 *    production) can never happen silently.
 */
export async function insertWordBates(config: BatesConfig): Promise<BatesResult> {
  if (config.startAt > 1) {
    // A start offset needs a different write mechanism entirely — see
    // insertWordBatesOffset. It verifies its own result and rolls back on
    // rendering garbage, so a wrong number can never reach a production.
    return insertWordBatesOffset(config);
  }
  if (!supportsFieldInsertion()) {
    return { applied: false, sections: 0, reason: "unsupported_host" };
  }
  const { prefix, padding, position, alignment } = config;
  const picture = batesPictureSwitch(padding);
  try {
    return await Word.run(async (context) => {
      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      await deleteOwnBatesStamps(context, sections, position);

      const written: Array<{ para: Word.Paragraph; field: Word.Field }> = [];
      for (const section of sections.items) {
        const region = regionOf(section, position);
        const para = region.insertParagraph("", Word.InsertLocation.end);
        const range = para.getRange(Word.RangeLocation.whole);
        // Prefix FIRST, then the field: the other order renders "000250ABC-".
        range.insertText(prefix, Word.InsertLocation.replace);
        // Always a plain PAGE field here: a start offset above 1 takes the
        // OOXML route (insertWordBatesOffset) because the formula field it
        // needs — a PAGE field NESTED inside `{ = ... }` — cannot be built
        // through Range.insertField, which takes a code string; the letters
        // "PAGE" in a string read as a bookmark name and Word for Windows
        // stamped "!Undefined Bookmark, PAGE" / "!Syntax Error, =" instead.
        const field = range.insertField(Word.InsertLocation.end, Word.FieldType.page, picture, false);
        written.push({ para, field });
      }
      await context.sync();

      // Alignment goes LAST, in its own sync, and its failure is swallowed —
      // the same rule page numbers learned in AIVA-7862, for two reasons.
      //
      // 1. It must come AFTER the text: `insertText(prefix, Replace)` rewrites
      //    the paragraph's whole range, and an alignment set before that is
      //    discarded with the content it replaced. Setting it first is why
      //    "bates numbering in bottom left" still landed bottom-right.
      // 2. Office.js does not throw where the property is assigned — the set
      //    is queued and the error surfaces at the next `context.sync()`. A
      //    try/catch around the assignment alone therefore catches nothing,
      //    and a host that rejects the value would take the whole batch down
      //    with it, including the field insert. Committing the stamp first
      //    means the numbering always lands; the worst case is that it lands
      //    in the default corner.
      //
      // The default is right, not centred — the G-14 layout: a page number
      // centred at the bottom and the Bates stamp bottom-right coexist in one
      // footer instead of stacking. When the user ASKED for a corner, that
      // choice is theirs and is honoured instead (AIVA-7871).
      try {
        for (const { para } of written) {
          para.alignment = wordAlignmentFor(alignment);
        }
        await context.sync();
      } catch (error) {
        console.warn("[word] bates alignment rejected by the host", error);
      }

      // No verification step: a start offset above 1 takes the OOXML route
      // (see the guard at the top), so what lands here is always a plain PAGE
      // field. That cannot render as field code the way the formula field did,
      // which is what G-13 was about — so there is nothing left to read back.
      const verified = true;

      return {
        applied: true,
        sections: sections.items.length,
        firstStamp: formatBatesStamp(config),
        verified,
      };
    });
  } catch {
    return { applied: false, sections: 0, reason: "failed" };
  }
}

/**
 * Clear OUR OWN previous stamp before writing, and nothing else.
 *
 * G-12 says never clear the region — letterhead and confidentiality legends
 * must survive. That protects the USER'S content. A stamp this feature wrote
 * earlier is not the user's content, and leaving it turns "apply bates
 * numbering with padding 25" into "add a second number": the page ends up
 * carrying WL-000001 AND WL-0000000001, which in a production bundle is a
 * citation defect.
 *
 * So: delete paragraphs that look like a Bates stamp, of ANY prefix —
 * re-running with a different prefix means "change the series", never "run
 * two series at once" — and leave every other paragraph alone. A page number
 * sharing the region (G-14) is NOT ours, so it is left in place: a bundle
 * keeps both, the Bates stamp bottom-right and the page number centred.
 */
async function deleteOwnBatesStamps(
  context: Word.RequestContext,
  sections: Word.SectionCollection,
  position: PageNumberPosition,
): Promise<void> {
  const priorRegions = sections.items.map((s) => regionOf(s, position));
  for (const region of priorRegions) {
    region.paragraphs.load("items/text");
  }
  await context.sync();
  let replaced = 0;
  for (const region of priorRegions) {
    for (const para of region.paragraphs.items) {
      // Deletion-grade match: the paragraph must BE a stamp, not merely
      // contain something stamp-shaped — this sweep destroys what it matches.
      if (isBatesStampParagraph(para.text ?? "")) {
        para.delete();
        replaced += 1;
      }
    }
  }
  if (replaced > 0) await context.sync();
}

// A field that RENDERED as code instead of a number: Word's error strings for
// a broken instruction, or the picture switch showing as literal text. No
// legitimate footer contains these, so they are safe rollback triggers.
const BATES_RENDER_GARBAGE = /!Undefined Bookmark|!Syntax Error|\\#/;

/**
 * The footer paragraph for an offset stamp, as raw OOXML: the prefix, then
 * `{ = { PAGE } + n \# "000…" }` with the PAGE field STRUCTURALLY nested via
 * fldChar begin/end runs.
 *
 * This is the construction `Range.insertField` cannot express — it takes the
 * field code as a flat string, and the letters "PAGE" in a formula string are
 * read as a bookmark name ("!Undefined Bookmark, PAGE" on Word for Windows).
 * The document FORMAT has no such limit: a .docx saved from desktop Word with
 * this exact field carries exactly this XML. The cached result run is left
 * empty on purpose — pre-seeding it with the expected first number would make
 * a host that never recalculates fields show that number on EVERY page, which
 * in a production bundle is a citation defect. Empty renders as blank until
 * Word paginates, and the read-back treats "blank" as unverified, not wrong.
 */
export function batesOffsetOoxml(config: BatesConfig): string {
  const { prefix, padding, startAt, alignment } = config;
  const instruction = String.raw` + ${startAt - 1} \# "${"0".repeat(clampPadding(padding))}" `;
  const paragraph =
    `<w:p><w:pPr><w:jc w:val="${alignment}"/></w:pPr>` +
    `<w:r><w:t xml:space="preserve">${escapeXml(prefix)}</w:t></w:r>` +
    '<w:r><w:fldChar w:fldCharType="begin"/></w:r>' +
    '<w:r><w:instrText xml:space="preserve"> = </w:instrText></w:r>' +
    '<w:r><w:fldChar w:fldCharType="begin"/></w:r>' +
    '<w:r><w:instrText xml:space="preserve"> PAGE </w:instrText></w:r>' +
    '<w:r><w:fldChar w:fldCharType="end"/></w:r>' +
    `<w:r><w:instrText xml:space="preserve">${escapeXml(instruction)}</w:instrText></w:r>` +
    '<w:r><w:fldChar w:fldCharType="separate"/></w:r>' +
    '<w:r><w:fldChar w:fldCharType="end"/></w:r>' +
    "</w:p>";
  return wrapOoxmlPackage(paragraph);
}

/**
 * Stamp Bates numbering with a start offset above 1 (AC-2), via raw OOXML.
 *
 * Verify-or-fall-back (G-13): after writing, each region is read back.
 *  - Rendered garbage (Word's "!Undefined Bookmark" / "!Syntax Error", or a
 *    visible picture switch) → OUR paragraphs are deleted and the offset is
 *    refused, so field code never reaches a produced document.
 *  - Rendered `prefix + digits` in every section → verified.
 *  - Blank (the host simply hasn't paginated yet, or hides field results) →
 *    the stamp is KEPT and verified stays false, so the caller tells the user
 *    to check page 1 (AC-6). Deleting on "can't confirm" is the old bug that
 *    removed correct numbering from ordinary Word for Windows.
 */
async function insertWordBatesOffset(config: BatesConfig): Promise<BatesResult> {
  if (!supportsFieldInsertion()) {
    return { applied: false, sections: 0, reason: "unsupported_host" };
  }
  const { prefix, position } = config;
  const ooxml = batesOffsetOoxml(config);
  const stampPattern = new RegExp(String.raw`^${escapeRegExp(prefix)}\s*\d+$`, "i");
  try {
    return await Word.run(async (context) => {
      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      await deleteOwnBatesStamps(context, sections, position);

      for (const section of sections.items) {
        regionOf(section, position).insertOoxml(ooxml, Word.InsertLocation.end);
      }
      await context.sync();

      const regions = sections.items.map((s) => regionOf(s, position));
      for (const region of regions) {
        region.paragraphs.load("items/text");
      }
      await context.sync();

      let garbage = 0;
      let confirmed = 0;
      for (const region of regions) {
        for (const para of region.paragraphs.items) {
          const text = (para.text ?? "").trim();
          if (BATES_RENDER_GARBAGE.test(text)) {
            para.delete();
            garbage += 1;
          } else if (stampPattern.test(text)) {
            confirmed += 1;
          }
        }
      }
      if (garbage > 0) {
        await context.sync();
        return { applied: false, sections: 0, reason: "offset_unsupported" };
      }
      return {
        applied: true,
        sections: sections.items.length,
        firstStamp: formatBatesStamp(config),
        verified: confirmed >= sections.items.length,
      };
    });
  } catch {
    // Covers hosts whose insertOoxml rejects the package outright — the batch
    // fails before anything lands, so there is nothing to roll back.
    return { applied: false, sections: 0, reason: "offset_unsupported" };
  }
}

/**
 * Shared removal skeleton for stamps previously written into each section's
 * header/footer region: load every region's paragraphs, delete the ones
 * `judge` marks for removal, and count the ones it marks as real stamps.
 * Shared by removeWordBates and removeWordPageNumbers so the two sweeps
 * cannot disagree about how regions are walked.
 */
async function removeRegionParagraphs(
  position: PageNumberPosition,
  judge: (text: string, region: Word.Body) => { remove: boolean; count: boolean },
  errorTag: string,
): Promise<{ applied: boolean; sections: number; reason?: "failed" }> {
  try {
    return await Word.run(async (context) => {
      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      const regions = sections.items.map((s) => regionOf(s, position));
      for (const region of regions) {
        region.paragraphs.load("items/text");
        // The region goes to the judge as well as its text, and its field codes
        // are read in the same batch, because a skip-front-matter page number
        // renders BLANK (AIVA-7869): from the text alone it is indistinguishable
        // from an ordinary empty footer paragraph, and the sweep would delete it
        // and then report that there had been nothing to remove.
        loadRegionFieldCodes(region);
      }
      await context.sync();

      let removed = 0;
      for (const region of regions) {
        for (const para of region.paragraphs.items) {
          const text = (para.text ?? "").trim();
          const verdict = judge(text, region);
          if (verdict.remove) {
            if (verdict.count) removed += 1;
            para.delete();
          }
        }
      }
      await context.sync();
      return { applied: removed > 0, sections: sections.items.length };
    });
  } catch (err) {
    logOfficeError(err, errorTag);
    return { applied: false, sections: 0, reason: "failed" };
  }
}

/**
 * Remove Bates paragraphs previously stamped into each section's region.
 *
 * Only paragraphs that ARE a stamp — a prefix + digits and nothing else — are
 * deleted, so a letterhead or a page number sharing the region is left alone.
 *
 * With no `prefix`, stamps of EVERY prefix go (AC-4: "Removal empties the
 * target region in every section" — the spec's removal clause has no prefix
 * language, so "remove bates numbering" must not silently mean "remove WL-").
 * With no `position`, both footer and header are swept for the same reason: a
 * header stamp surviving "remove bates numbering" fails AC-4 just as surely.
 */
export async function removeWordBates(
  prefix?: string,
  position?: PageNumberPosition,
): Promise<BatesResult> {
  // Prefix given → scoped to that series (case-insensitive, as before).
  // No prefix → any stamp, via the same deletion-grade test the re-stamp
  // sweep uses — everything AIVA can write, remove-all can see.
  const scoped = prefix ? new RegExp(String.raw`^${escapeRegExp(prefix)}\s*\d+$`, "i") : null;
  const judge = (text: string) => {
    const isStamp = Boolean(text) && (scoped ? scoped.test(text) : isBatesStampParagraph(text));
    return { remove: isStamp, count: isStamp };
  };
  if (position) {
    return removeRegionParagraphs(position, judge, "word-remove-bates");
  }
  const footer = await removeRegionParagraphs("footer", judge, "word-remove-bates");
  const header = await removeRegionParagraphs("header", judge, "word-remove-bates");
  return {
    applied: footer.applied || header.applied,
    sections: Math.max(footer.sections, header.sections),
    ...(footer.reason && header.reason ? { reason: "failed" as const } : {}),
  };
}

/**
 * The Bates stamps present in a region's text, one entry per stamp line —
 * ["WL-000001", "SIM-500"]. Scoped to `prefix` when given. Used by the
 * remove pill so its impact line can name what was actually FOUND in the
 * document rather than guessing a prefix.
 */
export function extractBatesStamps(text: string, prefix?: string): string[] {
  const scoped = prefix ? new RegExp(String.raw`^${escapeRegExp(prefix)}\s*\d+$`, "i") : null;
  return text
    .split(/\r?\n+/)
    .map((line) => line.trim())
    .filter((line) => (scoped ? scoped.test(line) : isBatesStampParagraph(line)));
}

export interface PageNumberResult {
  readonly applied: boolean;
  /** Regions actually written to — NOT necessarily the document's section count. */
  readonly sections: number;
  /**
   * Regions skipped because they already carried a page number: either the
   * document was numbered before, or the region is shared with a section
   * already stamped ("Link to Previous"). Reported so the caller can say what
   * really happened instead of implying every section was touched.
   */
  readonly skipped?: number;
  /**
   * Set when nothing was written, so the caller can explain why.
   *
   * The last three are the front-matter cases (AIVA-7869): asked to number
   * everything EXCEPT the contents pages, the run has to find where the
   * contents end, and every way that can fail is reported rather than
   * defaulting to numbering the whole document — which is what makes the
   * request look serviced when it was not.
   */
  readonly reason?:
    | "unsupported_host"
    | "failed"
    | "offset_unsupported"
    | "no_front_matter"
    | "toc_not_rendered"
    | "no_body_after_front_matter";
  /**
   * What Word actually said when the write threw, e.g.
   * ``GeneralException: The operation failed``.
   *
   * Surfaced all the way into the chat message on purpose. "I couldn't add page
   * numbers" alone is unactionable — for the user AND for whoever they report it
   * to — and the real error only ever existed in the browser console, which is
   * not somewhere most people can reach.
   */
  readonly errorDetail?: string;
}

/** A short, human-readable summary of an Office.js failure. */
function describeWordError(error: unknown): string | undefined {
  if (error === null || error === undefined) return undefined;
  const e = error as { code?: unknown; message?: unknown };
  const code = typeof e.code === "string" && e.code ? e.code : undefined;
  // Never String() the error object itself: an Office.js RichApi.Error has no
  // useful toString, so it stringifies to "[object Object]" — which would put
  // literal junk in the user's chat bubble. A thrown string is used as-is; a
  // thrown object with neither code nor message has nothing to report.
  const thrownString = typeof error === "string" && error ? error : undefined;
  const message =
    typeof e.message === "string" && e.message ? e.message : (thrownString ?? code ?? "");
  const combined = code && !message.startsWith(code) ? `${code}: ${message}` : message;
  const trimmed = combined.trim();
  if (!trimmed) return undefined;
  // Kept short: this lands in a chat bubble, not a log file.
  return trimmed.length > 200 ? `${trimmed.slice(0, 197)}...` : trimmed;
}

/**
 * A STRICTLY well-formed Roman numeral, lower case, 1-3999.
 *
 * Deliberately not the loose `[ivxlcdm]+` character class, which would treat
 * ordinary footer words as page numbers: "civil", "dill" and "mill" are all
 * built purely from Roman letters, and `removeWordPageNumbers` deletes what
 * this judge accepts. The strict grammar rejects every one of them.
 *
 * Every quantifier is optional, so the pattern also matches the EMPTY string.
 * `isRomanNumeral` rejects empty input before testing — the guard lives there
 * rather than as a lookahead here, which keeps this pattern to the canonical
 * strict-Roman form instead of a denser one.
 *
 * Groups are non-capturing: nothing reads them back.
 */
const ROMAN_NUMERAL = /^m{0,3}(?:cm|cd|d?c{0,3})(?:xc|xl|l?x{0,3})(?:ix|iv|v?i{0,3})$/;

/**
 * Is `text` a Roman numeral in ONE consistent case?
 *
 * A rendered `\* roman` field is all-lower and a `\* ROMAN` field is all-upper,
 * so mixed case ("Mix", "Dis") cannot have come from one and is left alone.
 *
 * A residual false positive remains: a footer whose entire text is exactly
 * "MIX" or "DI" is a valid numeral and would be treated as ours. That is the
 * same class of limit the Arabic rule has always carried (a footer reading just
 * "2024" is read as a page number), and it is bounded the same way — only a
 * paragraph that is NOTHING but the numeral matches, and only inside a
 * header/footer region.
 */
function isRomanNumeral(text: string): boolean {
  if (!text) return false;
  const uniformCase = text === text.toLowerCase() || text === text.toUpperCase();
  return uniformCase && ROMAN_NUMERAL.test(text.toLowerCase());
}

/**
 * A rendered `\* alphabetic` / `\* ALPHABETIC` field (AIVA-7866).
 *
 * Word does NOT count a, b … z, aa, ab, ac: past z it REPEATS the letter —
 * 27 is "aa", 28 is "bb", 53 is "aaa". So the rendered text is always one
 * letter repeated, which is a far tighter pattern than "a run of letters" and
 * is what keeps ordinary footer words ("of", "Ltd") out.
 *
 * The two alternatives, rather than one case-insensitive pattern plus a
 * uniform-case check, because `\1` back-references are already case-sensitive:
 * "aA" must not match, and this spells that out without a second test.
 *
 * Residual limit, the same one the Roman rule documents: a footer whose entire
 * text is exactly "a" is read as ours. Only a paragraph that is NOTHING but the
 * numeral matches, and only inside a header/footer region, so the blast radius
 * is a one-letter footer — not prose.
 */
function isAlphabeticNumeral(text: string): boolean {
  return /^([a-z])\1*$/.test(text) || /^([A-Z])\1*$/.test(text);
}

/** Either non-Arabic numeral shape. One judge so the bare-numeral branch and
 *  the "Page n of m" branch cannot disagree about what a numeral is. */
function isNonArabicNumeral(text: string): boolean {
  return isRomanNumeral(text) || isAlphabeticNumeral(text);
}

/**
 * Does this paragraph look like a page number we inserted?
 *
 * A rendered PAGE field reads as digits; ``with_total`` reads "Page 3 of 12".
 * Shared by the insert guard and the remove path so the two cannot disagree
 * about what counts as ours.
 *
 * AIVA-7863: it must recognise Roman numerals too, or the numeral style ships
 * with three fresh defects — re-running would stack a second number into the
 * footer (the "Link to Previous" guard reads this), "remove the page numbers"
 * would silently leave them behind, and `hasWordPageNumbers` would report a
 * numbered document as unnumbered.
 *
 * AIVA-7866: alphabetic numerals inherit all three, so they are taught here in
 * the same pass that adds the picture switch — never in a follow-up.
 */
function isPageNumberParagraph(text: string): boolean {
  const t = text.trim();
  if (/^\d+$/.test(t) || /^page\s+\d+\s+of\s+\d+$/i.test(t)) return true;
  if (isNonArabicNumeral(t)) return true;
  // "Page iii of xii" — both halves must be numerals, so a sentence beginning
  // "Page ... of ..." does not qualify.
  const withTotal = /^page\s+(\S+)\s+of\s+(\S+)$/i.exec(t);
  return Boolean(withTotal && isNonArabicNumeral(withTotal[1]!) && isNonArabicNumeral(withTotal[2]!));
}

// ---------------------------------------------------------------------------
// Custom start number (AIVA-7867)
// ---------------------------------------------------------------------------

/**
 * Bounds on the number the first page carries.
 *
 * A sanity bound, not a Word limit. The value is interpolated into a field
 * instruction, so a pasted phone number or a negative offset would produce a
 * field Word cannot evaluate — and an unevaluable field renders as visible
 * error text in the footer. Out-of-range values are CLAMPED rather than
 * refused, for the same reason Bates padding is: a typo is not a reason to
 * leave the document unnumbered.
 */
export const PAGE_NUMBER_START_MIN = 1;
export const PAGE_NUMBER_START_MAX = 999999;

/**
 * Normalise whatever the model passed into a usable first-page number.
 *
 * Only a number or a string is read. Anything else — an object, an array, a
 * boolean — falls to the default rather than being stringified: `String({})`
 * is "[object Object]", which parses to NaN anyway, and routing it through a
 * conversion that *looks* meaningful hides the fact that the value was junk.
 */
export function normalisePageNumberStart(raw: unknown): number {
  let n = Number.NaN;
  if (typeof raw === "number") n = Math.trunc(raw);
  else if (typeof raw === "string") n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return PAGE_NUMBER_START_MIN;
  return Math.min(PAGE_NUMBER_START_MAX, Math.max(PAGE_NUMBER_START_MIN, n));
}

/**
 * The runs for one offset page field — `{ = { PAGE } + 499 \* roman }`.
 *
 * A NESTED field, and that is the whole point. The flat spelling
 * `{ = PAGE + 499 }` is what `Range.insertField` can produce, and it does not
 * work: inside a formula field a bare `PAGE` is parsed as a BOOKMARK NAME, not
 * as the PAGE field, so Word stamps "!Undefined Bookmark, PAGE" into the
 * footer. (Prefixing the code with "=" on top of `FieldType.expression` — which
 * supplies its own — gives "!Syntax Error, =" instead.) Both were tried against
 * Word for Windows, which is why the Bates path refuses a start offset outright
 * rather than shipping a broken stamp.
 *
 * Only a real nested field evaluates, and a real nested field needs fldChar
 * begin/separate/end runs around the inner PAGE. Office.js has no API for that
 * at all — `Field.code` is a plain string, and braces typed into it are literal
 * characters, not field delimiters — so the field has to arrive as OOXML.
 *
 * The `<w:t>` between separate and end is the field's CACHED result, which is
 * what Word displays until it next recalculates. It carries the value the field
 * will compute, so the footer never flashes a stale "1" before recalc.
 */
function offsetFieldRuns(kind: "PAGE" | "NUMPAGES", offset: number, cached: string, picture?: string): string {
  const run = (inner: string): string => `<w:r>${inner}</w:r>`;
  const instr = (text: string): string =>
    run(`<w:instrText xml:space="preserve">${escapeXml(text)}</w:instrText>`);
  const fldChar = (type: string): string => run(`<w:fldChar w:fldCharType="${type}"/>`);
  // Lifted out of the instruction below: a template literal nested inside
  // another is hard to read and Sonar flags it (typescript:S4624).
  const switches = picture ? ` ${picture}` : "";
  return [
    fldChar("begin"),
    instr(" = "),
    fldChar("begin"),
    instr(` ${kind} `),
    fldChar("separate"),
    run("<w:t>1</w:t>"),
    fldChar("end"),
    // The picture switch rides on the OUTER field: it formats the computed sum.
    // On the inner PAGE it would render the un-offset number in Roman and then
    // fail the addition, because Word cannot add "iii".
    instr(` + ${offset}${switches} `),
    fldChar("separate"),
    run(`<w:t>${escapeXml(cached)}</w:t>`),
    fldChar("end"),
  ].join("");
}

/** OOXML `w:jc` value for an alignment. */
function ooxmlJustification(alignment: PageNumberAlignment): string {
  if (alignment === "left") return "left";
  if (alignment === "right") return "right";
  return "center";
}

/**
 * The whole footer paragraph for an offset page number, as a flat-OPC package.
 *
 * Exported for the tests: this is the half that can be asserted without a Word
 * host, and the nested-field shape IS the acceptance criterion — a flat
 * `{ = PAGE + n }` here is the defect, not a stylistic difference.
 *
 * Alignment is baked into `w:pPr` AND assigned by the caller to the paragraph
 * the package is inserted into, in the same sync. Both are needed: the `w:jc`
 * here is what a host that keeps the inserted paragraph mark honours, and the
 * assignment is what saves the host that merges the package's paragraph into
 * the mark it lands on — where that mark's justification wins. See
 * `writeOffsetPageNumber`.
 *
 * `with_total` offsets NUMPAGES by the same amount: a twelve-page document
 * starting at 500 runs 500-511, so "Page 500 of 12" would be nonsense.
 */
export function pageNumberStartOoxml(
  format: PageNumberFormat,
  alignment: PageNumberAlignment,
  numeralStyle: PageNumberNumeralStyle,
  startAt: number,
): string {
  const offset = startAt - 1;
  const picture = pageNumberPictureSwitch(numeralStyle);
  const cached = String(startAt);
  const text = (value: string): string => `<w:r><w:t xml:space="preserve">${value}</w:t></w:r>`;
  const runs =
    format === "with_total"
      ? text("Page ") +
        offsetFieldRuns("PAGE", offset, cached, picture) +
        text(" of ") +
        offsetFieldRuns("NUMPAGES", offset, cached, picture)
      : offsetFieldRuns("PAGE", offset, cached, picture);
  return wrapOoxmlPackage(
    `<w:p><w:pPr><w:jc w:val="${ooxmlJustification(alignment)}"/></w:pPr>${runs}</w:p>`,
  );
}

// ---------------------------------------------------------------------------
// Numbering that skips the front matter (AIVA-7869)
// ---------------------------------------------------------------------------

/**
 * Bookmark on the LAST front-matter paragraph — the end of the cover page and
 * contents. Every page up to and including the one it sits on carries no page
 * number; numbering starts at 1 on the first page after it.
 *
 * A bookmark rather than a page count, because the add-in cannot read page
 * numbers at all: Office.js gives a paragraph no page, `Word.Section` has no
 * `linkToPrevious`, and `PageSetup` (desktop-only anyway) has no numbering
 * restart. `{ PAGEREF }` makes WORD compute the page, which also means the
 * footer stays right when the contents grows from one page to two — a fixed
 * "skip 2" would silently go wrong on the next repagination.
 *
 * It marks the front matter's END rather than the body's START because a footer
 * belongs to a whole page and those two are not always on different ones. When
 * the contents ends halfway down page 3 and the body begins on that same page,
 * anchoring to the body numbered page 3 as "1" — printing a number on a page
 * that still shows the tail of the contents, which is the request inverted.
 * Anchoring to the front matter's last page leaves page 3 blank and numbers
 * page 4 as 1. A document with a page break after its contents is unaffected:
 * the front matter ends on page 2, so the body's first page is still 1.
 *
 * The bookmark's NAME is deliberately unchanged from when it marked the body
 * start: the re-run guard, `hasWordPageNumbers` and removal all recognise this
 * numbering by that name in the field code, so changing it would make every
 * document numbered by an earlier build look unnumbered — and re-running would
 * stack a second field into the footer instead of replacing the first.
 *
 * Word's own rules for the name: alphanumeric and underscore only, must begin
 * with a letter, 40 characters at most.
 */
export const FRONT_MATTER_END_BOOKMARK = "AIVA_BodyStart";

/**
 * Paragraph styles that put a paragraph in the contents rather than the body:
 * Word's stock `TOC 1`-`TOC 9` entry styles and the `TOC Heading` title above
 * them.
 *
 * Styles rather than the fields API, for the reason the style-based TOC read
 * further down already gives — a rendered TOC is always paragraphs styled
 * TOC 1-9, and the field's own result is unreadable on hosts that have not
 * paginated.
 */
const FRONT_MATTER_STYLE = /^TOC\s*(?:\d|Heading)$/i;

/**
 * Word's numeric picture switch, three sections: positive;negative;zero. `0`
 * forces a digit for a positive result; the empty negative and zero sections
 * render NOTHING.
 *
 * That is the entire mechanism for skipping front matter. On a front-matter
 * page PAGE is at most the bookmark's page, so the formula is zero or negative
 * and the footer is blank; on the first page after it the result is exactly 1.
 * No IF field, no section break, and ONE nesting level — the same shape as the
 * custom-start field above, which is the only nested-field spelling proved
 * against Word for Windows.
 */
const BLANK_UNLESS_POSITIVE = String.raw`\# "0;;"`;

/** Why the boundary could not be marked. Never "assume the contents is one page". */
type FrontMatterEndFailure = "no_front_matter" | "toc_not_rendered" | "no_body_after_front_matter";

/** Does the body hold a table-of-contents field? Guarded: the fields API is
 *  absent on some hosts, and its absence is not "there is no TOC". */
function hasTocField(fields: Word.FieldCollection): boolean {
  try {
    return fields.items.some((f) => f.type === Word.FieldType.toc);
  } catch {
    return false;
  }
}

/**
 * Find where the front matter ends, so the footer field can tell it from the
 * body, and return the range the bookmark belongs on. A pure read.
 *
 * It does NOT write the bookmark (AIVA-8181). Word gives every mutating sync its
 * own undo record, and a bookmark committed here — ahead of the footer writes —
 * was a third Ctrl+Z on top of the page number and its alignment. The caller
 * queues it with `queueFrontMatterBookmark` into the same sync as the first
 * footer write instead.
 *
 * Returns a failure string rather than guessing whenever the boundary cannot be
 * found. The three cases are genuinely different to the user:
 *
 *   - `no_front_matter` — no contents page in this document, so there is
 *     nothing to exclude and ordinary numbering is what they want.
 *   - `toc_not_rendered` — a TOC field exists but has never been calculated
 *     (the normal state on Word on the web), so it has no `TOC 1-9`
 *     paragraphs to measure. Refreshing the field fixes it.
 *   - `no_body_after_front_matter` — the contents is the last thing in the
 *     document; there are no body pages to number.
 *
 * Anything before the boundary is skipped, not just the contents itself, which
 * is what makes a cover page come out unnumbered too.
 */
async function findFrontMatterEnd(
  context: Word.RequestContext,
): Promise<Word.Range | FrontMatterEndFailure> {
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items/style");
  const fields = context.document.body.fields;
  fields.load("items/type");
  await context.sync();

  const items = paragraphs.items;
  // LAST front-matter paragraph, not the first: a contents page is a run of
  // TOC 1-9 paragraphs, and the body begins after all of them.
  let lastFrontMatter = -1;
  for (let i = 0; i < items.length; i += 1) {
    if (FRONT_MATTER_STYLE.test((items[i]!.style ?? "").trim())) lastFrontMatter = i;
  }
  if (lastFrontMatter < 0) return hasTocField(fields) ? "toc_not_rendered" : "no_front_matter";

  // The body still has to exist — a document that ends with its contents has no
  // pages to number — even though the bookmark now goes on the front matter.
  if (!items[lastFrontMatter + 1]) return "no_body_after_front_matter";

  // At the END of the LAST front-matter paragraph, so PAGEREF resolves to the
  // page the front matter finishes on — including the case where it finishes
  // halfway down a page the body then shares. "End" rather than "Start" for the
  // reason the reverse anchor needed "Start": a range that straddles a page
  // break reports the page it begins on, and here the later page is the answer.
  return items[lastFrontMatter]!.getRange(Word.RangeLocation.end);
}

/**
 * Queue the front-matter bookmark onto `anchor`. Does NOT sync — see
 * `findFrontMatterEnd` for why it has to ride in a footer write's sync.
 *
 * insertBookmark deletes any same-named bookmark first, which is what makes a
 * second run replace the marker instead of stacking markers — and what
 * relocates the marker left on the body by an earlier build.
 */
function queueFrontMatterBookmark(anchor: Word.Range): void {
  anchor.insertBookmark(FRONT_MATTER_END_BOOKMARK);
}

/**
 * The whole footer paragraph for numbering that skips the front matter:
 *
 *   `{ = { PAGE } - { PAGEREF AIVA_BodyStart } \# "0;;" }`
 *
 * No `+ 1`: the bookmark marks the last page OF the front matter, so that page
 * subtracts to zero and blanks, and the first page after it is 1. See
 * `FRONT_MATTER_END_BOOKMARK` for why the boundary is anchored that way.
 *
 * Both inner fields are real nested fields for the reason `offsetFieldRuns`
 * documents at length: inside a formula field a bare `PAGE` is parsed as a
 * BOOKMARK NAME and Word stamps "!Undefined Bookmark, PAGE" into the footer.
 *
 * Always Arabic and always "just the number". A `\*` numeral switch would fight
 * the `\#` picture that does the blanking, and "Page 3 of 12" would leave the
 * literal "Page  of " visible on the contents page with the number missing from
 * the middle of it — worse than not honouring the request. The caller is
 * therefore responsible for telling the user that plain Arabic numbering is
 * what it chose; `format` and `numeralStyle` are deliberately not parameters
 * here, so no call site can believe it passed one.
 *
 * ``startAt`` above 1 DOES ride along, for "start at 200 excluding the contents"
 * (AIVA-7867 + AIVA-7869 together):
 *
 *   `{ = IF({ PAGE } > { PAGEREF X }, { PAGE } - { PAGEREF X } + 199, 0) \# "0;;" }`
 *
 * The offset cannot simply be added to the subtraction, which is why these two
 * options were mutually exclusive before: `PAGE - X + 199` is positive on the
 * contents pages too, so every page the user asked to leave blank would print a
 * number. No single expression can be zero-or-less up to the boundary and
 * offset past it — the slope has to be 1 either side — so the choice has to be
 * conditional.
 *
 * `IF(x,y,z)` is a function OF Word's formula field, not a nested `{ IF }`
 * field. That matters: a nested IF field would put the arithmetic three field
 * levels deep, and one level is the only nesting this feature has ever got
 * working (see `offsetFieldRuns`). As a function it stays at exactly the depth
 * the plain skip formula already uses, with PAGE and PAGEREF nested once each —
 * twice over, because the condition and the result each need them.
 */
export function pageNumberSkipFrontMatterOoxml(
  alignment: PageNumberAlignment,
  startAt: number = PAGE_NUMBER_START_MIN,
): string {
  const run = (inner: string): string => `<w:r>${inner}</w:r>`;
  const instr = (text: string): string =>
    run(`<w:instrText xml:space="preserve">${escapeXml(text)}</w:instrText>`);
  const fldChar = (type: string): string => run(`<w:fldChar w:fldCharType="${type}"/>`);
  const nested = (code: string): string =>
    [fldChar("begin"), instr(` ${code} `), fldChar("separate"), run("<w:t>1</w:t>"), fldChar("end")].join("");
  const page = (): string => nested("PAGE");
  const boundary = (): string => nested(`PAGEREF ${FRONT_MATTER_END_BOOKMARK}`);
  // The default start keeps the simpler formula rather than IF(…, +0, 0): it is
  // the spelling already proved against Word, and every ordinary skip run must
  // stay byte-identical to it.
  const formula =
    startAt > PAGE_NUMBER_START_MIN
      ? [
          instr(" = IF("),
          page(),
          instr(" > "),
          boundary(),
          instr(", "),
          page(),
          instr(" - "),
          boundary(),
          instr(` + ${startAt - 1}, 0) ${BLANK_UNLESS_POSITIVE} `),
        ]
      : [instr(" = "), page(), instr(" - "), boundary(), instr(` ${BLANK_UNLESS_POSITIVE} `)];
  const runs = [
    fldChar("begin"),
    ...formula,
    fldChar("separate"),
    // Cached result: BLANK, at every start value. This field is only ever
    // written when there IS front matter to skip, so page one is a front-matter
    // page by definition and its footer must show nothing. Caching a digit
    // would flash a page number onto the contents page until Word next
    // recalculated — the exact thing the ticket asks us to stop doing.
    run("<w:t></w:t>"),
    fldChar("end"),
  ].join("");
  return wrapOoxmlPackage(
    `<w:p><w:pPr><w:jc w:val="${ooxmlJustification(alignment)}"/></w:pPr>${runs}</w:p>`,
  );
}

/**
 * Queue a read of one region's field CODES.
 *
 * The skip-front-matter field renders BLANK on a front-matter page, and a
 * footer part reports ONE cached result rather than a value per page — so the
 * rendered-text guard that stops every other numbering style from
 * double-stamping cannot see this one. The field code can: it is the only
 * signal that survives a blank render.
 */
function loadRegionFieldCodes(region: Word.Body): void {
  try {
    region.fields.load("items/code");
  } catch {
    // No fields API on this host — the text guard is all there is.
  }
}

/** Does this region already carry a skip-front-matter field of ours? */
function regionCarriesSkipField(region: Word.Body): boolean {
  try {
    return region.fields.items.some((f) => (f.code ?? "").includes(FRONT_MATTER_END_BOOKMARK));
  } catch {
    return false;
  }
}

/**
 * Which OOXML package this run needs, or null for the plain `insertField` path.
 *
 * Skipping the front matter and a custom start now travel TOGETHER: the skip
 * package takes the start value and expresses both, so "start at 200 excluding
 * the contents" numbers the first page after the front matter 200 and leaves
 * the contents blank. They were mutually exclusive while the formula was a bare
 * subtraction — see `pageNumberSkipFrontMatterOoxml` for what changed and why
 * the offset could not simply be added to it.
 *
 * The skip still overrides `format` and `numeralStyle`, which the caller states.
 */
function skipFrontMatterOrOffsetPackage(
  skipFrontMatter: boolean,
  format: PageNumberFormat,
  alignment: PageNumberAlignment,
  numeralStyle: PageNumberNumeralStyle,
  startAt: number,
): string | null {
  if (skipFrontMatter) return pageNumberSkipFrontMatterOoxml(alignment, startAt);
  if (startAt > PAGE_NUMBER_START_MIN) return pageNumberStartOoxml(format, alignment, numeralStyle, startAt);
  return null;
}

/**
 * Did Word render a field as an ERROR rather than as a number?
 *
 * Deliberately matches only Word's own field-error strings — never "the text is
 * not the number I expected". An earlier offset check compared the rendered
 * text against the expected value and rolled back stamps that were perfectly
 * good, because a freshly inserted field has no calculated result yet (the
 * field mock in tests/helpers/word-mocks.ts records that regression). Rolling
 * back a correct stamp is worse than leaving a correct one unverified, so the
 * rollback fires on an explicit error string and nothing else.
 */
const FIELD_ERROR = /!(?:Undefined Bookmark|Syntax Error|Unexpected End of Formula|Zero Divide)|\bError!/i;

export function looksLikeFieldError(text: string): boolean {
  return FIELD_ERROR.test(text);
}

/**
 * Set the alignment on a page-number paragraph, in its OWN sync, swallowing a
 * host that refuses it (AIVA-7862).
 *
 * Office.js does not throw where the property is assigned — the set is queued
 * and the error surfaces at the next `context.sync()`. A try/catch wrapped
 * around the assignment therefore catches nothing, and a host that rejects the
 * value takes the whole batch down with it, including the field insert: the
 * user asked for right-aligned page numbers and got "I couldn't add page
 * numbers" with an unchanged document. Committing the field first means the
 * number always lands; the worst case is that it lands centred.
 *
 * AIVA-8181: neither write path calls this FIRST any more — both queue the
 * alignment with the number so the pair lands as one undo record, and reach
 * this only when that combined sync was refused.
 */
async function alignPageNumberParagraph(
  context: Word.RequestContext,
  para: Word.Paragraph,
  alignment: PageNumberAlignment,
): Promise<void> {
  try {
    para.alignment = wordAlignmentFor(alignment);
    await context.sync();
  } catch (error) {
    console.warn("[word] page-number alignment rejected by the host", error);
  }
}

/**
 * Did the OOXML package land in `para`? Read after a failed combined sync.
 *
 * The number is fields, and on the skip-front-matter path those render BLANK,
 * so the paragraph's text cannot answer this — its field count can. Returns
 * null when even the read fails, which is what a host that rolled the whole
 * batch back (and so never created `para`) answers with.
 */
async function offsetPackageLanded(context: Word.RequestContext, para: Word.Paragraph): Promise<boolean | null> {
  try {
    para.fields.load("items");
    await context.sync();
    return para.fields.items.length > 0;
  } catch {
    return null;
  }
}

/**
 * Append the offset page-number paragraph to one region.
 *
 * ``probe`` is set for the FIRST region only. Every region receives a
 * byte-identical package, so a host that cannot render this field will fail on
 * all of them — proving it once costs one extra read instead of one per
 * section, and it happens before any other region is touched, so a host that
 * cannot do offsets leaves the document completely unchanged rather than
 * half-stamped.
 *
 * Returns false only from a probe, and only with the region left as it was
 * found. A later region that throws is NOT swallowed: at that point the offset
 * is known to work, so the failure is an ordinary host error and belongs in the
 * caller's catch with its Word error detail intact.
 *
 * ONE mutating sync on the happy path (AIVA-8181), the same shape as
 * `writePlainPageNumber`. The package used to be inserted at the region's end
 * and the alignment assigned in a second sync, which put two records on Word's
 * undo stack: the first Ctrl+Z only reverted the centring and left the number
 * in the footer, now on the left.
 *
 * The second sync existed because Word merges a package's paragraph INTO the
 * paragraph mark it lands on, and the mark owns the paragraph properties — so
 * the package's own `w:jc` lost to the footer's left justification. The package
 * therefore now lands in a paragraph created here, and the alignment is queued
 * onto THAT paragraph in the same batch: whichever way the host merges, the
 * mark that wins is one whose alignment this code set.
 *
 * Queued LAST for the reason `writePlainPageNumber` gives: Office.js stops a
 * batch at the failing command without rolling back what came before, so a
 * refused alignment leaves the number landed. Unlike the plain path, though,
 * the paragraph is created by a SEPARATE command from its content, so a refused
 * package would leave an empty paragraph behind — the failure path reads which
 * of the two happened rather than assuming.
 */
async function writeOffsetPageNumber(
  context: Word.RequestContext,
  region: Word.Body,
  ooxml: string,
  alignment: PageNumberAlignment,
  probe: boolean,
): Promise<boolean> {
  const para = region.insertParagraph("", Word.InsertLocation.end);
  const wanted = optionalAlignmentValue(alignment);
  try {
    para.insertOoxml(ooxml, Word.InsertLocation.end);
    if (wanted !== undefined) para.alignment = wanted;
    await context.sync();
  } catch (error) {
    const landed = await offsetPackageLanded(context, para);
    if (landed === true && wanted !== undefined) {
      // The package is in; only the alignment is outstanding.
      console.warn("[word] page-number alignment rejected at sync; applying it separately", error);
      await alignPageNumberParagraph(context, para, alignment);
    } else {
      // The write failed for a reason other than the alignment. Take back the
      // paragraph that did land — empty, or holding a number the caller is about
      // to report as not added — so the footer is left as found; a host that
      // rolled the batch back (null) has nothing to take back.
      if (landed !== null) {
        para.delete();
        await context.sync();
      }
      if (!probe) throw error;
      console.warn("[word] page-number start offset rejected by the host", error);
      return false;
    }
  }
  if (!probe) return true;
  para.load("text");
  await context.sync();
  if (looksLikeFieldError(para.text ?? "")) {
    // A visible "!Undefined Bookmark, PAGE" in the footer of a document about to
    // be sent out is worse than no page number at all.
    console.warn("[word] page-number start offset rendered as field error", para.text);
    para.delete();
    await context.sync();
    return false;
  }
  return true;
}

/**
 * Queue the page-number paragraph and its field(s) onto `region`. Does NOT sync.
 *
 * Split out from `writePlainPageNumber` (AIVA-8181) so that what gets QUEUED is
 * separable from when it is committed — the caller now adds the alignment to
 * this same batch, and keeping the field writes in one named place is what
 * makes that ordering legible.
 */
function queuePlainPageNumber(
  region: Word.Body,
  format: PageNumberFormat,
  picture: string | undefined,
): Word.Paragraph {
  const para = region.insertParagraph("", Word.InsertLocation.end);
  // Every call is made on the SAME range object, appending at its end.
  // Deliberately not chained off the return values of insertText or
  // field.result: those are not present on every host (the TOC path already
  // has to guard field.updateResult for the same reason), and a chain that
  // breaks mid-way would leave a half-written footer.
  const range = para.getRange(Word.RangeLocation.whole);
  if (format === "with_total") {
    range.insertText("Page ", Word.InsertLocation.replace);
    range.insertField(Word.InsertLocation.end, Word.FieldType.page, picture, false);
    range.insertText(" of ", Word.InsertLocation.end);
    // NUMPAGES carries the SAME switch as PAGE: "Page iii of 12" mixes two
    // numbering systems in one line, which reads as a bug to the user.
    range.insertField(Word.InsertLocation.end, Word.FieldType.numPages, picture, false);
  } else {
    // Materialise the range BEFORE the field, then append — the exact shape
    // both working field writes use (the with_total branch above, and
    // insertWordBates, which stamps a PAGE field into this same footer). This
    // branch used to insert straight onto the range of a freshly-inserted
    // EMPTY paragraph, and Word answered GeneralException on that, failing the
    // sync and leaving the document untouched.
    range.insertText("", Word.InsertLocation.replace);
    range.insertField(Word.InsertLocation.end, Word.FieldType.page, picture, false);
  }
  return para;
}

/**
 * The Office.js alignment value, or undefined on a host that has no such enum.
 *
 * `wordAlignmentFor` reads `Word.Alignment.left`, so a host without the enum
 * throws a TypeError SYNCHRONOUSLY — before anything is queued. That is the
 * cheap half of the two failure modes AIVA-7862 guards, and catching it here
 * means it costs no sync of its own: the alignment is simply never queued.
 */
function optionalAlignmentValue(alignment: PageNumberAlignment): Word.Alignment | undefined {
  try {
    return wordAlignmentFor(alignment);
  } catch (error) {
    console.warn("[word] host has no paragraph alignment; leaving the page number unaligned", error);
    return undefined;
  }
}

/**
 * Append the plain (start-at-1) page-number paragraph to one region.
 *
 * The counterpart to `writeOffsetPageNumber`: a live PAGE field, or
 * "Page {PAGE} of {NUMPAGES}", built with `insertField` — all the default case
 * needs, and the path every request took before custom starts existed.
 *
 * ONE mutating sync on the happy path (AIVA-8181). Word puts one undo record on
 * the stack per mutating sync, so the previous two-sync shape — field, then
 * alignment — cost the user TWO Ctrl+Z presses: the first only reverted the
 * centring and left the number sitting in the footer, now wrongly positioned.
 * A single action the user asked for has to undo as a single action.
 *
 * The alignment is queued LAST, and that ordering is load-bearing rather than
 * cosmetic. Word applies a batch up to the failing command, so if the host
 * rejects the alignment the paragraph and its field have already landed and
 * only the alignment is missing — which the catch then applies on its own,
 * exactly as the old split path did. AIVA-7862's guarantee is therefore kept in
 * full: the number always lands, and the worst case is that it lands centred.
 * Queued FIRST it would be the opposite — the rejection would take the field
 * down with it, which is the defect AIVA-7862 was raised for.
 */
async function writePlainPageNumber(
  context: Word.RequestContext,
  region: Word.Body,
  format: PageNumberFormat,
  picture: string | undefined,
  alignment: PageNumberAlignment,
): Promise<void> {
  const para = queuePlainPageNumber(region, format, picture);
  const wanted = optionalAlignmentValue(alignment);
  if (wanted !== undefined) para.alignment = wanted;

  try {
    // Sync per region, not once at the end: the shared-region check in the
    // caller can only see a previous write if that write has been committed.
    await context.sync();
    return;
  } catch (error) {
    // With no alignment queued there is nothing here for us to have caused, so
    // this is an ordinary write failure and belongs with the caller.
    if (wanted === undefined) throw error;
    console.warn("[word] page-number alignment rejected at sync; applying it separately", error);
  }

  // Office.js runs a batch in order and stops at the failing command; it does
  // not roll back the commands before it. That is the same property the field
  // chain above already depends on ("a chain that breaks mid-way would leave a
  // half-written footer"), so with the alignment queued LAST the paragraph and
  // its field have landed and only the alignment is missing. Applying it on its
  // own is precisely what the pre-AIVA-8181 two-sync path did, so a host that
  // refuses it twice ends exactly where it used to: numbered, not aligned.
  await alignPageNumberParagraph(context, para, alignment);
}

/** What to write into each region — the plain field, or a custom-start package. */
interface PageNumberWriteSpec {
  readonly format: PageNumberFormat;
  readonly picture: string | undefined;
  readonly alignment: PageNumberAlignment;
  /** OOXML for a start above 1, or null for the ordinary PAGE field. */
  readonly offsetPackage: string | null;
}

/**
 * Write one region, whichever mechanism the request needs.
 *
 * Returns false only when the offset probe failed and the region was left
 * exactly as found — the plain path cannot fail this way, it throws instead.
 */
async function writePageNumberRegion(
  context: Word.RequestContext,
  region: Word.Body,
  spec: PageNumberWriteSpec,
  probe: boolean,
): Promise<boolean> {
  if (spec.offsetPackage === null) {
    await writePlainPageNumber(context, region, spec.format, spec.picture, spec.alignment);
    return true;
  }
  return writeOffsetPageNumber(context, region, spec.offsetPackage, spec.alignment, probe);
}

/**
 * Does this region already carry a page number, or is it one another section
 * has already been written to?
 *
 * Sections whose footer is "Link to Previous" share one underlying region, so
 * writing per-section would stack a page number per section into the same
 * footer. Two independent signals guard against that:
 *
 *   1. text — the region already holds a page-number paragraph. Also makes a
 *      second run idempotent instead of double-stamping.
 *   2. count — the region has MORE paragraphs than its baseline, which can
 *      only mean an earlier iteration wrote into it, i.e. it is the same
 *      shared region.
 *
 * Signal 2 matters because it does not depend on a rendered field surfacing as
 * text, which is not guaranteed on every host. It costs a re-read, so it is
 * only checked once something has actually been written.
 */
async function regionAlreadyNumbered(
  context: Word.RequestContext,
  region: Word.Body,
  preNumbered: boolean,
  baselineCount: number,
  anyWritten: boolean,
): Promise<boolean> {
  if (preNumbered) return true;
  if (!anyWritten) return false;
  region.paragraphs.load("items/text");
  await context.sync();
  return region.paragraphs.items.length > baselineCount;
}

/**
 * Insert live page-number fields into every section's footer (or header).
 *
 * ``simple`` inserts a PAGE field; ``with_total`` inserts "Page {PAGE} of
 * {NUMPAGES}". Existing content in the region is **preserved** — the fields are
 * appended on their own paragraph rather than clearing the region, because
 * clearing is what destroys letterhead and confidentiality legends (G-12) and
 * what makes page numbers and Bates stamps overwrite each other (G-14).
 *
 * ``numeralStyle`` selects the rendered numeral via a picture switch on the
 * field itself (AIVA-7863), so Word keeps recalculating it — a Roman page
 * number is still a live field, not literal text.
 *
 * ``startAt`` above 1 numbers the first page from that value (AIVA-7867). It
 * takes a different write path — a nested formula field, delivered as OOXML,
 * because `insertField` cannot express one — and that path is PROVED on the
 * first region before any other is touched: if the host renders it as field-
 * error text the paragraph is removed and the whole run reports
 * ``offset_unsupported``. Nothing falls back to numbering from 1, because a
 * bundle that silently starts at 1 when 500 was asked for is the defect this
 * fixes, not a graceful degradation.
 *
 * Support is probed before the first write, so an unsupported host leaves the
 * document untouched instead of half-edited.
 *
 * ``skipFrontMatter`` leaves the contents pages unnumbered and numbers the
 * first page AFTER them 1 — or ``startAt``, which rides along with it (AIVA-7869
 * with AIVA-7867). Where the body starts on the same page the contents ends,
 * that page counts as front matter and stays blank — see
 * `FRONT_MATTER_END_BOOKMARK`. It takes the same OOXML write path as a custom
 * start, and it OVERRIDES ``format`` and ``numeralStyle``: see
 * `pageNumberSkipFrontMatterOoxml` for why those two cannot be honoured at the
 * same time, and the caller for how it says so. It also refuses outright when
 * the front-matter boundary cannot be found, rather than numbering the whole
 * document and letting that look like the request was applied.
 */
export async function insertWordPageNumbers(
  format: PageNumberFormat = "simple",
  position: PageNumberPosition = "footer",
  alignment: PageNumberAlignment = "center",
  numeralStyle: PageNumberNumeralStyle = "arabic",
  startAt: number = PAGE_NUMBER_START_MIN,
  skipFrontMatter = false,
): Promise<PageNumberResult> {
  if (!supportsFieldInsertion()) {
    return { applied: false, sections: 0, reason: "unsupported_host" };
  }
  // Built once: identical for every region, and building it inside the loop
  // would rebuild the same 1 KB package per section.
  const spec: PageNumberWriteSpec = {
    format,
    picture: pageNumberPictureSwitch(numeralStyle),
    alignment,
    offsetPackage: skipFrontMatterOrOffsetPackage(skipFrontMatter, format, alignment, numeralStyle, startAt),
  };
  try {
    return await Word.run(async (context) => {
      // FIRST, before a single region is touched: a document whose front-matter
      // boundary cannot be found must come out completely unchanged.
      // The bookmark is only FOUND here; it is written with the first footer
      // write, so the whole insert stays one undo record (AIVA-8181).
      let pendingBookmark: Word.Range | null = null;
      if (skipFrontMatter) {
        const found = await findFrontMatterEnd(context);
        if (typeof found === "string") return { applied: false, sections: 0, reason: found };
        pendingBookmark = found;
      }

      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      const regions = sections.items.map((s) => regionOf(s, position));
      for (const region of regions) region.paragraphs.load("items/text");
      // Folded into the SAME sync as the paragraph load, so the skip path costs
      // no extra round trip and every other path's sync count is untouched —
      // the alignment tests pin those exactly.
      if (skipFrontMatter) for (const region of regions) loadRegionFieldCodes(region);
      await context.sync();

      // Baseline paragraph counts, captured BEFORE any write — see
      // regionAlreadyNumbered for what the two signals are guarding against.
      const baseline = regions.map((r) => r.paragraphs.items.length);
      const preNumbered = regions.map(
        (r) =>
          r.paragraphs.items.some((p) => isPageNumberParagraph(p.text ?? "")) ||
          // A skip field renders blank on the contents page, so its own
          // re-run guard has to read the field code, not the text.
          (skipFrontMatter && regionCarriesSkipField(r)),
      );

      let written = 0;
      let skipped = 0;
      for (let i = 0; i < regions.length; i += 1) {
        const region = regions[i]!;
        if (await regionAlreadyNumbered(context, region, preNumbered[i]!, baseline[i]!, written > 0)) {
          skipped += 1;
          continue;
        }
        // `written === 0` makes the FIRST region the offset probe: a host that
        // cannot render the nested field is found before anything else is
        // touched, so the document is left completely unchanged rather than
        // half-stamped.
        //
        // The bookmark is queued immediately before it, AFTER every read above,
        // so the write sync is the first one to carry it. Queued any earlier, a
        // read sync would commit it on its own.
        if (pendingBookmark) {
          queueFrontMatterBookmark(pendingBookmark);
          pendingBookmark = null;
        }
        const ok = await writePageNumberRegion(context, region, spec, written === 0);
        if (!ok) return { applied: false, sections: 0, skipped, reason: "offset_unsupported" };
        written += 1;
      }
      // Every region was already numbered: nothing carried the bookmark, but a
      // re-run still has to move it to where the contents now ends — that was
      // always part of what a re-run did, and here it is the only change made.
      if (pendingBookmark) {
        queueFrontMatterBookmark(pendingBookmark);
        await context.sync();
      }
      return { applied: written > 0, sections: written, skipped };
    });
  } catch (error) {
    // The user-facing message says only "I couldn't add page numbers", which is
    // right for chat and useless for diagnosis — an Office.js RichApi error
    // carries the code and the failing operation, and swallowing it meant every
    // report of this arrived with nothing to act on.
    console.error("[word] insertWordPageNumbers failed", error);
    return { applied: false, sections: 0, reason: "failed", errorDetail: describeWordError(error) };
  }
}

/**
 * Remove page-number paragraphs previously inserted into each section's region.
 *
 * Only paragraphs whose text is a bare page number ("3", "Page 3 of 12") are
 * removed, so a letterhead or legend sharing the region survives.
 */
export async function removeWordPageNumbers(
  position: PageNumberPosition = "footer",
): Promise<PageNumberResult> {
  return removeRegionParagraphs(
    position,
    // Empty paragraphs in the region are swept too, and only real page-number
    // text counts as a removal — EXCEPT in a region carrying a
    // skip-front-matter field, where blank IS the page number's rendered result
    // and not counting it would delete the numbering and then report that there
    // was none to delete.
    (text, region) => ({
      remove: !text || isPageNumberParagraph(text),
      count: Boolean(text) || regionCarriesSkipField(region),
    }),
    "word-remove-page-numbers",
  );
}


export async function hasWordPageNumbers(
  position: PageNumberPosition = "footer",
): Promise<boolean | null> {
  try {
    return await Word.run(async (context) => {
      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      const regions = sections.items.map((s) => regionOf(s, position));
      for (const region of regions) {
        region.paragraphs.load("items/text");
        // Same batch, no extra round trip. A skip-front-matter field renders
        // blank, so without its code this read reports a numbered document as
        // unnumbered — which would make the change-log verifier drop the entry
        // for a page-number write that did land (AIVA-7869).
        loadRegionFieldCodes(region);
      }
      await context.sync();

      return regions.some(
        (r) =>
          r.paragraphs.items.some((p) => isPageNumberParagraph(p.text ?? "")) ||
          regionCarriesSkipField(r),
      );
    });
  } catch (err) {
    logOfficeError(err, "word-has-page-numbers");
    return null;
  }
}


export async function hasWordBates(
  prefix: string,
  position: PageNumberPosition = "footer",
): Promise<boolean | null> {
  const survey = await surveyWordRegions(position);
  if (survey === null) return null;
  const stampFor = new RegExp(String.raw`${escapeRegExp(prefix)}[-_ ]?\d+`, "i");
  return survey.occupied.some((r) => stampFor.test(r.text));
}

// ---------------------------------------------------------------------------
// Hyperlink (DP-04)
// ---------------------------------------------------------------------------

/** Schemes that may be written into a document. Anything else is refused. */
const SAFE_URL_SCHEME = /^https?:\/\//i;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Normalise a URL for insertion, or return null when it must not be written.
 *
 * A bare `example.com/note` gets `https://` (the spec's AC-1) and an existing
 * scheme is never double-prefixed (AC-4). `javascript:` and `data:` URLs are
 * REFUSED rather than normalised: a clickable link in a document a colleague
 * will open is an execution vector, and no prompt should be able to place one.
 */
export function normaliseUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!HAS_SCHEME.test(trimmed)) return `https://${trimmed}`;
  return SAFE_URL_SCHEME.test(trimmed) ? trimmed : null;
}

/** Is `Range.hyperlink` (WordApi 1.3) available on this host? */
export function supportsHyperlink(): boolean {
  try {
    return Office.context.requirements.isSetSupported("WordApi", "1.3");
  } catch {
    return false;
  }
}

export interface HyperlinkResult {
  readonly inserted: boolean;
  /** The normalised URL that was written. */
  readonly url?: string;
  /** The visible text the link carries. */
  readonly text?: string;
  readonly reason?: "unsupported_host" | "invalid_url" | "failed";
}

/**
 * Insert a hyperlink at the selection (or turn the selection into one).
 *
 * Display-text precedence follows the specification: an explicit `displayText`
 * wins; otherwise the selected text becomes the link text and is NOT duplicated
 * (AC-2); with nothing selected and no text given, the URL itself is visible
 * (AC-3).
 */
export async function insertWordHyperlink(
  url: string,
  displayText = "",
): Promise<HyperlinkResult> {
  const safeUrl = normaliseUrl(url);
  if (!safeUrl) return { inserted: false, reason: "invalid_url" };
  if (!supportsHyperlink()) return { inserted: false, reason: "unsupported_host" };
  try {
    return await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.load("text");
      await context.sync();

      const selected = (selection.text ?? "").trim();
      const label = displayText.trim() || selected || safeUrl;
      // With a selection and no explicit label, hyperlink the selection in
      // place; otherwise write the label first and link what was written.
      const target =
        selected && !displayText.trim()
          ? selection
          : selection.insertText(label, Word.InsertLocation.replace);
      target.hyperlink = safeUrl;
      await context.sync();
      return { inserted: true, url: safeUrl, text: label };
    });
  } catch {
    return { inserted: false, reason: "failed" };
  }
}


// ---------------------------------------------------------------------------
// Paste an email into the document (DP-05)
// ---------------------------------------------------------------------------

/** Outcome of an email insertion. `reason` is set only on the failure paths. */
export interface InsertEmailResult {
  inserted: boolean;
  reason?: "empty_body" | "failed";
  /** Paragraphs written, preface included — what the toast reports. */
  paragraphs?: number;
  /** Some links, pictures or list formatting could not be applied; the text is complete. */
  formattingSkipped?: boolean;
}

/** Does `lines` (already blank-stripped) open with a markdown pipe table? */
function looksLikeMarkdownTable(lines: string[]): boolean {
  return lines.length >= 2 && (lines[0] ?? "").startsWith("|") && TABLE_SEP_RE.test(lines[1] ?? "");
}

/** Word's own hard limit — `insertTable` throws on anything wider. */
const WORD_TABLE_MAX_COLUMNS = 63;

/**
 * Is a block that PARSED as a pipe table one Word will actually accept?
 *
 * A mail body is not markdown, so a block can look like a table without being
 * one: a sanitiser that flattened nested layout tables leaves rows of stray
 * pipes and `---` fragments that parse as a table of nonsense shape. Handing
 * one to `insertTable` throws, and the throw aborts the whole insert — which
 * is what used to surface as "the document may be protected or read-only".
 * Anything failing these checks is written as plain text instead: ugly pipes
 * beat losing the email.
 *
 *  * Width — Word rejects more than 63 columns outright.
 *  * Separator agreement — a genuine markdown table's `| --- | --- |` row has
 *    exactly as many cells as its header row. A flattened layout table does
 *    not (a seven-column header under a one-column separator), and that
 *    disagreement is the clearest signal that the pipes are content rather
 *    than structure.
 */
function isInsertableEmailTable(block: { headers: string[] }, lines: string[]): boolean {
  const columns = block.headers.length;
  if (columns === 0 || columns > WORD_TABLE_MAX_COLUMNS) return false;
  const separatorCells = (lines[1] ?? "").trim().split("|").slice(1, -1).length;
  return separatorCells === columns;
}

// ---------------------------------------------------------------------------
// Email-body inline formatting (DP-05)
// ---------------------------------------------------------------------------
//
// `sanitise_email_body` (backend) turns a mail's bold/italic/underline/mark
// tags into `[[B]]`/`[[I]]`/`[[U]]`/`[[H:name]]` markers rather than stripping
// them outright — plain-text markers carry no active content (no script, no
// fetchable URL, no attribute an event handler could hide in), so preserving
// them does not reopen the sanitiser's security guarantee. This is a CLOSED,
// ad-hoc vocabulary the backend alone emits — not general markdown — chosen
// so it can't collide with genuine prose the way `**`/`*`/`_` could (a sender
// writing "please read **carefully**" as literal emphasis would otherwise be
// reinterpreted). What follows turns those markers into real Word formatting
// on the way in; the markers themselves are never written to the document.

/** Exported for direct unit testing — a pure tokeniser needs no Word mock. */
export interface EmailRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** A `#RRGGBB` hex string for `range.font.highlightColor`, always one of
   *  Word's own 15 highlight palette colours (e.g. "#FFFF00" for yellow) —
   *  never an arbitrary CSS value or a highlight NAME. See
   *  `EMAIL_HIGHLIGHT_HEX_BY_NAME` for why hex rather than a name. */
  highlightColor?: string;
  /** A `#rrggbb` hex string for `range.font.color` — the run's own text colour,
   *  as opposed to `highlightColor`'s background. Word's `font.color` takes any
   *  hex string directly, unlike the highlight enum, so this is never snapped
   *  to a fixed palette. */
  color?: string;
  strike?: boolean;
  /** An http(s)/mailto URL the run links to (`[[L:url]]`). */
  href?: string;
  /** Content id of an embedded image (`[[IMG:cid]]`); the run has no text. */
  image?: string;
}

/** Written where an embedded image's bytes are unavailable, as for a remote image. */
const EMAIL_IMAGE_PLACEHOLDER = "[Image]";

/** Canonical lowercase highlight name (matches the backend's `_HIGHLIGHT_RGB`
 *  keys) → the `#RRGGBB` value of that Word palette colour.
 *
 *  Hex, deliberately, not a colour NAME. `font.highlightColor` accepts a hex
 *  string or one of exactly fifteen documented names — "Yellow", "Lime",
 *  "Turquoise", "Pink", "Blue", "Red", "DarkBlue", "Teal", "Green", "Purple",
 *  "DarkRed", "Olive", "Gray", "LightGray", "Black" — and those are NOT the
 *  ribbon's own labels: Word's "Bright Green" is the API's "Lime", "Violet" is
 *  "Purple", "Dark Yellow" is "Olive", "Gray 25%" is "LightGray". This table
 *  used to send the ribbon spellings ("BrightGreen", "Violet", "Gray25"…);
 *  Word for Windows snaps an unknown value to the nearest colour, but Word on
 *  the web rejects it, and because the host applies the queued batch
 *  incrementally the rejection aborted the whole paste at the first such run
 *  — everything after a bright-green highlight was simply never written.
 *  Microsoft's own sample for this property sets hex, and every value here is
 *  an exact palette colour, so no host has anything to snap or refuse. */
const EMAIL_HIGHLIGHT_HEX_BY_NAME: Readonly<Record<string, string>> = {
  yellow: "#FFFF00",
  brightgreen: "#00FF00",
  green: "#008000",
  turquoise: "#00FFFF",
  cyan: "#00FFFF",
  pink: "#FF00FF",
  magenta: "#FF00FF",
  blue: "#0000FF",
  red: "#FF0000",
  darkblue: "#000080",
  teal: "#008080",
  violet: "#800080",
  purple: "#800080",
  darkred: "#800000",
  darkyellow: "#808000",
  gray25: "#C0C0C0",
  gray50: "#808080",
  black: "#000000",
};

// `[[C:#rrggbb]]...[[/C]]` — a text-colour marker, alongside `[[H:name]]`.
// `[[S]]`, `[[L:url]]`, `[[IMG:cid]]` and the block-level `[[LI:…]]` share the shape.
//
// ONE shape for every marker — an optional "/", one of the kinds above,
// an optional ":value" — with the value checked per kind in `emailMarkerFrom`
// rather than spelled out here. The previous pattern listed the three kinds as
// alternatives with six named groups, which put it over Sonar's regex
// complexity ceiling (S5843) and read those groups through `m.groups`, where
// static analysis could not see them used (S5860). Behaviour is unchanged: a
// value on a toggle, a highlight name with punctuation, or a colour that is
// not six hex digits is NOT a marker and stays in the text exactly as before.
const EMAIL_MARKER_RE = /\[\[(\/?)(B|I|U|S|H|C|L|IMG|LI)(?::([^\]]+))?\]\]/g;
const EMAIL_HIGHLIGHT_NAME_RE = /^[a-zA-Z0-9]+$/;
const EMAIL_HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const EMAIL_LINK_RE = /^(?:https?:\/\/|mailto:)[^\s[\]<>"']+$/i;
const EMAIL_CONTENT_ID_RE = /^[A-Za-z0-9._%+@-]+$/;
const EMAIL_LIST_VALUE_RE = /^([BN])([1-9])([aAiI])?$/;
/** Value check per marker kind; a kind absent here takes no value. */
const EMAIL_MARKER_VALUE_RE: Readonly<Partial<Record<EmailMarker["kind"], RegExp>>> = {
  H: EMAIL_HIGHLIGHT_NAME_RE,
  C: EMAIL_HEX_COLOR_RE,
  L: EMAIL_LINK_RE,
  IMG: EMAIL_CONTENT_ID_RE,
  LI: EMAIL_LIST_VALUE_RE,
};

/** One recognised marker: its kind, whether it closes, and its value — a
 *  highlight name for H, a `#rrggbb` for C, a URL for L, a content id for IMG,
 *  a list descriptor for LI; never present for B/I/U/S. */
interface EmailMarker {
  kind: "B" | "I" | "U" | "S" | "H" | "C" | "L" | "IMG" | "LI";
  closing: boolean;
  value?: string;
}

/** The marker a regex match denotes, or null when the match is not one of
 *  ours and must be left in the text. */
function emailMarkerFrom(m: RegExpExecArray): EmailMarker | null {
  const kind = m[2] as EmailMarker["kind"] | undefined;
  const value = m[3];
  if (!kind) return null;
  const closing = m[1] === "/";
  // IMG and LI always carry a value and never close.
  if ((kind === "IMG" || kind === "LI") && (closing || value === undefined)) return null;
  if (value !== undefined && !EMAIL_MARKER_VALUE_RE[kind]?.test(value)) return null;
  return { kind, closing, ...(value !== undefined ? { value } : {}) };
}

/** Mutable in-progress formatting state threaded through one `parseEmailMarkupRuns` pass. */
interface EmailMarkupState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  highlightStack: string[];
  colorStack: string[];
  hrefStack: string[];
}

/** Appends `text.slice(start, end)` as one formatted run, per the state's current toggles. No-op on an empty slice. */
function flushEmailRun(text: string, start: number, end: number, state: EmailMarkupState, runs: EmailRun[]): void {
  if (end <= start) return;
  const chunk = text.slice(start, end);
  if (!chunk) return;
  const highlightTop = state.highlightStack[state.highlightStack.length - 1];
  const highlightColor = highlightTop ? EMAIL_HIGHLIGHT_HEX_BY_NAME[highlightTop] : undefined;
  const color = state.colorStack[state.colorStack.length - 1];
  const href = state.hrefStack[state.hrefStack.length - 1];
  runs.push({
    text: chunk,
    ...(state.bold ? { bold: true } : {}),
    ...(state.italic ? { italic: true } : {}),
    ...(state.underline ? { underline: true } : {}),
    ...(state.strike ? { strike: true } : {}),
    ...(highlightColor ? { highlightColor } : {}),
    ...(color ? { color } : {}),
    ...(href ? { href } : {}),
  });
}

/** Applies an H, C or L marker to its stack: a closer pops, an opener with a
 *  value pushes, a bare opener is a no-op. URLs keep their case. */
function applyStackMarker(marker: EmailMarker, stack: string[]): void {
  if (marker.closing) {
    if (stack.length) stack.pop();
  } else if (marker.value) {
    stack.push(marker.kind === "L" ? marker.value : marker.value.toLowerCase());
  }
}

/** Applies one recognised marker to the running state; IMG appends its own run, LI is ignored. */
function applyEmailMarker(marker: EmailMarker, state: EmailMarkupState, runs: EmailRun[]): void {
  const on = !marker.closing;
  if (marker.kind === "B") state.bold = on;
  else if (marker.kind === "I") state.italic = on;
  else if (marker.kind === "U") state.underline = on;
  else if (marker.kind === "S") state.strike = on;
  else if (marker.kind === "H") applyStackMarker(marker, state.highlightStack);
  else if (marker.kind === "C") applyStackMarker(marker, state.colorStack);
  else if (marker.kind === "L") applyStackMarker(marker, state.hrefStack);
  else if (marker.kind === "IMG") runs.push({ text: "", image: marker.value });
}

/** Tokenises one line of marker-annotated text into runs Word can format directly.
 *  Tolerant of an unbalanced/truncated marker (the backend's own char cap can
 *  cut a body mid-marker) — an unclosed tag simply carries to the end of the
 *  text rather than throwing. */
export function parseEmailMarkupRuns(text: string): EmailRun[] {
  const runs: EmailRun[] = [];
  const state: EmailMarkupState = {
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    highlightStack: [],
    colorStack: [],
    hrefStack: [],
  };
  let lastIndex = 0;
  EMAIL_MARKER_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  let sawMarker = false;
  while ((m = EMAIL_MARKER_RE.exec(text))) {
    const marker = emailMarkerFrom(m);
    if (!marker) continue; // not one of ours — it stays in the text like any other bracketed words
    sawMarker = true;
    flushEmailRun(text, lastIndex, m.index, state, runs);
    lastIndex = EMAIL_MARKER_RE.lastIndex;
    applyEmailMarker(marker, state, runs);
  }
  flushEmailRun(text, lastIndex, text.length, state, runs);
  // No markers at all → the single untouched run (the overwhelming common
  // case). Markers that bracketed nothing visible legitimately produce no
  // runs — falling back to the raw text here would paste the literal marker
  // syntax into the document instead of nothing.
  return sawMarker ? runs : [{ text }];
}

function hasEmailFormatting(runs: EmailRun[]): boolean {
  return runs.some(
    (r) => r.bold || r.italic || r.underline || r.strike || r.highlightColor || r.color || r.href || r.image !== undefined,
  );
}

function applyEmailRunFont(range: Word.Range, run: EmailRun): void {
  try {
    if (run.bold) range.font.bold = true;
    if (run.italic) range.font.italic = true;
    if (run.underline) range.font.underline = Word.UnderlineType.single;
    if (run.strike) range.font.strikeThrough = true;
    if (run.highlightColor) range.font.highlightColor = run.highlightColor;
    if (run.color) range.font.color = run.color;
  } catch { /* formatting not settable on this host */ }
}

export interface InsertEmailOptions {
  /** Base64 image bytes keyed by the content id in `[[IMG:cid]]`. */
  images?: Readonly<Record<string, string>>;
}

/**
 * Per-paste state. `deferred` holds the writes the text does not depend on —
 * hyperlinks, pictures, list level formats — which run in a second batch after
 * the text has synced, so a host rejecting one cannot cut the paste short.
 */
interface EmailInsertContext {
  images: Readonly<Record<string, string>>;
  /** WordApi 1.3: `Range.hyperlink` and the list API. */
  api13: boolean;
  deferred: Array<() => void>;
  /** The body needed WordApi 1.3 and the host lacks it, so fallbacks were used. */
  needsApi13: boolean;
}

/**
 * Write `runs` into `p`, one `insertText` per run, each chained off the
 * paragraph's current end. Without WordApi 1.3 a link's URL is written after
 * its text. An image is written as "[Image]" and replaced by the picture in the
 * deferred phase.
 */
function appendEmailRuns(p: Word.Paragraph, runs: EmailRun[], ctx: EmailInsertContext): void {
  for (const run of runs) {
    if (run.image !== undefined) {
      const base64 = ctx.images[run.image];
      const slot = p.getRange(Word.RangeLocation.end).insertText(EMAIL_IMAGE_PLACEHOLDER, Word.InsertLocation.end);
      if (base64) ctx.deferred.push(() => slot.insertInlinePictureFromBase64(base64, Word.InsertLocation.replace));
      continue;
    }
    if (run.href && !ctx.api13) ctx.needsApi13 = true;
    const showTarget = run.href && !ctx.api13 && run.text.trim() !== run.href;
    const text = showTarget ? `${run.text} (${run.href})` : run.text;
    const inserted = p.getRange(Word.RangeLocation.end).insertText(text, Word.InsertLocation.end);
    applyEmailRunFont(inserted, run);
    const href = run.href;
    if (href && ctx.api13) ctx.deferred.push(() => { inserted.hyperlink = href; });
  }
}

/**
 * Insert one paragraph made of several differently-formatted runs.
 *
 * `queueBlock`'s paragraph path (used for everything else this file inserts)
 * sets ONE font for the whole paragraph, because model-authored and outline
 * content never needs more. A mail body routinely does — "please read
 * **Section 4** *carefully*" bolds and italicises different words on the same
 * line — so each run is inserted and formatted individually, chained off the
 * previous run's end exactly the way `queueBlock` chains paragraphs off each
 * other, with no sync between runs.
 */
function insertRichEmailParagraph(anchor: EmailAnchor, runs: EmailRun[], style: string, ctx: EmailInsertContext): Word.Paragraph {
  const p = insertEmptyEmailParagraph(anchor);
  applyBlockStyle(p, style);
  appendEmailRuns(p, runs, ctx);
  return p;
}

type EmailAnchor = { kind: "range"; range: Word.Range } | { kind: "after"; prev: WordInsertable };

function insertEmptyEmailParagraph(anchor: EmailAnchor): Word.Paragraph {
  return anchor.kind === "range"
    ? anchor.range.insertParagraph("", Word.InsertLocation.after)
    : anchor.prev.insertParagraph("", Word.InsertLocation.after);
}

// ---------------------------------------------------------------------------
// Email-body lists (DP-05)
// ---------------------------------------------------------------------------

interface EmailListItem {
  bullet: boolean;
  /** 0-based, as `Word.ListItem.level` counts. */
  level: number;
  numbering: Word.ListNumbering;
}

const EMAIL_LIST_MARKER_RE = /^\[\[LI:([BN])([1-9])([aAiI])?\]\]/;

/** `<ol type>` letter → Word numbering. A function, not a table: `Word` is not defined at module load. */
function emailListNumbering(letter: string | undefined): Word.ListNumbering {
  switch (letter) {
    case "a": return Word.ListNumbering.lowerLetter;
    case "A": return Word.ListNumbering.upperLetter;
    case "i": return Word.ListNumbering.lowerRoman;
    case "I": return Word.ListNumbering.upperRoman;
    default: return Word.ListNumbering.arabic;
  }
}

/** The list item a block opens with and the text after its marker, or null. */
function emailListItemFrom(block: string): { item: EmailListItem; text: string } | null {
  const m = EMAIL_LIST_MARKER_RE.exec(block);
  if (!m) return null;
  return {
    item: { bullet: m[1] === "B", level: Number(m[2]) - 1, numbering: emailListNumbering(m[3]) },
    text: block.slice(m[0].length),
  };
}

/** The list being built, and which of its levels have had their format queued. */
interface EmailListState {
  list: Word.List;
  levels: Set<number>;
  rootBullet: boolean;
}

/**
 * Insert one list item as a real Word list paragraph (WordApi 1.3). The first
 * item calls `startNewList` so each list numbers from 1; later items append to
 * it. Level formats and nesting are deferred.
 */
async function insertEmailListItem(
  context: Word.RequestContext,
  anchor: EmailAnchor,
  item: EmailListItem,
  runs: EmailRun[],
  state: EmailListState | null,
  ctx: EmailInsertContext,
): Promise<{ paragraph: Word.Paragraph; state: EmailListState }> {
  let p: Word.Paragraph;
  let current = state;
  if (current === null) {
    p = insertEmptyEmailParagraph(anchor);
    applyBlockStyle(p, "Normal");
    // startNewList throws on a paragraph that is already a list item.
    await detachIfListItem(context, p);
    current = { list: p.startNewList(), levels: new Set(), rootBullet: item.bullet };
  } else {
    p = current.list.insertParagraph("", Word.InsertLocation.end);
  }
  if (!current.levels.has(item.level)) {
    const { list } = current;
    ctx.deferred.push(
      item.bullet
        ? () => list.setLevelBullet(item.level, Word.ListBullet.solid)
        : () => list.setLevelNumbering(item.level, item.numbering, [item.level, "."]),
    );
    current.levels.add(item.level);
  }
  if (item.level > 0) ctx.deferred.push(() => { p.listItem.level = item.level; });
  appendEmailRuns(p, runs, ctx);
  return { paragraph: p, state: current };
}

/**
 * Detach `p` from its list only if it is in one. Whether a paragraph inserted
 * after a list item inherits the list depends on the host, and detaching a
 * non-member throws, so membership is read back first.
 */
async function detachIfListItem(context: Word.RequestContext, p: Word.Paragraph): Promise<void> {
  p.load("isListItem");
  await context.sync();
  if (p.isListItem) p.detachFromList();
}

function isWordParagraph(item: WordInsertable): item is Word.Paragraph {
  return "detachFromList" in item;
}

/** Built-in list style for a host without the WordApi 1.3 list API. */
function emailListFallbackStyle(item: EmailListItem): string {
  const base = item.bullet ? "List Bullet" : "List Number";
  const depth = Math.min(item.level + 1, 5);
  return depth > 1 ? `${base} ${depth}` : base;
}

/** Where the paste is up to: the last thing written, the open list, the count. */
interface EmailPasteState {
  prev: WordInsertable | null;
  list: EmailListState | null;
  written: number;
}

type EmailParagraphWriter = (text: string, style?: string) => WordInsertable | null;

interface EmailBlockWriters {
  anchor: () => EmailAnchor;
  insertBlock: (mdBlock: MdBlock) => WordInsertable;
  insertParagraphText: EmailParagraphWriter;
}

/** One `[[LI:…]]` block: a real list item on WordApi 1.3, a list-styled paragraph otherwise. */
async function insertEmailListBlock(
  context: Word.RequestContext,
  listItem: { item: EmailListItem; text: string },
  state: EmailPasteState,
  ctx: EmailInsertContext,
  writers: EmailBlockWriters,
): Promise<void> {
  const itemText = listItem.text.replace(/[^\S\n]*\n\s*/g, " ").trim();
  const runs = parseEmailMarkupRuns(itemText);
  if (runs.length === 0) return;
  if (!ctx.api13) {
    ctx.needsApi13 = true;
    const p = writers.insertParagraphText(itemText, emailListFallbackStyle(listItem.item));
    if (p) {
      state.prev = p;
      state.written += 1;
    }
    return;
  }
  const { item } = listItem;
  // A bullet list directly followed by a numbered one is two lists.
  const sameList = state.list !== null && (item.level > 0 || state.list.rootBullet === item.bullet);
  const inserted = await insertEmailListItem(context, writers.anchor(), item, runs, sameList ? state.list : null, ctx);
  state.list = inserted.state;
  state.prev = inserted.paragraph;
  state.written += 1;
}

/** Writes every block in document order; list boundaries need a round-trip each. */
async function insertEmailBlocks(
  context: Word.RequestContext,
  blocks: string[],
  state: EmailPasteState,
  ctx: EmailInsertContext,
  writers: EmailBlockWriters,
): Promise<void> {
  for (const block of blocks) {
    const listItem = emailListItemFrom(block.trim());
    if (listItem) {
      await insertEmailListBlock(context, listItem, state, ctx, writers); // NOSONAR - items must land in document order
      continue;
    }
    const { last, count } = insertEmailBlock(block, writers.insertBlock, writers.insertParagraphText);
    if (!last) continue;
    // The first paragraph after a list may have inherited its membership.
    if (state.list !== null && isWordParagraph(last)) await detachIfListItem(context, last); // NOSONAR - shared Word context, writes must stay ordered
    state.list = null;
    state.prev = last;
    state.written += count;
  }
}

/** Second phase: links, pictures, list formats. A rejection is reported, the text is already in. */
async function applyDeferredEmailFormatting(context: Word.RequestContext, ctx: EmailInsertContext): Promise<boolean> {
  if (ctx.deferred.length === 0) return ctx.needsApi13;
  try {
    for (const apply of ctx.deferred) apply();
    await context.sync();
    return ctx.needsApi13;
  } catch (error) {
    console.error("[word] insertWordEmailAtCursor: host rejected link/picture/list formatting", error);
    return true;
  }
}

/**
 * One block's contribution to `insertWordEmailAtCursor` — a markdown table,
 * the stray remainder after one, or a plain paragraph. Extracted so the
 * per-block loop there doesn't nest its own conditionals inside the `for`,
 * which is what pushed that function over Sonar's cognitive complexity limit.
 *
 * Returns the LAST item actually inserted (so the next block anchors after
 * it) and how many paragraphs this block wrote — `null`/`0` when the block
 * had nothing visible to insert.
 */
function insertEmailBlock(
  block: string,
  insertBlock: (mdBlock: MdBlock) => WordInsertable,
  insertParagraphText: (text: string) => WordInsertable | null,
): { last: WordInsertable | null; count: number } {
  const lines = block
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  // Only a block whose shape Word will actually accept is inserted as a table;
  // see isInsertableEmailTable for why a mail body needs that check at all.
  const parsed = looksLikeMarkdownTable(lines) ? parseTableBlock(lines, 0) : null;
  const table = parsed?.block?.kind === "table" && isInsertableEmailTable(parsed.block, lines) ? parsed : null;
  if (table?.block) {
    let last: WordInsertable = insertBlock(table.block);
    let count = 1;
    // A table block should consume every line — anything left over is
    // stray text the parser could not place, and is written as its own
    // paragraph rather than silently dropped.
    const rest = lines.slice(table.nextIdx).join(" ").trim();
    if (rest) {
      const restInserted = insertParagraphText(rest);
      if (restInserted) {
        last = restInserted;
        count += 1;
      }
    }
    return { last, count };
  }
  const paragraphText = lines.join(" ").trim();
  if (!paragraphText) return { last: null, count: 0 };
  const inserted = insertParagraphText(paragraphText);
  return inserted ? { last: inserted, count: 1 } : { last: null, count: 0 };
}

/**
 * Insert a mail body at the cursor behind its `From · date · Subject` preface
 * (DP-05, AC-1).
 *
 * Deliberately NOT routed through the full `parseMdBlocks` pipeline like the
 * other insert paths. That parser promotes `# ` to Heading 1 and `- ` to a
 * list, which is right for model-authored content and wrong for a mail body:
 * a quoted reply beginning "- - - Original message" or a line starting with
 * "#1" would silently restyle someone else's correspondence. Mail is inserted
 * as plain text — with two exceptions: a block the backend has already
 * rewritten as a markdown pipe table (`sanitise_email_body`'s table handling)
 * is recognised here and rendered as a real Word table via the same
 * `parseTableBlock`/`queueBlock` machinery `parseMdBlocks` uses, rather than
 * being flattened into one run-together line of pipes like ordinary prose
 * would be; and inline `[[B]]`/`[[I]]`/`[[U]]`/`[[H:name]]`/`[[C:#hex]]`
 * markers are turned into real formatting by `parseEmailMarkupRuns` rather
 * than reaching the document as literal marker text (AIVA-7736 — `[[C:#hex]]`
 * previously had no handling here at all, so a coloured run fell back to
 * plain black text; bold/italic/underline/highlight already worked).
 *
 * The preface is italicised so provenance is visually separable from the
 * document's own prose — a reader must be able to see where the pasted text
 * starts and who wrote it.
 *
 * Strikethrough, links, embedded images and lists are rendered too; links and
 * lists fall back gracefully on hosts without WordApi 1.3.
 */
export async function insertWordEmailAtCursor(
  preface: string,
  body: string,
  options: InsertEmailOptions = {},
): Promise<InsertEmailResult> {
  const text = body.trim();
  if (!text) return { inserted: false, reason: "empty_body" };

  // Blank lines separate blocks. Within an ORDINARY block, single newlines are
  // soft wraps from the mail client and are joined back up; a table block
  // keeps its row-by-row lines instead (see looksLikeMarkdownTable below).
  const blocks = text.split(/\n{2,}/).filter((b) => b.trim());
  const ctx: EmailInsertContext = {
    images: options.images ?? {},
    api13: supportsHyperlink(),
    deferred: [],
    needsApi13: false,
  };

  try {
    return await Word.run(async (context) => {
      const cursor = context.document.getSelection().getRange(Word.RangeLocation.end);
      const state: EmailPasteState = { prev: null, list: null, written: 0 };
      const anchor = (): EmailAnchor =>
        state.prev === null ? { kind: "range", range: cursor } : { kind: "after", prev: state.prev };
      const insertBlock = (mdBlock: MdBlock): WordInsertable => queueBlock(anchor(), mdBlock, {});
      // A plain paragraph carrying no [[B]]/[[I]]/[[U]]/[[H:]]/[[C:]] marker
      // takes the same single-run path every other insert path uses; one that
      // does goes through insertRichEmailParagraph so each marked word keeps
      // its own formatting instead of the paragraph being flattened to one
      // style.
      //
      // Real mail HTML routinely leaves a marker pair wrapping nothing (an
      // empty <span style="background:yellow"></span> Outlook drops after the
      // visible run, or a stray <b></b> from cursor state) — sanitise_email_body
      // turns that into a block that is JUST "[[H:yellow]][[/H]]"/"[[B]][[/B]]"
      // with no text between. parseEmailMarkupRuns correctly consumes the
      // markers and returns an EMPTY runs array for that block (there is
      // nothing to format), but `[].some(...)` in hasEmailFormatting is also
      // false — so this used to fall through to the "unformatted" branch below
      // and insert the ORIGINAL, un-parsed `text`, which still contains the
      // literal "[[H:yellow]]"/"[[/H]]" marker syntax. Same failure mode for
      // an unrecognised highlight name ("[[H:mauve]]text[[/H]]"): the runs
      // array has no formatting flags set, so it must never fall back to the
      // raw argument — only ever to the runs' own (marker-stripped) text.
      const insertParagraphText: EmailParagraphWriter = (text, style = "Normal") => {
        const runs = parseEmailMarkupRuns(text);
        if (runs.length === 0) return null; // marker(s) consumed the whole block; nothing visible to write
        return hasEmailFormatting(runs)
          ? insertRichEmailParagraph(anchor(), runs, style, ctx)
          : insertBlock({ kind: "para", text: runs.map((r) => r.text).join(""), style });
      };

      const head = preface.trim();
      if (head) {
        const prefaceParagraph = insertBlock({ kind: "para", text: head, style: "Normal" }) as Word.Paragraph;
        // Normal, explicitly. A new paragraph inherits the style of the one it
        // was inserted after, so pasting with the cursor inside a Heading would
        // otherwise turn the whole email into a heading — and put it in the
        // navigation pane as though it were part of the document's structure.
        prefaceParagraph.font.italic = true;
        state.prev = prefaceParagraph;
        state.written += 1;
      }

      await insertEmailBlocks(context, blocks, state, ctx, { anchor, insertBlock, insertParagraphText });
      await context.sync();
      const formattingSkipped = await applyDeferredEmailFormatting(context, ctx);
      return { inserted: true, paragraphs: state.written, ...(formattingSkipped ? { formattingSkipped: true } : {}) };
    });
  } catch (error) {
    // Report the whole paste as a refusal. NOT atomic, despite the single
    // Word.run: the host applies a queued batch incrementally, so a rejection
    // part-way through leaves the paragraphs before it in the document — the
    // caller's message says so and points at undo rather than claiming
    // nothing was written.
    // The error itself is logged (never the body — no document or mail text in
    // diagnostics) because this catch also absorbs host rejections that have
    // nothing to do with protection, and a swallowed error here once
    // misattributed a table-shape failure to a read-only document.
    console.error("[word] insertWordEmailAtCursor failed", error);
    return { inserted: false, reason: "failed" };
  }
}

export interface DocumentHeading {
  level: number;
  text: string;
}

/** One section of the outline AIVA builds for itself — never written to the document. */
export interface TocEntry {
  /** Heading text, exactly as the body carries it. */
  readonly title: string;
  /** Heading level, 1-9. */
  readonly level: number;
  /**
   * Index into `body.paragraphs` of the heading itself. This is the ADDRESSABLE
   * part: a window taken from an index is exact, where a window taken from a
   * phrase depends on a literal text match that can miss.
   */
  readonly startParagraph: number;
  /** Index of the last paragraph before the next heading of the same or higher level. */
  readonly endParagraph: number;
  /** Paragraphs the section spans, heading included. */
  readonly paragraphCount: number;
  /**
   * The number a NEW sub-clause of this section should carry — "1667.9" for a section
   * already running 1667.1 to 1667.8. Null when the section is not numbered.
   *
   * Computed, not inferred: the model was told to derive this from the window and
   * produced a number already in use. See `nextSubClauseNumber`.
   */
  readonly nextSubClause: string | null;
}

/**
 * Builds a table of contents for AIVA's own use, in memory, from the document's
 * heading styles — no field inserted, nothing written, the document untouched.
 *
 * This is the map `/write` routes on. It carries three things the flat heading
 * list from `getWordDocumentStructure` does not:
 *
 *   1. `startParagraph` / `endParagraph` — so the reading window can be taken by
 *      INDEX rather than by matching a phrase. `findAnchorIndex` is deliberately
 *      literal (there is no embedding service here), so a reworded request can
 *      miss; an index cannot.
 *   2. `paragraphCount` — so a caller can tell whether a 30-paragraph window covers
 *      the section or clips it, and how many sub-clauses it already has.
 *
 * Section extent runs to the next heading of the SAME OR HIGHER level, so
 * "9. Confidentiality" spans its own 9.1/9.2 sub-clauses rather than stopping at
 * the first of them.
 *
 * Returns `[]` for a document with no headings, and `[]` on a failed read — pair
 * it with `getWordDocumentStats().readOk` to tell those apart, exactly as
 * `getWordDocumentStructure` requires.
 */
/**
 * Every paragraph's text, style and outline level, in ONE round trip.
 *
 * The property load matters for speed. `paragraphs.load("items")` followed by a
 * `p.load("text, style")` per paragraph queues one property load per paragraph and
 * costs a second sync — on a 1,300-paragraph agreement that is what pushed
 * `word.get_document_structure` past its 5s timeout once the handler was doing
 * more than one read. Loading `items/text,items/style,items/outlineLevel` asks for
 * all three properties across the whole collection up front and syncs once, so the
 * outline level rides along for free — which is what lets a heading be recognised
 * by outline level as well as by style name.
 *
 * Returns null when the read cannot be taken, which callers must keep apart from
 * an empty document.
 */
async function sweepWordParagraphs(): Promise<ParagraphSweep | null> {
  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      // All three properties are WordApi 1.1, the floor for any Word add-in host, so
      // none of them needs an isSetSupported gate.
      paragraphs.load("items/text,items/style,items/outlineLevel");
      await context.sync();
      return {
        texts: paragraphs.items.map((p) => p.text ?? ""),
        styles: paragraphs.items.map((p) => p.style ?? ""),
        outlineLevels: paragraphs.items.map((p) => p.outlineLevel ?? null),
      };
    });
  } catch {
    return null;
  }
}

/**
 * The number a new sub-clause should carry, derived from the ones a section already
 * has. Returns null when the section is not numbered at all.
 *
 * This exists because asking the model to work it out did not hold. Told to "find
 * the LAST sub-clause and continue from it", it read a section running 1667.1 to
 * 1667.8 and drafted 1667.4 — a number already in use, with different wording. Two
 * clauses sharing a number survives insertion in silence and is found later by
 * whoever tries to cross-reference one of them.
 *
 * So the arithmetic moves into code, the same way `startParagraph` replaced matching
 * an anchor phrase: hand the model ONE value to copy instead of a derivation to
 * perform. What can be computed should not be prompted.
 *
 * Reads only the section's own paragraphs, matching the leading number on each. The
 * stem is taken from the HIGHEST existing sub-clause rather than from the heading,
 * so a section whose heading numbering and body numbering disagree still continues
 * from what the body actually shows.
 */
export function nextSubClauseNumber(sectionTexts: readonly string[]): string | null {
  let bestStem: string | null = null;
  let bestLeaf = 0;
  let bestDepth = 0;

  for (const text of sectionTexts) {
    // "1667.8 The parties shall…" -> stem "1667", leaf 8. A bare "1667." heading
    // carries no leaf and is skipped: it is the section, not a sub-clause.
    const m = /^(\d+(?:\.\d+)*)\.(\d+)(?=[\s)\].:]|$)/.exec((text ?? "").trimStart());
    if (!m) continue;
    const stem = m[1] as string;
    const leaf = Number.parseInt(m[2] as string, 10);
    const depth = stem.split(".").length;
    // Deeper numbering (1667.8.2) belongs to a sub-list, not to this level. Track the
    // SHALLOWEST stem seen, which is the section's own sub-clause level.
    if (bestStem === null || depth < bestDepth) {
      bestStem = stem;
      bestDepth = depth;
      bestLeaf = leaf;
    } else if (depth === bestDepth && stem === bestStem && leaf > bestLeaf) {
      bestLeaf = leaf;
    }
  }

  return bestStem === null ? null : `${bestStem}.${bestLeaf + 1}`;
}

/** One paragraph sweep: text, style and outline level, index-aligned. */
interface ParagraphSweep {
  texts: string[];
  styles: string[];
  /** null where the host would not report one — never treat that as level 0. */
  outlineLevels: Array<number | null>;
}

/**
 * A paragraph's heading level, or null when it is body text.
 *
 * The STYLE NAME is the primary signal and is unchanged. AiVA's own heading writes
 * go through `styleBuiltIn`, so everything it produces is matched here.
 *
 * The OUTLINE LEVEL is the fallback, and it is the signal Word's own Navigation
 * Pane goes by. A document pasted in from HTML carries its heading-ness as DIRECT
 * paragraph formatting — `<w:outlineLvl>` inside `w:pPr`, with no `w:pStyle`
 * anywhere in the body — so every paragraph reports its style as "Normal" and the
 * style regex matched nothing. One such document had 171 headings that Word listed
 * in the Navigation Pane and that `/structure` reported as none.
 *
 * The two signals share one scale, so a document mixing them still nests correctly:
 * Heading N style carries outline level N.
 *
 * Only 1-9 counts as a heading. Word answers body text with the sentinel 10
 * (`wdOutlineLevelBodyText`), and a host that answers 0 instead must not be read as
 * "level 0" — neither value is a heading.
 */
export function headingLevelFrom(style: string, outlineLevel?: number | null): number | null {
  const styled = /^Heading\s*(\d)/i.exec(style ?? "");
  if (styled?.[1]) return Number.parseInt(styled[1], 10);
  // A rendered contents page is TOC 1-9 paragraphs. Word's stock TOC styles carry no
  // outline level, but a customised template's can — and counting those would report
  // the contents page itself as the document's headings.
  if (/^TOC\s*\d/i.test((style ?? "").trim())) return null;
  if (typeof outlineLevel === "number" && Number.isInteger(outlineLevel) && outlineLevel >= 1 && outlineLevel <= 9) {
    return outlineLevel;
  }
  return null;
}

/**
 * Derives the table of contents from a paragraph sweep. Pure, so the sectioning
 * rules are testable without an Office host.
 *
 * `outlineLevels` is optional: callers that swept style only keep the style-name
 * detection they had.
 */
export function tocFromParagraphs(
  texts: readonly string[],
  styles: readonly string[],
  outlineLevels?: readonly (number | null)[],
): TocEntry[] {
  const found: Array<{ index: number; level: number; title: string }> = [];
  styles.forEach((style, index) => {
    const level = headingLevelFrom(style, outlineLevels?.[index]);
    if (level === null) return;
    // A heading with no text is not one Word's own Navigation Pane lists, and the
    // outline-level fallback exists to match that pane. A pasted document carries
    // outlineLvl on its blank spacer paragraphs too, which counted them as
    // sections — 171 reported where 101 have a name (AIVA-8026).
    const title = (texts[index] ?? "").trim();
    if (!title) return;
    found.push({ index, level, title });
  });

  // Every paragraph that is a heading, at any level. nextSubClause below counts
  // BODY paragraphs only, so all of these are excluded from its input.
  const headingIndices = new Set(found.map((h) => h.index));

  return found.map((h, i) => {
    // The section ends where the next same-or-higher heading begins. A LOWER level
    // (9.1 under 9) belongs to this section, so it is skipped over.
    const next = found.slice(i + 1).find((c) => c.level <= h.level);
    const endParagraph = (next ? next.index : texts.length) - 1;
    const slice = texts.slice(h.index, endParagraph + 1);
    // nextSubClause is computed from the section's BODY PARAGRAPHS — every
    // heading in the range is excluded, at any level (AIVA-7938).
    //
    // A numbered heading is indistinguishable from a numbered clause by text
    // alone: "100.2 Detail" matches the same shape as "100.2 The parties
    // shall...". Two reports came from letting headings through:
    //
    //   - the section's OWN heading: a section whose body is plain prose still
    //     reported "100.3", purely because it was itself numbered;
    //   - its CHILD headings: "10. Deployment Strategy" with sub-headings
    //     "10.1 Overview" and "10.2 Detail" reported "10.3", and /write numbered
    //     its paragraphs 10.3, 10.4, 10.5 from it.
    //
    // Both are the wrong signal. A numbered sub-HEADING means the document has
    // numbered sub-SECTIONS, so the next number would OPEN one — which the
    // prompt forbids ("do NOT open a new numbered section"). A numbered BODY
    // clause means a numbered agreement, where adding the next clause is exactly
    // the job, and those paragraphs are still counted here.
    const body = texts.filter(
      (_, idx) => idx > h.index && idx <= endParagraph && !headingIndices.has(idx),
    );
    return {
      title: h.title,
      level: h.level,
      startParagraph: h.index,
      endParagraph,
      // Still measured on the whole section — "heading included", as documented.
      paragraphCount: slice.length,
      nextSubClause: nextSubClauseNumber(body),
    };
  });
}

/** One row of a rendered outline. */
export interface OutlineRow {
  /** Normalised depth, 1-based: the document's shallowest heading is level 1. */
  readonly level: number;
  readonly text: string;
  /** Index into body.paragraphs — addressable, so "show me this section" is exact. */
  readonly startParagraph: number;
  readonly paragraphCount: number;
}

/**
 * Where the document's heading-ness actually comes from.
 *
 * `outlineLevelOnly > 0 && styleBacked === 0` is the HTML-paste signature: real
 * structure that no paragraph style records. Worth telling the user, because it
 * is why their Navigation Pane, contents page and PDF bookmarks disagree with
 * each other — and it is a `/format` job, not a broken read.
 *
 * Calls the SAME predicate twice rather than adding a second style regex: once
 * without the outline level, which answers "would the style name alone have
 * found this?", then once with it.
 *
 * Takes `texts` for one reason: untitled paragraphs must be skipped here on the
 * same terms as in tocFromParagraphs. These counts sit on the outline card BESIDE
 * the heading total, so counting blanks on only one side of that line printed
 * "151 headings ... 171 without Word heading styles" — a provenance larger than
 * the total it describes (AIVA-8026).
 */
export function headingProvenance(
  texts: readonly string[],
  styles: readonly string[],
  outlineLevels?: readonly (number | null)[],
): { styleBacked: number; outlineLevelOnly: number } {
  let styleBacked = 0;
  let outlineLevelOnly = 0;
  styles.forEach((style, index) => {
    if (!(texts[index] ?? "").trim()) return;
    if (headingLevelFrom(style) !== null) styleBacked += 1;
    else if (headingLevelFrom(style, outlineLevels?.[index]) !== null) outlineLevelOnly += 1;
  });
  return { styleBacked, outlineLevelOnly };
}

/**
 * The outline rows for a table of contents, optionally trimmed to a depth.
 *
 * Levels are NORMALISED so the shallowest heading renders at level 1. A document
 * whose headings are all outline level 2 with no level 1 above them — the HTML
 * paste again — would otherwise render as a run of orphaned sub-bullets nested
 * under a parent that does not exist, and a model asked to number it invents
 * "1.1 … 1.171".
 *
 * `maxDepth` counts in normalised levels, so "level 1 only" means the document's
 * own top level whatever Word calls it.
 */
export function outlineFromToc(toc: readonly TocEntry[], maxDepth?: number): OutlineRow[] {
  if (toc.length === 0) return [];
  const minLevel = Math.min(...toc.map((entry) => entry.level));
  const rows = toc.map((entry) => ({
    level: entry.level - minLevel + 1,
    text: entry.title,
    startParagraph: entry.startParagraph,
    paragraphCount: entry.paragraphCount,
  }));
  return maxDepth === undefined ? rows : rows.filter((row) => row.level <= maxDepth);
}

/** Nested markdown bullets — nesting from list markers, which is the only kind
 *  the chat pane renders (leading spaces alone collapse into one paragraph). */
export function outlineToMarkdown(rows: readonly OutlineRow[]): string {
  return rows.map((row) => `${"  ".repeat(row.level - 1)}- ${row.text}`).join("\n");
}

/** Non-blank paragraphs and total words, from a sweep already in hand. */
export function statsFromParagraphs(texts: readonly string[]): { paragraphCount: number; wordCount: number } {
  return {
    paragraphCount: texts.filter((t) => t.trim().length > 0).length,
    wordCount: texts.join(" ").split(/\s+/).filter(Boolean).length,
  };
}

/**
 * Recovers the document's TOC from its PARAGRAPH STYLES rather than the fields API.
 *
 * `body.fields` is the direct route, but it is not dependable: on Word on the web
 * it comes back EMPTY rather than throwing, so a document carrying a real contents
 * page reported `hasToc: false` and every page number went missing with it. The
 * rendered TOC is always paragraphs styled TOC 1-9 though, and the sweep already
 * loads every paragraph's style — so this costs no extra round trip.
 *
 * `parseTocLine` does the per-line work, which is why leaders, tabs and roman
 * numerals are handled here exactly as they are for a field read.
 */
export function tocFieldEntriesFromParagraphs(
  texts: readonly string[],
  styles: readonly string[],
): TocFieldEntry[] {
  const entries: TocFieldEntry[] = [];
  styles.forEach((style, i) => {
    if (!/^TOC\s*\d/i.test(style.trim())) return;
    const entry = parseTocLine(texts[i] ?? "", style);
    if (entry) entries.push(entry);
  });
  return entries;
}

/**
 * The whole document map from a SINGLE paragraph sweep.
 *
 * `word.get_document_structure` used to call four separate readers, two of which
 * swept every paragraph independently. Everything below is derived from one sweep
 * instead — the TOC field read is the only other round trip, and it is small.
 */
export async function readWordDocumentMap(): Promise<{
  toc: TocEntry[];
  headings: DocumentHeading[];
  /**
   * The document's own contents page, recovered from its TOC 1-9 paragraph styles.
   * Empty when it has none. Callers use it when the fields API returns nothing,
   * which is what happens on Word on the web.
   */
  tocParagraphs: TocFieldEntry[];
  /** How many headings the style names found vs. how many only the outline level
   *  did — see `headingProvenance`. Both zero on a document with no headings. */
  provenance: { styleBacked: number; outlineLevelOnly: number };
  paragraphCount: number;
  wordCount: number;
  readOk: boolean;
}> {
  const sweep = await sweepWordParagraphs();
  if (!sweep) {
    // readOk:false is the signal that these empties mean "unknown", not "empty".
    return {
      toc: [],
      headings: [],
      tocParagraphs: [],
      provenance: { styleBacked: 0, outlineLevelOnly: 0 },
      paragraphCount: 0,
      wordCount: 0,
      readOk: false,
    };
  }
  const toc = tocFromParagraphs(sweep.texts, sweep.styles, sweep.outlineLevels);
  return {
    toc,
    // The heading list IS the TOC's titles and levels — same paragraphs, flat. No
    // second sweep, and the two can no longer disagree.
    headings: toc.map((e) => ({ level: e.level, text: e.title })),
    tocParagraphs: tocFieldEntriesFromParagraphs(sweep.texts, sweep.styles),
    provenance: headingProvenance(sweep.texts, sweep.styles, sweep.outlineLevels),
    ...statsFromParagraphs(sweep.texts),
    readOk: true,
  };
}

export async function buildWordDocumentToc(): Promise<TocEntry[]> {
  const sweep = await sweepWordParagraphs();
  return sweep ? tocFromParagraphs(sweep.texts, sweep.styles, sweep.outlineLevels) : [];
}

export async function getWordDocumentStructure(): Promise<DocumentHeading[]> {
  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();
      for (const p of paragraphs.items) p.load("text, style, outlineLevel");
      await context.sync();

      const headings: DocumentHeading[] = [];
      for (const p of paragraphs.items) {
        // headingLevelFrom, not a local regex. A second definition of "heading"
        // living here is how this reader and readWordDocumentMap came to disagree.
        const level = headingLevelFrom(p.style ?? "", p.outlineLevel);
        const text = (p.text ?? "").trim();
        // Untitled headings are excluded here in the same terms as in
        // tocFromParagraphs — see the note there.
        if (level !== null && text) {
          headings.push({ level, text });
        }
      }
      return headings;
    });
  } catch {
    return [];
  }
}

/** How much content the document actually holds, and whether the read worked. */
export interface DocumentStats {
  /** Non-blank paragraphs in the body. */
  readonly paragraphCount: number;
  readonly wordCount: number;
  /** False when Office.js refused the read — NOT the same as an empty document. */
  readonly readOk: boolean;
}

/**
 * Count the body's content, independently of heading styles.
 *
 * ``getWordDocumentStructure`` only returns paragraphs styled Heading 1-9, so a
 * perfectly full but unstyled document comes back as an empty array — and a
 * failed read comes back as an empty array too. Callers that need to tell
 * "no headings" apart from "no content" (and either apart from "couldn't read")
 * must have these counts as well.
 */
export async function getWordDocumentStats(): Promise<DocumentStats> {
  try {
    return await Word.run(async (context) => {
      const body = context.document.body;
      body.load("text");
      await context.sync();
      // Word separates paragraphs in `body.text` with a BARE CR, not CRLF and not
      // LF — so `/\r?\n/` never matched and a 1,300-paragraph agreement counted as
      // one line. Split on all three forms.
      const text = body.text ?? "";
      return {
        paragraphCount: text.split(/\r\n|\r|\n/).filter((line) => line.trim().length > 0).length,
        wordCount: text.split(/\s+/).filter(Boolean).length,
        readOk: true,
      };
    });
  } catch {
    // readOk:false is the signal that these zeros mean "unknown", not "empty".
    return { paragraphCount: 0, wordCount: 0, readOk: false };
  }
}

/** One style change for {@link applyWordStyles}. `index` is the paragraph's
 *  position in body.paragraphs, as `word_get_format_candidates` reports it. */
export interface WordStyleChange {
  text: string;
  style: string;
  index?: number;
}

/**
 * Which paragraph a style change lands on.
 *
 * The INDEX wins when the paragraph there still carries the text the model was
 * shown: documents repeat short lines (a recurring caption, a bare "1."),
 * and matching by text alone restyled the first one every time. When the
 * index is missing or has drifted (the user edited above it), fall back to the
 * first paragraph with that exact text that this batch has not already restyled.
 * Pure, so the targeting rules are testable without an Office host.
 */
export function resolveStyleTarget(
  texts: readonly string[],
  change: WordStyleChange,
  used: ReadonlySet<number>,
): number | null {
  const want = (change.text ?? "").trim();
  if (!want) return null;
  const at = change.index;
  if (typeof at === "number" && Number.isInteger(at) && !used.has(at) && (texts[at] ?? "").trim() === want) {
    return at;
  }
  const found = texts.findIndex((t, i) => !used.has(i) && (t ?? "").trim() === want);
  return found === -1 ? null : found;
}

export async function applyWordStyles(changes: WordStyleChange[]): Promise<number> {
  let applied = 0;
  try {
    await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      // One load + one sync for every paragraph's text (see sweepWordParagraphs).
      paragraphs.load("items/text");
      await context.sync();
      const texts = paragraphs.items.map((p) => p.text ?? "");
      const used = new Set<number>();
      for (const change of changes) {
        const index = resolveStyleTarget(texts, change, used);
        const target = index === null ? undefined : paragraphs.items[index];
        if (index === null || !target) continue;
        used.add(index);
        // Both routes, so a heading lands on a non-English Word too.
        applyBlockStyle(target, change.style);
        applied++;
      }
      await context.sync();
    });
  } catch { /* return partial count */ }
  return applied;
}

// ---------------------------------------------------------------------------
// Format candidates (AIVA-8606) — what /format restyles
// ---------------------------------------------------------------------------

/** One paragraph as the format sweep reads it. */
export interface FormatParagraphFacts {
  text: string;
  style: string;
  outlineLevel?: number | null;
  /** True / false, or null when the paragraph mixes bold and regular runs. */
  bold?: boolean | null;
  /** Points, or null when the paragraph mixes sizes or the host would not say. */
  fontSize?: number | null;
  /** > 0 inside a table cell. */
  tableNestingLevel?: number | null;
  /** True when Word numbers or bullets the paragraph as part of a list. */
  isListItem?: boolean | null;
}

/** A paragraph short enough to be a heading, with the facts that tell the model
 *  whether it is one. No suggestion is made here: deciding what a heading is
 *  belongs to the model, which reads the text, so nothing about any one kind of
 *  document is built into the add-in. */
export interface FormatCandidate {
  /** Position in body.paragraphs — pass back as `index` to pill_word_apply_styles. */
  index: number;
  text: string;
  currentStyle: string;
  /** Heading level the paragraph already has (style or outline level), else null. */
  currentLevel: number | null;
  bold: boolean | null;
  fontSize: number | null;
  /** Part of a Word list (bulleted or numbered). Numbered headings are often
   *  lists too, so this is context, not a verdict. */
  isListItem: boolean | null;
  /** The start of the next non-empty paragraph. A heading is followed by the
   *  content it names; a list item by its siblings. Without this the model saw
   *  every short line in isolation and promoted bullet points to headings. */
  nextText: string;
}

export interface FormatCandidatesResult {
  readOk: boolean;
  /** Every paragraph in the document, so the model can say how much it covered. */
  paragraphCount: number;
  /** Paragraphs that already carry a heading style or outline level. */
  styledHeadingCount: number;
  /** The size most of the document's prose is set in — the baseline that makes a
   *  candidate's `fontSize` meaningful. Null when the host reported no sizes. */
  bodyFontSize: number | null;
  candidates: FormatCandidate[];
  /** True when {@link FORMAT_CANDIDATE_CAP} was reached before the end of the
   *  document. Read on from {@link nextIndex}. */
  truncated: boolean;
  /** Where the next page starts — pass as `startIndex`. Null on the last page. */
  nextIndex: number | null;
}

/** Longest paragraph that can still be a heading. Anything longer is prose — and
 *  a clause that merely opens with a run-in heading is one paragraph, so styling
 *  it would turn the whole clause into a heading. */
export const FORMAT_HEADING_MAX_CHARS = 120;

/** How much of the following paragraph rides along as `nextText`. */
const FORMAT_NEXT_TEXT_CHARS = 80;

/** One page of candidates. Keeps each tool result well inside the model's
 *  context; a longer document is read in pages via `startIndex`. */
export const FORMAT_CANDIDATE_CAP = 400;

/** The font size carrying the most prose: each size is weighted by the text set
 *  in it, so a document's many short headings cannot outvote its body. */
function dominantFontSize(paragraphs: readonly FormatParagraphFacts[]): number | null {
  const weight = new Map<number, number>();
  for (const p of paragraphs) {
    const size = p.fontSize;
    if (typeof size !== "number" || Number.isNaN(size) || size <= 0) continue;
    weight.set(size, (weight.get(size) ?? 0) + (p.text ?? "").trim().length);
  }
  let best: number | null = null;
  let bestWeight = 0;
  for (const [size, w] of weight) {
    if (w > bestWeight) {
      best = size;
      bestWeight = w;
    }
  }
  return best;
}

function nextNonEmptyText(paragraphs: readonly FormatParagraphFacts[], index: number): string {
  for (let i = index + 1; i < paragraphs.length; i++) {
    const text = (paragraphs[i]?.text ?? "").trim();
    if (text) return text.length > FORMAT_NEXT_TEXT_CHARS ? `${text.slice(0, FORMAT_NEXT_TEXT_CHARS)}…` : text;
  }
  return "";
}

/**
 * The paragraphs /format may restyle, from ONE sweep of the whole document.
 *
 * This is what lets /format work past the 6,000-character document context
 * (DOCUMENT_CONTEXT_CHAR_CAP): it used to retype the document in chat, which
 * stopped where the context did and — through Replace selection — could have
 * replaced the whole document with its first 6,000 characters. Now the add-in
 * reads every paragraph, the model chooses styles, and pill_word_apply_styles
 * applies them in place without touching any text.
 *
 * The filter is structural only — non-empty, short enough to be a heading, not
 * a contents-page entry, not inside a table — so it holds for any document, not
 * just one family of them. Pure, so it is testable without an Office host.
 */
export function formatCandidatesFromParagraphs(
  paragraphs: readonly FormatParagraphFacts[],
  startIndex = 0,
): Omit<FormatCandidatesResult, "readOk"> {
  const candidates: FormatCandidate[] = [];
  let styledHeadingCount = 0;
  let nextIndex: number | null = null;
  const from = Number.isInteger(startIndex) && startIndex > 0 ? startIndex : 0;

  paragraphs.forEach((p, index) => {
    const text = (p.text ?? "").trim();
    const style = p.style ?? "";
    const currentLevel = headingLevelFrom(style, p.outlineLevel);
    if (currentLevel !== null && text) styledHeadingCount++;
    if (index < from || nextIndex !== null) return;
    if (!text || text.length > FORMAT_HEADING_MAX_CHARS) return;
    if (/^TOC\s*\d/i.test(style.trim())) return;
    if ((p.tableNestingLevel ?? 0) > 0) return;
    if (candidates.length >= FORMAT_CANDIDATE_CAP) {
      nextIndex = index;
      return;
    }
    candidates.push({
      index,
      text,
      currentStyle: style,
      currentLevel,
      bold: typeof p.bold === "boolean" ? p.bold : null,
      fontSize: typeof p.fontSize === "number" && p.fontSize > 0 ? p.fontSize : null,
      isListItem: typeof p.isListItem === "boolean" ? p.isListItem : null,
      nextText: nextNonEmptyText(paragraphs, index),
    });
  });

  return {
    paragraphCount: paragraphs.length,
    styledHeadingCount,
    bodyFontSize: dominantFontSize(paragraphs),
    candidates,
    truncated: nextIndex !== null,
    nextIndex,
  };
}

/** Every paragraph's text, style, outline level, table depth and font, in one
 *  sync. Falls back to the WordApi 1.1 sweep (no font, no table depth) on a host
 *  that rejects the richer load, so /format still works there — the model just
 *  has less to go on. */
async function sweepFormatFacts(): Promise<FormatParagraphFacts[] | null> {
  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load(
        "items/text,items/style,items/outlineLevel,items/tableNestingLevel,items/isListItem,items/font/bold,items/font/size",
      );
      await context.sync();
      return paragraphs.items.map((p) => ({
        text: p.text ?? "",
        style: p.style ?? "",
        outlineLevel: p.outlineLevel ?? null,
        tableNestingLevel: p.tableNestingLevel ?? null,
        isListItem: p.isListItem ?? null,
        bold: p.font?.bold ?? null,
        fontSize: p.font?.size ?? null,
      }));
    });
  } catch {
    const basic = await sweepWordParagraphs();
    if (!basic) return null;
    return basic.texts.map((text, i) => ({
      text,
      style: basic.styles[i] ?? "",
      outlineLevel: basic.outlineLevels[i] ?? null,
    }));
  }
}

export async function getWordFormatCandidates(startIndex = 0): Promise<FormatCandidatesResult> {
  const facts = await sweepFormatFacts();
  if (!facts) {
    return {
      readOk: false,
      paragraphCount: 0,
      styledHeadingCount: 0,
      bodyFontSize: null,
      candidates: [],
      truncated: false,
      nextIndex: null,
    };
  }
  return { readOk: true, ...formatCandidatesFromParagraphs(facts, startIndex) };
}

// ---------------------------------------------------------------------------
// Strip all styles (FS-02)
// ---------------------------------------------------------------------------

/** Word's default body formatting — what "no style" means after a strip. */
const NORMAL_STYLE = "Normal";
const NORMAL_FONT = { name: "Calibri", size: 11, color: "#000000" } as const;

export interface StripStylesResult {
  stripped: boolean;
  /** Paragraphs reset. Present on success. */
  paragraphs?: number;
  reason?: "empty" | "count_moved" | "failed";
  /** On count_moved: what the user approved vs what is there now. */
  expected?: number;
  actual?: number;
}

/**
 * Count the paragraphs a strip would affect, without touching the document.
 *
 * This is the read half of FS-02's count-then-confirm (G-6 closure): the number
 * has to reach the confirm card before anything is written, because "reset every
 * paragraph in this document" is not a decision a user can make without knowing
 * how many that is.
 */
export async function countWordParagraphs(): Promise<number> {
  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();
      return paragraphs.items.length;
    });
  } catch {
    return 0;
  }
}

/** Reset one paragraph's font to Normal's — Calibri 11, black, not bold/italic. */
function applyNormalParagraphFont(font: Word.Font): void {
  font.name = NORMAL_FONT.name;
  font.size = NORMAL_FONT.size;
  font.color = NORMAL_FONT.color;
  font.bold = false;
  font.italic = false;
  // Highlighting is direct character formatting, same as bold/italic — it
  // survives a paragraph.style reset and must be cleared explicitly, or
  // "stripped" text stays visibly highlighted (AIVA-7612).
  (font as { highlightColor: string | null }).highlightColor = null;
}

/** What a strip would actually do to this document. */
export interface FormattingSurvey {
  /** Body paragraphs. Zero means there is nothing to reset at all. */
  paragraphs: number;
  /** True when at least one paragraph would come out different than it went in. */
  wouldChange: boolean;
}

/**
 * True when this paragraph's font differs from what a strip would leave behind.
 *
 * Word reports MIXED runs as `null` (booleans, colour, size) or `""` (name): a
 * paragraph holding a single bold word reads exactly like this, and it IS
 * formatted. Treating mixed as clean is precisely how a bold word inside
 * otherwise-plain text would earn a document "no formatting here" while visibly
 * having some — so mixed counts as formatted, deliberately.
 *
 * `undefined` is the other case, and it is NOT the same thing: it means the host
 * did not report the property at all. Those are skipped rather than treated as
 * formatting, or one unsupported property on one host would make every document
 * look formatted and the check could never fire.
 */
function _fontDiffersFromNormal(font: Word.Font): boolean {
  const name = font.name;
  if (name === "" || name === null) return true;               // mixed run
  if (name !== undefined && name !== NORMAL_FONT.name) return true;
  const size = font.size;
  if (size === null) return true;                              // mixed run
  if (size !== undefined && size !== NORMAL_FONT.size) return true;
  if (font.bold !== false && font.bold !== undefined) return true;
  if (font.italic !== false && font.italic !== undefined) return true;
  // Highlight is the one property whose null is NOT "mixed": stripAllWordStyles
  // CLEARS a highlight by writing null, so a freshly-stripped paragraph reads
  // back null. Calling that "mixed, therefore formatted" is what made the card
  // reappear on a document that had just been stripped — the check could never
  // be satisfied by the very operation it gates. isRealHighlight is this file's
  // existing answer to "is there actually a highlight here", and it is the one
  // that agrees with what the strip writes.
  if (isRealHighlight((font as { highlightColor?: string | null }).highlightColor)) return true;
  const color = font.color;
  if (color === null || color === "") return true;             // mixed run
  return color !== undefined && color.toLowerCase() !== NORMAL_FONT.color.toLowerCase();
}

/**
 * Whether stripping this document's styles would change anything (AIVA-7613).
 *
 * The strip-styles pill used to be offered whenever the document had at least
 * one paragraph, because counting paragraphs was all it did — it never looked at
 * a single style or font. So an already-plain document still got an approval card
 * promising to "reset 286 paragraphs … only Ctrl+Z restores them" for an
 * operation that would change nothing.
 *
 * The question deliberately asked here is "would a strip change anything?", NOT
 * "does this document have formatting?". Those come apart on a document whose
 * Normal style is, say, Arial 12: it LOOKS unformatted, yet stripping would
 * restyle every paragraph to Calibri 11. Answering the first question is the one
 * that can never mislead the user about what the button in front of them does.
 *
 * Two passes, cheapest first: a document with any heading exits on styles alone,
 * and the per-paragraph font load never runs. Anything unreadable answers TRUE —
 * the failure mode is offering a pill that changes nothing (today's behaviour),
 * never withholding one the user needed.
 */
export async function surveyWordFormatting(): Promise<FormattingSurvey> {
  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();
      const count = paragraphs.items.length;
      if (count === 0) return { paragraphs: 0, wouldChange: false };

      for (const paragraph of paragraphs.items) paragraph.load("style");
      await context.sync();
      if (paragraphs.items.some((paragraph) => (paragraph.style ?? NORMAL_STYLE) !== NORMAL_STYLE)) {
        return { paragraphs: count, wouldChange: true };
      }

      // Direct character formatting survives a style reset, which is why
      // stripAllWordStyles clears it explicitly in its own pass — so a document
      // that is all-Normal can still have plenty for a strip to do.
      for (const paragraph of paragraphs.items) {
        paragraph.font.load("name, size, color, bold, italic, highlightColor");
      }
      await context.sync();
      return {
        paragraphs: count,
        wouldChange: paragraphs.items.some((paragraph) => _fontDiffersFromNormal(paragraph.font)),
      };
    });
  } catch (err) {
    logOfficeError(err, "word-survey-formatting");
    return { paragraphs: await countWordParagraphs(), wouldChange: true };
  }
}

/**
 * Reset every paragraph to Normal — Calibri 11, black (FS-02).
 *
 * ``expectedCount`` is the number the user approved on the confirm card. If the
 * document has changed since, the operation ABORTS rather than applying to a
 * different number of paragraphs than was agreed. That is the G-11 lesson from
 * find/replace applied here: an approval is for a specific blast radius, and
 * silently widening it is the defect, not the safety check.
 *
 * The most destructive operation in the channel, so it is deliberately incapable
 * of running without a count having been shown first.
 */
export async function stripAllWordStyles(expectedCount?: number): Promise<StripStylesResult> {
  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();

      const actual = paragraphs.items.length;
      if (actual === 0) return { stripped: false, reason: "empty" as const };
      if (expectedCount != null && expectedCount !== actual) {
        return { stripped: false, reason: "count_moved" as const, expected: expectedCount, actual };
      }

      // ONE SYNC first (AIVA-7616). Word records every context.sync() as its
      // own undo step, so the original two-sync split — style, then font, each
      // in its own sync — meant a single "strip styles" action needed TWO
      // Ctrl+Z presses to fully revert. Style and font are both reliably
      // writable properties on every host this add-in targets, so try them
      // together first: one sync, one undo step, on the common path.
      for (const paragraph of paragraphs.items) {
        paragraph.style = NORMAL_STYLE;
        applyNormalParagraphFont(paragraph.font);
      }
      try {
        await context.sync();
        return { stripped: true, paragraphs: actual };
      } catch {
        // Fall through to the two-pass fallback below.
      }

      // Fallback: TWO SYNCS, deliberately. Word.js QUEUES property writes and
      // only reports failures at context.sync(), so wrapping the assignments
      // in try/catch guards nothing — a single unsupported font property
      // fails the WHOLE batch, which is exactly what the merged attempt above
      // just hit. That is what made this report "could not be reset" on a
      // perfectly writable document, before the two-pass split existed.
      //
      // Re-load fresh paragraph proxies rather than reusing the ones above:
      // the failed sync already queued style AND font writes on those
      // objects, and resyncing them here would just retry — and re-fail —
      // the same combined batch instead of isolating style from font.
      const retryParagraphs = context.document.body.paragraphs;
      retryParagraphs.load("items");
      await context.sync();

      // Pass 1 is the operation and must succeed on its own: style only.
      for (const paragraph of retryParagraphs.items) {
        paragraph.style = NORMAL_STYLE;
      }
      await context.sync();

      // Pass 2 is the enhancement, in its own sync so its failure cannot undo
      // pass 1. Direct character formatting survives a style change, so a
      // paragraph manually set to 18pt bold red would still LOOK styled — but a
      // host that rejects one of these must not turn a successful strip into a
      // reported failure.
      try {
        for (const paragraph of retryParagraphs.items) {
          applyNormalParagraphFont(paragraph.font);
        }
        await context.sync();
      } catch {
        /* styles are already Normal (AC-3); direct formatting may remain */
      }
      return { stripped: true, paragraphs: actual };
    });
  } catch {
    return { stripped: false, reason: "failed" };
  }
}

export interface StripSelectionResult {
  stripped: boolean;
  /** Paragraphs reset. Present on success. */
  paragraphs?: number;
  reason?: "empty" | "count_moved" | "failed";
  /** On count_moved: what the user approved vs what is there now. */
  expected?: number;
  actual?: number;
}

/**
 * Count the paragraphs the current selection touches, without writing anything.
 *
 * Read half of the selection-scoped strip's own count-then-confirm gate
 * (mirrors {@link countWordParagraphs} for the whole-document path, and keeps
 * the same unit — paragraphs, not words — so the two approval cards read
 * consistently). A paragraph only partially covered by the selection still
 * counts as one: the strip clears the whole paragraph's direct formatting,
 * not just the selected characters within it. 0 means nothing is selected
 * (or the selection could not be read), which the caller uses to fall back
 * to the whole-document flow instead.
 */
export async function countWordSelectionParagraphs(): Promise<number> {
  try {
    return await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.load("text");
      const paragraphs = selection.paragraphs;
      paragraphs.load("items");
      await context.sync();
      if (!(selection.text ?? "").trim()) return 0;
      return paragraphs.items.length;
    });
  } catch {
    return 0;
  }
}

/**
 * Clear direct character formatting — font, size, color, bold, italic and
 * highlight — from the current selection only.
 *
 * Narrower than {@link stripAllWordStyles} on purpose: it never touches
 * `paragraph.style`. A selection can start or end mid-paragraph, so resetting
 * the paragraph's style would restyle text outside what the user actually
 * selected. This matches Word's own "Clear Formatting" — direct run
 * formatting only, not the paragraph style.
 *
 * ``expectedParagraphCount`` is the number the user approved on the confirm
 * card; like the whole-document strip, the operation aborts rather than
 * silently applying to a selection that changed size since it was approved.
 */
export async function stripSelectionStyles(expectedParagraphCount?: number): Promise<StripSelectionResult> {
  try {
    return await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.load("text");
      const paragraphs = selection.paragraphs;
      paragraphs.load("items");
      await context.sync();

      const text = (selection.text ?? "").trim();
      if (!text) return { stripped: false, reason: "empty" as const };
      const actual = paragraphs.items.length;
      if (expectedParagraphCount != null && expectedParagraphCount !== actual) {
        return { stripped: false, reason: "count_moved" as const, expected: expectedParagraphCount, actual };
      }

      const font = selection.font;
      font.name = NORMAL_FONT.name;
      font.size = NORMAL_FONT.size;
      font.color = NORMAL_FONT.color;
      font.bold = false;
      font.italic = false;
      (font as { highlightColor: string | null }).highlightColor = null;
      await context.sync();

      return { stripped: true, paragraphs: actual };
    });
  } catch {
    return { stripped: false, reason: "failed" };
  }
}

// ---------------------------------------------------------------------------
// Comments — list, resolve, delete (RV-02)
// ---------------------------------------------------------------------------

export interface WordComment {
  /** Word's own comment id — what resolve/delete target. */
  id: string;
  author: string;
  /** Formatted for display; the raw value is never shown. */
  date: string;
  text: string;
  resolved: boolean;
}

export interface CommentActionResult {
  ok: boolean;
  reason?: "not_found" | "unsupported" | "failed";
}

/**
 * Every comment in the document, oldest-first as Word returns them (RV-02).
 *
 * Distinct from ``getWordReviewItems``, which filters to recent items for the
 * review summary. This is the full list, and it carries the ``id`` that
 * ``resolveWordComment``/``deleteWordComment`` need — targeting by author+text
 * would act on the wrong comment whenever two are identical.
 *
 * Returns ``null`` (not ``[]``) when the host has no comments API, so the caller
 * can distinguish "no comments" from "cannot read comments" — the same
 * three-outcome distinction the specification asks for elsewhere.
 */
export async function getWordComments(): Promise<WordComment[] | null> {
  // Comments are WordApi 1.4. When the set is unsupported the collection can come
  // back EMPTY rather than throwing, which previously surfaced as "No comments in
  // this document." — a false statement about a document that plainly has them.
  // Probe first so an unsupported host reports being unable to read them.
  if (!wordCommentsApiSupported()) return null;
  try {
    return await Word.run(async (context) => {
      const body = context.document.body;
      if (typeof body.getComments !== "function") return null;
      const comments = body.getComments();
      // Nested item paths in ONE load, per Microsoft's documented pattern. The
      // two-phase form (load("items"), then load each item) leaves `items`
      // unpopulated on some hosts WITHOUT throwing, so the read appeared to
      // succeed and reported a document full of comments as empty.
      comments.load(COMMENT_LIST_FIELDS);
      await context.sync();
      // AIVA-9042: comments on footnote text too. Without them the review's
      // comment reconciliation read a comment it had placed in a footnote as
      // deleted, and reopened that finding on every scan. Deduplicated by id,
      // in case a host's body list already reaches into footnotes.
      const seen = new Set(comments.items.map((c) => String(c.id ?? "")));
      const inFootnotes = (await getFootnoteComments(context)).filter((c) => !seen.has(String(c.id ?? "")));
      return [...comments.items, ...inFootnotes].map((c) => ({
        id: String(c.id ?? ""),
        author: c.authorName ?? "",
        date: c.creationDate ? new Date(c.creationDate as unknown as string | Date).toLocaleString() : "",
        text: (c.content ?? "").trim(),
        resolved: c.resolved === true,
      }));
    });
  } catch (err) {
    logOfficeError(err, "word-get-comments");
    return null;
  }
}

const COMMENT_LIST_FIELDS = "items/id,items/authorName,items/creationDate,items/content,items/resolved";

/** Every comment anchored in a footnote, in footnote order. Empty when the host
 *  cannot read footnotes or the read throws — the main text's comments are
 *  still worth returning on their own. */
async function getFootnoteComments(context: Word.RequestContext): Promise<Word.Comment[]> {
  const bodies = await loadFootnoteBodies(context);
  if (bodies.length === 0) return [];
  try {
    const lists = bodies.map((body) => {
      const list = body.getComments();
      list.load(COMMENT_LIST_FIELDS);
      return list;
    });
    await context.sync();
    return lists.flatMap((list) => list.items);
  } catch (err) {
    logOfficeError(err, "word-get-footnote-comments");
    return [];
  }
}

/** One comment, plus the document text it is anchored to. */
export interface AnchoredWordComment {
  /** The comment's own text, trimmed. */
  text: string;
  /** The document text the comment is attached to, trimmed — e.g. "Section 12"
   *  for a broken_reference flag. Empty when the range could not be read. */
  anchorText: string;
}

/**
 * Every comment in the document WITH the text it is anchored to (AIVA-8538).
 *
 * `getWordComments` above deliberately does not read ranges — it feeds a
 * display list, and one extra round trip per comment would be pure cost there.
 * Suppression needs the opposite trade: knowing a comment exists is useless
 * without knowing WHICH reference it sits on, because every broken_reference
 * comment carries the identical text "section reference not found". Matching on
 * that text alone would silence every broken-reference finding in the document
 * as soon as one comment existed anywhere in it.
 *
 * Returns `null` (not `[]`) when comments cannot be read at all, so a caller can
 * tell "no comments" from "could not look" and fall back to NOT suppressing —
 * never the other way round. A comment whose own range fails to load yields an
 * empty `anchorText` and so matches nothing, which fails the same safe way.
 */
export async function getAnchoredWordComments(): Promise<AnchoredWordComment[] | null> {
  if (!wordCommentsApiSupported()) return null;
  try {
    return await Word.run(async (context) => {
      const body = context.document.body;
      if (typeof body.getComments !== "function") return null;
      const comments = body.getComments();
      comments.load("items/id,items/content");
      await context.sync();
      // AIVA-9042: and the comments on footnote text, or a footnote finding
      // AiVA already commented on would be offered again by every new thread.
      const seen = new Set(comments.items.map((c) => String(c.id ?? "")));
      const all = [
        ...comments.items,
        ...(await getFootnoteComments(context)).filter((c) => !seen.has(String(c.id ?? ""))),
      ];

      // Ranges are queued in ONE batch and read after a single sync — the
      // "one round trip, not N" pattern the rest of this file uses. A
      // per-comment sync would cost a round trip each on a document that can
      // carry dozens.
      const ranges = all.map((c) => (typeof c.getRange === "function" ? c.getRange() : null));
      for (const r of ranges) r?.load("text");
      await context.sync();

      return all.map((c, i) => ({
        text: (c.content ?? "").trim(),
        anchorText: (ranges[i]?.text ?? "").trim(),
      }));
    });
  } catch (err) {
    logOfficeError(err, "word-get-anchored-comments");
    return null;
  }
}

/** Find one comment by id inside a Word.run context, or undefined. */
async function loadCommentById(context: Word.RequestContext, id: string): Promise<Word.Comment | undefined> {
  const comments = context.document.body.getComments();
  comments.load("items/id");
  await context.sync();
  return comments.items.find((c) => String(c.id ?? "") === id);
}

/** Mark a comment resolved (RV-02). Reversible in Word, so no confirm. */
export async function resolveWordComment(id: string): Promise<CommentActionResult> {
  if (!id) return { ok: false, reason: "not_found" };
  try {
    return await Word.run(async (context) => {
      const target = await loadCommentById(context, id);
      if (!target) return { ok: false, reason: "not_found" as const };
      target.resolved = true;
      await context.sync();
      return { ok: true };
    });
  } catch {
    return { ok: false, reason: "failed" };
  }
}

/**
 * Delete a comment (RV-02).
 *
 * Irreversible from the pane — Word offers no undo for it — which is why the
 * card requires a second click before calling this (G-8). The service itself
 * stays a plain operation: the confirmation belongs to the UI, not here.
 */
export async function deleteWordComment(id: string): Promise<CommentActionResult> {
  if (!id) return { ok: false, reason: "not_found" };
  try {
    return await Word.run(async (context) => {
      const target = await loadCommentById(context, id);
      if (!target) return { ok: false, reason: "not_found" as const };
      target.delete();
      await context.sync();
      return { ok: true };
    });
  } catch {
    return { ok: false, reason: "failed" };
  }
}

// ---------------------------------------------------------------------------
// Accept / reject tracked changes (RV-04)
// ---------------------------------------------------------------------------

export interface TrackedChangeResult {
  applied: boolean;
  /** Changes accepted or rejected. Present on success. */
  count?: number;
  reason?: "none" | "count_moved" | "unsupported" | "failed";
  expected?: number;
  actual?: number;
}

/**
 * How many tracked changes the document holds, without altering any (RV-04).
 *
 * The read half of the G-10 closure: "reject all changes" discards every mark-up
 * in the document, and find/replace confirms for far less. The count has to be
 * on screen first.
 *
 * Returns ``null`` when the host has no tracked-changes API (WordApi 1.6), which
 * is a different answer from "none" — the caller says so rather than reporting a
 * clean document.
 */
export async function countWordTrackedChanges(): Promise<number | null> {
  try {
    if (Office.context.requirements?.isSetSupported("WordApi", "1.6") === false) return null;
  } catch (err) {
    // No requirements API on this host — log it, then fall through and try
    // the read anyway rather than treating an unrelated probe failure as
    // "tracked changes unsupported".
    logOfficeError(err, "word-count-tracked-changes-probe");
  }
  try {
    return await Word.run(async (context) => {
      const tracked = context.document.body.getTrackedChanges();
      tracked.load("items");
      await context.sync();
      return tracked.items.length;
    });
  } catch (err) {
    logOfficeError(err, "word-count-tracked-changes");
    return null;
  }
}

/**
 * Accept or reject every tracked change (RV-04).
 *
 * ``expectedCount`` is what the user approved; a document that has moved since
 * aborts rather than discarding a different amount of a colleague's mark-up than
 * was agreed. Same rule as FS-02 and find/replace — an approval names a blast
 * radius, and widening it silently is the defect.
 */
export async function applyWordTrackedChanges(
  mode: "accept" | "reject",
  expectedCount?: number,
): Promise<TrackedChangeResult> {
  // Tracked-revision access is WordApi 1.6. Probing first means an unsupported
  // host is told so explicitly, rather than falling into "failed" — which the
  // caller phrases as "the document may be protected", a misleading reason
  // when the real cause is the Word client's version.
  try {
    if (Office.context.requirements?.isSetSupported("WordApi", "1.6") === false) {
      return { applied: false, reason: "unsupported" };
    }
  } catch (err) {
    // No requirements API on this host — log it, then fall through and try
    // the write anyway rather than treating an unrelated probe failure as
    // "tracked changes unsupported".
    logOfficeError(err, "word-apply-tracked-changes-probe");
  }
  try {
    return await Word.run(async (context) => {
      const tracked = context.document.body.getTrackedChanges();
      tracked.load("items");
      await context.sync();

      const actual = tracked.items.length;
      // Idempotent by design: running on a clean document is a no-op that says
      // so, not a silent success implying work happened.
      if (actual === 0) return { applied: false, reason: "none" as const };
      if (expectedCount != null && expectedCount !== actual) {
        return { applied: false, reason: "count_moved" as const, expected: expectedCount, actual };
      }

      // Resolving a revision is not itself an authored edit, so it must not
      // become one. Left to the document's own mode, Word records the accept or
      // reject AS a fresh revision: accepting every change hands back a
      // document with new changes in it and the user is never finished, which
      // is the reported "still tracking after tracking was stopped". Every
      // other write in this file either suspends tracking or forces it on
      // purpose; this was the one that did neither.
      await withTrackingSuspended(context, async () => {
        if (mode === "accept") tracked.acceptAll();
        else tracked.rejectAll();
        await context.sync();
      });
      return { applied: true, count: actual };
    });
  } catch {
    return { applied: false, reason: "failed" };
  }
}

export async function enableAutoShowTaskpane(): Promise<void> {
  try {
    Office.context.document.settings.set("Office.AutoShowTaskpaneWithDocument", true);
    await new Promise<void>((resolve) => {
      try {
        Office.context.document.settings.saveAsync(() => resolve());
      } catch {
        resolve();
      }
    });
  } catch {
    /* settings not available — ignore */
  }
  try {
    const officeAddin = (Office as { addin?: { setStartupBehavior?: (b: unknown) => void } }).addin;
    const behavior = (Office as { StartupBehavior?: { load?: unknown } }).StartupBehavior?.load;
    if (officeAddin?.setStartupBehavior && behavior !== undefined) {
      officeAddin.setStartupBehavior(behavior);
    }
  } catch {
    /* setStartupBehavior not supported — ignore */
  }
}
